import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import ProductEntryForm, { ProductEntryFormHandle } from './ProductEntryForm';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Printer, Search, RefreshCw, Loader2 } from 'lucide-react';
import { useSalesInvoice } from '../context/SalesInvoiceContext';
import { Html5Qrcode } from 'html5-qrcode';
import { toast } from './ui/use-toast';
import { createInvoiceApiCall, getPayBeforeDateFromInvoiceDaysSetting, sanitizeGstInput, GST_VALIDATION_MESSAGE } from '../lib/utils';
import { PrintOptionsModal } from './PrintOptionsModal';
import { checkBluetoothPrinter, checkBluetoothEnabled, enableBluetooth, formatDeviceName, isPrinterDevice, listBluetoothDevices, connectBluetoothDevice, disconnectBluetoothDevice } from '../lib/bluetoothPrinter';
import { printKohineer, printKohineer2mm, drawInvoicePdf } from '../lib/printKohineer';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Capacitor } from '@capacitor/core';
import { BarcodeScanner } from '@capacitor-community/barcode-scanner';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { downloadPdf } from '@/lib/pdfDownload';
import RepresentativeManager from './RepresentativeManager';
import SerialSelectionPopup from './SerialSelectionPopup';

import { cn } from "@/lib/utils";
import { AppBar } from './ui/app-bar';
import { useBackNavigation } from '../hooks/use-back-navigation';
import { useSettings } from '../hooks/useSettings';
import { getBaseUrl, authenticatedFetch, fetchPDF } from '@/lib/api';
import { useUserPrivileges } from '@/context/UserPrivilegesContext';
import { PRIVILEGE } from '@/lib/privilegeConstants';

const initialCustomer = {
  Phone: '',
  Name: '',
  Company: '',
  GST: '',
  Locality: '',
  State: '',
  Email: '',
  Address: '',
  ReferredBy: '',
  SelectCompany: '',
  Location: '',
  Partner: '',
  eCommOrderID: '',
};

// Placeholder data for dropdowns
// const mockCompanies = [
//   { Id: '1', Name: 'BEST TRADING COMP', State: 'Telangana' },
//   { Id: '2', Name: 'ANOTHER COMPANY', State: 'Maharashtra' },
// ];

const SalesInvoiceMobile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [showRepManager, setShowRepManager] = useState(false);
  
  // State to control which section is open
  const [openSection, setOpenSection] = useState<'customer' | 'product' | 'payment'>('customer');
  const [hasAccessedPaymentSection, setHasAccessedPaymentSection] = useState(false);

  const [showQuantityExceededPopup, setShowQuantityExceededPopup] = useState(false);
  const [quantityExceededProductIndex, setQuantityExceededProductIndex] = useState<number | null>(null);
  const [quantityExceededAvailable, setQuantityExceededAvailable] = useState<number>(0);

  // State for Unit Price validation popup
  const [showUnitPriceValidation, setShowUnitPriceValidation] = useState(false);
  const [unitPriceValidationData, setUnitPriceValidationData] = useState<{
    index: number;
    enteredValue: string;
    originalValue: string;
  } | null>(null);

  // Ref to store timeout for unit price validation
  const unitPriceValidationTimeout = useRef<number | null>(null);

  // Use shared context for invoice state
  const {
    customer, setCustomer,
    products, setProducts,
    payments, setPayments,
    notes, setNotes,
    repName, setRepName,
    invoiceForm, setInvoiceForm,
    shipToForm, setShipToForm,
    shipToSame, setShipToSame,
    isUnknown, setIsUnknown,
    isEditMode, editingInvoiceId, onBackToReports, onUpdateSuccess
  } = useSalesInvoice();
  const { hasPrivilege } = useUserPrivileges();
  const canEditPrice = hasPrivilege(PRIVILEGE.INVOICE_PRICE_CHANGE);
  const canEditDiscount = hasPrivilege(PRIVILEGE.EDIT_DISCOUNT);

  // Get settings for dynamic field visibility and default payment type
  const { salesInvoiceSettings, getSetting, primaryCompanyId, settings: settingsList } = useSettings();

  // Serial field: only show when "Serial Text" setting has a value
  const serialText = (getSetting('Serial Text') || '').trim();
  const showSerialField = serialText.length > 0;
  // 3MM print/preview: show serial using "Serial Print" template; must contain exact tag <SerialNo> (replaced with actual serial)
  const serialPrintTemplate = (getSetting('Serial Print') || '').trim();
  const showSerialInPrint = serialPrintTemplate.includes('<SerialNo>');

  // Resolve default payment method from "Invoice Default Payment Type" setting (Wallet -> Wallets for UI)
  const getDefaultPaymentMethod = () => {
    const raw = (getSetting('Invoice Default Payment Type') || 'Cash').trim();
    const mapped = raw === 'Wallet' ? 'Wallets' : raw;
    const allowed = ['Cash', 'Card', 'Wallets', 'Cheque', 'Coupon', 'Credit Note', 'Points'];
    return allowed.includes(mapped) ? mapped : 'Cash';
  };

  // Round payment amounts to 2 decimal places (currency)
  const roundPayment = (val: unknown): number => {
    const n = parseFloat(String(val ?? ''));
    return Number.isNaN(n) ? 0 : Math.round(n * 100) / 100;
  };

  // Custom back navigation handler for SalesInvoiceMobile
  const handleSalesInvoiceMobileBack = () => {
    if (isEditMode && onBackToReports) {
      onBackToReports();
    } else {
      // Navigate back to dashboard/transactions
      window.dispatchEvent(new CustomEvent('setCurrentPage', { detail: 'transactions' }));
    }
  };

  // Use back navigation hook with custom handler
  useBackNavigation({
    onBack: handleSalesInvoiceMobileBack,
    canGoBack: true,
    showExitConfirmation: false // Don't show exit confirmation on this page
  });

  // Function to fetch and log invoice details from API
  const fetchAndLogInvoiceDetails = async (invoiceId: string, shouldPopulatePayments = false) => {
    try {
      const baseUrl = getBaseUrl();
      const token = localStorage.getItem('token') || '';
      const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      
      console.log('SalesInvoiceMobile: Fetching invoice details for debugging...');
      console.log('SalesInvoiceMobile: Invoice ID:', invoiceId);
      console.log('SalesInvoiceMobile: API URL:', `${baseUrl}/api/InvoiceFullDetails?InvoiceID=${invoiceId}`);
      
      const response = await fetch(`${baseUrl}/api/InvoiceFullDetails?InvoiceID=${invoiceId}`, {
        method: 'GET',
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      console.log('SalesInvoiceMobile: === INVOICE DETAILS DEBUG ===');
      console.log('SalesInvoiceMobile: Full API Response:', JSON.stringify(data, null, 2));
      
      // Log payment fields specifically
      if (data && data.length > 0) {
        const firstRecord = data[0];
        console.log('SalesInvoiceMobile: === PAYMENT FIELDS FROM API ===');
        console.log('SalesInvoiceMobile: PaidbyCash:', firstRecord.PaidbyCash);
        console.log('SalesInvoiceMobile: PaidbyOthers:', firstRecord.PaidbyOthers);
        console.log('SalesInvoiceMobile: walletamount:', firstRecord.walletamount);
        console.log('SalesInvoiceMobile: Chequeamount:', firstRecord.Chequeamount);
        console.log('SalesInvoiceMobile: Couponamount:', firstRecord.Couponamount);
        console.log('SalesInvoiceMobile: CreditNoteamount:', firstRecord.CreditNoteamount);
        console.log('SalesInvoiceMobile: PaidbyPoints:', firstRecord.PaidbyPoints);
        console.log('SalesInvoiceMobile: CashDenominationReceived:', firstRecord.CashDenominationReceived);
        console.log('SalesInvoiceMobile: PaidDetails:', firstRecord.PaidDetails);
        console.log('SalesInvoiceMobile: === END PAYMENT FIELDS FROM API ===');
        
        // Populate payments state from invoice data if requested
        if (shouldPopulatePayments) {
          console.log('SalesInvoiceMobile: === POPULATING PAYMENTS FROM INVOICE DATA ===');
          
          // Determine the primary payment method based on amounts
          const paymentMethods = [
            { key: 'PaidbyCash', value: 'Cash' },
            { key: 'PaidbyOthers', value: 'Card' },
            { key: 'walletamount', value: 'Wallets' },
            { key: 'Chequeamount', value: 'Cheque' },
            { key: 'Couponamount', value: 'Coupon' },
            { key: 'CreditNoteamount', value: 'Credit Note' },
            { key: 'PaidbyPoints', value: 'Points' }
          ];

          // Find the first payment method with a value > 0
          const usedPaymentMethod = paymentMethods.find(
            method => parseFloat(firstRecord[method.key]) > 0
          );

          const selectedMethod = usedPaymentMethod ? usedPaymentMethod.value : 'Cash';

          // Parse Payments JSON for Coupon: when PaymentMode === 'Coupon', Reference holds the coupon number
          let couponNoFromPayments = '';
          if (firstRecord.Payments && typeof firstRecord.Payments === 'string') {
            try {
              const paymentsArray = JSON.parse(firstRecord.Payments);
              if (Array.isArray(paymentsArray)) {
                const couponEntry = paymentsArray.find((p) => p.PaymentMode === 'Coupon');
                if (couponEntry && couponEntry.Reference) {
                  couponNoFromPayments = String(couponEntry.Reference).trim();
                }
              }
            } catch {
              // ignore parse errors
            }
          }
          
          // Populate payments state with invoice data (rounded to 2 decimals)
          setPayments(prev => ({
            ...prev,
            splitPaymentEnabled: false,
            selectedPaymentMethod: selectedMethod,
            cashPayment: firstRecord.PaidbyCash != null ? roundPayment(firstRecord.PaidbyCash).toFixed(2) : '',
            cardPayment: firstRecord.PaidbyOthers != null ? roundPayment(firstRecord.PaidbyOthers).toFixed(2) : '',
            walletProvider: 'PhonePe', // Default wallet provider
            walletAmount: firstRecord.walletamount != null ? roundPayment(firstRecord.walletamount).toFixed(2) : '',
            walletTxNo: firstRecord.PaidDetails || '', // Map PaidDetails to walletTxNo for wallet transactions
            chequeAmount: firstRecord.Chequeamount != null ? roundPayment(firstRecord.Chequeamount).toFixed(2) : '',
            chequeNo: '',
            couponNo: couponNoFromPayments,
            couponAmount: firstRecord.Couponamount != null ? roundPayment(firstRecord.Couponamount).toFixed(2) : '0',
            couponAmountLocked: false,
            creditNoteNo: '',
            creditNoteAmount: firstRecord.CreditNoteamount != null ? roundPayment(firstRecord.CreditNoteamount).toFixed(2) : '0',
            pointsRedeem: firstRecord.PaidbyPoints != null ? roundPayment(firstRecord.PaidbyPoints).toFixed(2) : '',
            eligiblePoints: prev.eligiblePoints || '',
            bankTransAmount: '',
            otherAmount: '',
            payBefore: prev.payBefore || '',
          }));
          
          // Also update the local walletTxNo state for mobile
          console.log('SalesInvoiceMobile: Setting walletTxNo to:', firstRecord.PaidDetails);
          setWalletTxNo(firstRecord.PaidDetails || '');
          
          console.log('SalesInvoiceMobile: === PAYMENTS STATE POPULATED ===');
          console.log('SalesInvoiceMobile: Selected payment method:', selectedMethod);
          console.log('SalesInvoiceMobile: Wallet Tx No (from PaidDetails):', firstRecord.PaidDetails);
          console.log('SalesInvoiceMobile: payments.walletTxNo set to:', firstRecord.PaidDetails);
          console.log('SalesInvoiceMobile: =====================================');
        }
      }
      
      console.log('SalesInvoiceMobile: === END INVOICE DETAILS DEBUG ===');
      
    } catch (error) {
      console.error('SalesInvoiceMobile: Error fetching invoice details:', error);
    }
  };

  // Function to validate coupon
  const validateCoupon = async (couponNo: string) => {
    try {
      const baseUrl = getBaseUrl();
      const token = localStorage.getItem('token') || '';
      const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      const response = await fetch(`${baseUrl}/api/ValidateCoupon?CouponNo=${encodeURIComponent(couponNo)}`, {
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to validate coupon');
      return response.json();
    } catch (error) {
      console.error('Error validating coupon:', error);
      throw error;
    }
  };

  // Function to validate credit note
  const validateCreditNote = async (invSeqNo: string, invDate: string, companyId: string) => {
    try {
      const baseUrl = getBaseUrl();
      const token = localStorage.getItem('token') || '';
      const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      const response = await fetch(`${baseUrl}/api/ValidateCreditNote?InvSeqNO=${encodeURIComponent(invSeqNo)}&InvDate=${encodeURIComponent(invDate)}&CompanyID=${encodeURIComponent(companyId)}`, {
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to validate credit note');
      return response.json();
    } catch (error) {
      console.error('Error validating credit note:', error);
      throw error;
    }
  };

  // Reset all fields to clean state on component mount (like fresh login)
  useEffect(() => {
    // Don't reset data if we're in edit mode
    if (isEditMode && editingInvoiceId) {
      console.log('SalesInvoiceMobile: Edit mode detected, preserving loaded data');
      console.log('SalesInvoiceMobile: Initial shipToForm data:', shipToForm);
      console.log('SalesInvoiceMobile: Initial shipToSame value:', shipToSame);
      console.log('SalesInvoiceMobile: Fetching invoice details for debugging...');
      fetchAndLogInvoiceDetails(editingInvoiceId, true); // Pass true to populate payments
      
      // Save the original API shipping data for later use
      const hasApiShippingData = Boolean(shipToForm.Name || shipToForm.Company || shipToForm.Phone || 
                                shipToForm.Email || shipToForm.GST || shipToForm.Address || shipToForm.State);
      if (hasApiShippingData) {
        console.log('SalesInvoiceMobile: Saving original API shipping data:', shipToForm);
        setOriginalApiShippingData({ ...shipToForm });
      } else {
        console.log('SalesInvoiceMobile: No API shipping data found in shipToForm');
        console.log('SalesInvoiceMobile: shipToForm contents:', shipToForm);
      }
      
      // Ensure partner is set for product search to work
      if (customer.Partner && !selectedPartner) {
        setSelectedPartner(customer.Partner);
      }
      
      return;
    }

    // Only reset customer details and products, but preserve company/location/partner selections
    const keepUnknownManual = salesInvoiceSettings?.unknownUserTicketed ?? false;
    setCustomer(prev => ({
      ...prev,
      Phone: '',
      Mobile1: '',
      Name: keepUnknownManual ? 'UNKNOWN' : '',
      Company: keepUnknownManual ? 'UNKNOWN' : '',
      Email: '',
      GST: '',
      GSTN: '',
      Address: '',
      Address1: '',
      ReferredBy: '',
      // Keep SelectCompany, Location, Partner, State as they may be set by other logic
    }));
    
    // Clear products
    setProducts([]);
    
    // Reset payments (default payment method from settings)
    setPayments({
      splitPaymentEnabled: false,
      selectedPaymentMethod: getDefaultPaymentMethod(),
      cashPayment: '',
      cardPayment: '',
      walletProvider: 'PhonePe',
      walletAmount: '',
      walletTxNo: '',
      chequeAmount: '',
      chequeNo: '',
      couponNo: '',
      couponAmount: '0',
      couponAmountLocked: false,
      creditNoteNo: '',
      creditNoteAmount: '0',
      pointsRedeem: '',
      eligiblePoints: '',
      bankTransAmount: '',
      payBefore: getPayBeforeDateFromInvoiceDaysSetting(getSetting('Invoice No of Day for Due')),
    });
    
    // Clear notes and rep name
    setNotes('');
    resetRepNameAndSearch();
    
    // Reset UI states (preserve Unknown checkbox when "Unknown User Ticketed" is enabled in settings)
    setIsUnknown(keepUnknownManual);
    setCustomerTotalDue(null);
    setShowMoreCustomerFields(false);
    setShipToSame(true);
    setShowNotes(false);
    setShowShipToPopup(false);
    setPrintInvoice(false);
    setOpenSection('customer');
    
    // Reset Ship To form (but preserve in edit mode)
    if (!isEditMode || !editingInvoiceId) {
      setShipToForm({ Name: keepUnknownManual ? 'UNKNOWN' : '', Company: keepUnknownManual ? 'UNKNOWN' : '', Phone: '', Email: '', GST: '', Address: '', State: '' });
    }
    
    // Reset product search states
    setProductSearch('');
    setProductTableSearch('');
    setBarcodeSearchTerm('');
    setProductNameSearchTerm('');
    
    // Invalidate product search cache to ensure fresh suggestions
    queryClient.invalidateQueries({ queryKey: ['products-search'] });
    queryClient.removeQueries({ queryKey: ['products-search'] });
  }, [isEditMode, editingInvoiceId]); // Add dependencies to prevent reset in edit mode

  // Set default payBefore date from "Invoice No of Day for Due" setting when settings load and not already set
  useEffect(() => {
    if (!payments.payBefore && settingsList.length > 0) {
      const value = getSetting('Invoice No of Day for Due');
      const payBeforeDate = getPayBeforeDateFromInvoiceDaysSetting(value);
      setPayments(p => ({ ...p, payBefore: payBeforeDate }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsList.length]);

  // When user opens payment section, set default payment method, Pay Before, and Split Payment from settings (user can still change manually)
  useEffect(() => {
    if (openSection === 'payment') {
      const payBeforeDate = getPayBeforeDateFromInvoiceDaysSetting(getSetting('Invoice No of Day for Due'));
      setPayments(p => ({
        ...p,
        selectedPaymentMethod: getDefaultPaymentMethod(),
        payBefore: payBeforeDate,
        splitPaymentEnabled: salesInvoiceSettings.splitPayment,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getSetting/getDefaultPaymentMethod are from useSettings and change every render; we only want to run when openSection changes
  }, [openSection]);

  // Default invoice date to today for new invoices (so field shows and sends current date; user can edit)
  useEffect(() => {
    if (!isEditMode && (!customer.InvoiceDate || customer.InvoiceDate.trim() === '')) {
      const today = new Date().toISOString().slice(0, 10);
      setCustomer(prev => ({ ...prev, InvoiceDate: today }));
    }
  }, [isEditMode]); // Only run when edit mode changes; avoid overwriting user-selected date

  // Remove local useState for customer, products, payments, notes, repName
  // isUnknown is now managed by SalesInvoiceContext
  const [showNotes, setShowNotes] = useState(false);
  const [showShipToPopup, setShowShipToPopup] = useState(false);
  const [showCompanyConfirm, setShowCompanyConfirm] = useState(false);
  const [pendingCompany, setPendingCompany] = useState('');
  const [prevCompany, setPrevCompany] = useState('');
  const [selectedPartner, setSelectedPartner] = useState('');
  const [showLocationConfirm, setShowLocationConfirm] = useState(false);
  const [pendingLocation, setPendingLocation] = useState('');
  const [prevLocation, setPrevLocation] = useState('');
  const [showStateConfirm, setShowStateConfirm] = useState(false);
  const [pendingState, setPendingState] = useState('');
  const [prevState, setPrevState] = useState('');
  // Add state for 'More' toggle in Customer section
  const [showMoreCustomerFields, setShowMoreCustomerFields] = useState(false);
  const [customerGstError, setCustomerGstError] = useState(false);
  const [shipToGstError, setShipToGstError] = useState(false);

  // Add state for Print checkbox and Create Invoice functionality
  const [printInvoice, setPrintInvoice] = useState(false);
  const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);
  const [pointsError, setPointsError] = useState('');
  const [couponError, setCouponError] = useState('');
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [couponValidationData, setCouponValidationData] = useState(null);
  const [creditNoteError, setCreditNoteError] = useState('');
  const [isValidatingCreditNote, setIsValidatingCreditNote] = useState(false);

  // Function to fetch customer points when Points payment method is selected
  const fetchPointsWhenSelected = async () => {
    const isEditMode = editingInvoiceId !== null;
    const previousPointsRedeem = payments.pointsRedeem;
    if (customer.customerCode) {
      try {
        const balancePoints = await fetchCustomerPoints(customer.customerCode);
        console.log('Fetched customer points when Points selected (Mobile):', balancePoints);
        
        console.log('=== POINTS DEBUG (Mobile) ===');
        console.log('isEditMode:', isEditMode);
        console.log('editingInvoiceId:', editingInvoiceId);
        console.log('balancePoints:', balancePoints);
        console.log('============================');
        
        // In edit mode, only update eligible points, preserve the API points value
        if (isEditMode) {
          console.log('In edit mode, preserving API points value (Mobile):', previousPointsRedeem);
          setPayments(p => ({
            ...p,
            eligiblePoints: balancePoints.toString(),
            pointsRedeem: previousPointsRedeem
          }));
          return;
        }
        
        // Only proceed with points calculation in create mode
        const invoiceTotalValue = invoiceTotal;
        let pointsRedeemValue = '0';
        
        if (balancePoints <= 0) {
          // No eligible points, set to 0 and make read-only
          pointsRedeemValue = '0';
        } else if (!payments.splitPaymentEnabled) {
          // Split payment is unchecked
          if (balancePoints >= invoiceTotalValue) {
            // Eligible points are enough, allow Points payment type
            pointsRedeemValue = invoiceTotalValue.toString();
          } else {
            // Eligible points are not enough, make field read-only
            pointsRedeemValue = '0';
          }
        } else {
          // Split payment is checked, allow manual entry
          pointsRedeemValue = '0';
        }
        
        // Update both values only in create mode
        setPayments(p => ({
          ...p,
          eligiblePoints: balancePoints.toString(),
          pointsRedeem: pointsRedeemValue
        }));
        
        console.log('Create mode - Updated payments state with eligiblePoints:', balancePoints.toString(), 'pointsRedeem:', pointsRedeemValue);
      } catch (error) {
        console.error('Error fetching customer points:', error);
      }
    } else {
      // No customer code, only update in create mode
      if (!isEditMode) {
        setPayments(p => ({
          ...p,
          eligiblePoints: '0',
          pointsRedeem: '0'
        }));
      }
    }
  };

  // Function to handle coupon validation
  const handleCouponValidation = async () => {
    if (!payments.couponNo || payments.couponNo.trim() === '') {
      setCouponError('Please enter a coupon code');
      return;
    }

    setIsValidatingCoupon(true);
    setCouponError('');
    setCouponValidationData(null);

    try {
      const response = await validateCoupon(payments.couponNo.trim());
      
      if (response.status === 0 && response.data && response.data.length > 0) {
        const couponData = response.data[0];
        setCouponValidationData(couponData);
        
        // Validation rules
        const billValue = parseFloat(couponData.BillValue) || 0;
        const apiAmount = parseFloat(couponData.Amount) || 0;
        const validFrom = couponData.ValidFrom ? new Date(couponData.ValidFrom) : null;
        const validTo = couponData.ValidTo ? new Date(couponData.ValidTo) : null;

        // Compare dates only (ignore time): coupon valid for the full ValidFrom/ValidTo calendar days
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (validFrom) {
          const validFromStart = new Date(validFrom);
          validFromStart.setHours(0, 0, 0, 0);
          if (today < validFromStart) {
            setCouponError('Coupon is not yet valid.');
            setPayments(p => ({ ...p, couponAmount: '0', couponAmountLocked: false }));
            return;
          }
        }
        if (validTo) {
          const endOfValidToDay = new Date(validTo);
          endOfValidToDay.setHours(23, 59, 59, 999);
          if (today > endOfValidToDay) {
            setCouponError('Coupon has expired.');
            setPayments(p => ({ ...p, couponAmount: '0', couponAmountLocked: false }));
            return;
          }
        }

        // Check minimum bill value against total invoice amount
        if (invoiceTotal < billValue) {
          setCouponError(`Minimum bill value should be ${billValue.toFixed(2)}.`);
          setPayments(p => ({ ...p, couponAmount: '0', couponAmountLocked: false }));
          return;
        }

        // Success: set amount from API and lock field
        setPayments(p => ({ ...p, couponAmount: apiAmount.toFixed(2), couponAmountLocked: true }));
        toast({
          title: 'Success',
          description: 'Coupon applied successfully.',
          className: 'bg-green-600 text-white border-green-700'
        });
      } else {
        setCouponError('Invalid coupon code');
        setPayments(p => ({ ...p, couponAmount: '0', couponAmountLocked: false }));
      }
    } catch (error) {
      console.error('Error validating coupon:', error);
      setCouponError('Failed to validate coupon. Please try again.');
      setPayments(p => ({ ...p, couponAmount: '0', couponAmountLocked: false }));
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  // Function to handle credit note validation
  const handleCreditNoteValidation = async () => {
    if (!payments.creditNoteNo || payments.creditNoteNo.trim() === '') {
      setCreditNoteError('Please enter a credit note number');
      return;
    }

    setIsValidatingCreditNote(true);
    setCreditNoteError('');

    try {
      const invSeqNo = payments.creditNoteNo.trim();
      const invDate = new Date().toISOString().slice(0, 10);
      const companyId = String(customer.SelectCompany || '');

      const response = await validateCreditNote(invSeqNo, invDate, companyId);
      if (response.status === 0 && response.data && response.data.length > 0) {
        const creditData = response.data[0];
        const invoicedAmount = parseFloat(creditData.InvoicedAmount) || 0;
        setPayments(p => ({ ...p, creditNoteAmount: invoicedAmount.toFixed(2) }));
        // Check if credit note amount exceeds invoice total (invoiceTotal is in scope via closure - will use latest)
        const invTotal = products.reduce((sum, p) => {
          const offer = parseFloat(p.OfferPrice) || 0;
          const qty = parseFloat(p.Qty) || 0;
          return sum + offer * qty;
        }, 0);
        if (invoicedAmount > invTotal) {
          setCreditNoteError(
            `Credit Note Amount Exceeds Invoice Total\nThe selected credit note amount (${invoicedAmount.toFixed(2)}) is greater than the invoice total (${invTotal.toFixed(2)}).\nPlease add more products or remove the credit note to continue.`
          );
          toast({
            title: 'Warning',
            description: 'Credit note amount exceeds invoice total. Add more products or remove the credit note.',
            variant: 'destructive'
          });
        } else {
          setCreditNoteError('');
          toast({
            title: 'Success',
            description: 'Credit Note applied successfully.',
            className: 'bg-green-600 text-white border-green-700'
          });
        }
      } else {
        setCreditNoteError('Invalid Credit Note');
        setPayments(p => ({ ...p, creditNoteAmount: '0' }));
      }
    } catch (error) {
      console.error('Error validating credit note:', error);
      setCreditNoteError('Failed to validate credit note. Please try again.');
      setPayments(p => ({ ...p, creditNoteAmount: '0' }));
    } finally {
      setIsValidatingCreditNote(false);
    }
  };

  // Function to validate coupon amount when user enters it
  const validateCouponAmount = (amount: string) => {
    if (!couponValidationData) {
      setCouponError('Please validate the coupon first');
      return false;
    }

    const enteredAmount = parseFloat(amount) || 0;
    const maxAmount = parseFloat(couponValidationData.Amount) || 0;

    if (enteredAmount > maxAmount) {
      setCouponError(`You can use up to ₹${maxAmount.toFixed(2)} only.`);
      return false;
    }

    setCouponError('');
    return true;
  };

  // Helper function to recalculate all derived fields (GST, Taxable Value, Cess, Total)
  // This ensures consistency across all field changes
  const recalculateProductFields = (product: any, changedField?: string, changedValue?: string) => {
    const updated = { ...product };
    
    // Update the changed field if provided
    if (changedField && changedValue !== undefined) {
      updated[changedField] = changedValue;
      if (changedField === 'Qty') {
        // Update DelQty to match the new quantity
        updated.DelQty = changedValue;
      }
    }
    
    // Get current values for calculation
    const mrp = parseFloat(updated.MRP) || 0;
    const qty = parseFloat(updated.Qty) || 1;
    const gstPercent = parseFloat(updated.GST) || 0;
    const cessPercent = parseFloat(updated.CessPer) || 0;
    
    // Recalculate based on what changed
    if (changedField === 'MRP') {
      // When MRP is changed and Offer price is not changed => Calculate discount and Discount%
      // Keep OfferPrice unchanged, recalculate discount and discount%
      const offerPrice = parseFloat(updated.OfferPrice) || 0;
      const displayDiscountPer = mrp > 0 ? 100 - (offerPrice * 100 / mrp) : 0;
      const displayDiscount = (mrp - offerPrice) * qty;
      updated.DiscountPer = String(displayDiscountPer.toFixed(2));
      updated.Discount = String(displayDiscount.toFixed(2));
      
      console.log(`Mobile: MRP changed: Keeping OfferPrice unchanged at ${offerPrice.toFixed(2)}, new discount%: ${displayDiscountPer.toFixed(2)}`);
    } else if (changedField === 'OfferPrice') {
      // When OfferPrice is changed, recalculate discount and Discount%
      const offerPrice = parseFloat(changedValue || '0') || 0;
      const displayDiscountPer = mrp > 0 ? 100 - (offerPrice * 100 / mrp) : 0;
      const displayDiscount = (mrp - offerPrice) * qty;
      updated.DiscountPer = String(displayDiscountPer.toFixed(2));
      updated.Discount = String(displayDiscount.toFixed(2));
    } else if (changedField === 'DiscountPer') {
      // When DiscountPer is changed, calculate Offer Price and Discount
      const discountPer = parseFloat(changedValue || '0') || 0;
      const offerPrice = mrp * (1 - discountPer / 100);
      updated.OfferPrice = String(offerPrice.toFixed(2));
      const displayDiscount = (mrp - offerPrice) * qty;
      updated.Discount = String(displayDiscount.toFixed(2));
    } else if (changedField === 'Discount') {
      // When Discount is changed, calculate OfferPrice and DiscountPer
      const discount = parseFloat(changedValue || '0') || 0;
      const offerPrice = qty > 0 ? mrp - (discount / qty) : mrp;
      updated.OfferPrice = String(offerPrice.toFixed(2));
      const displayDiscountPer = mrp > 0 ? 100 - (offerPrice * 100 / mrp) : 0;
      updated.DiscountPer = String(displayDiscountPer.toFixed(2));
    } else if (changedField === 'TaxableValue') {
      // When Taxable Value is changed, recalculate OfferPrice, then discount and Discount%
      const taxableValue = parseFloat(changedValue || '0') || 0;
      const offerPrice = (taxableValue * (1 + (gstPercent + cessPercent) / 100)) / qty;
      updated.OfferPrice = String(offerPrice.toFixed(2));
      const displayDiscountPer = mrp > 0 ? 100 - (offerPrice * 100 / mrp) : 0;
      const displayDiscount = (mrp - offerPrice) * qty;
      updated.DiscountPer = String(displayDiscountPer.toFixed(2));
      updated.Discount = String(displayDiscount.toFixed(2));
    } else if (changedField === 'Qty') {
      // When Quantity is changed, KEEP OfferPrice unchanged, recalculate discount
      const offerPrice = parseFloat(updated.OfferPrice) || 0;
      const displayDiscount = (mrp - offerPrice) * qty;
      updated.Discount = String(displayDiscount.toFixed(2));
      const displayDiscountPer = mrp > 0 ? 100 - (offerPrice * 100 / mrp) : 0;
      updated.DiscountPer = String(displayDiscountPer.toFixed(2));
    }
    
    // ALWAYS recalculate Taxable Value, GST Amount, Cess Amount, and Total
    // This ensures these fields are ALWAYS up to date regardless of which field changed
    const offerPrice = parseFloat(updated.OfferPrice) || 0;
    
    // Calculate Taxable Value (unless it was just edited directly)
    let taxableValue;
    if (changedField === 'TaxableValue') {
      taxableValue = parseFloat(changedValue || '0') || 0;
    } else {
      // Formula: Taxable Value = OfferPrice * Qty / (1 + (GST% + Cess%) / 100)
      taxableValue = (offerPrice * qty) / (1 + (gstPercent + cessPercent) / 100);
    }
    
    // Calculate GST Amount = Taxable Value * GST% / 100
    const gstAmount = taxableValue * gstPercent / 100;
    
    // Calculate Cess Amount = Taxable Value * CessPercent / 100
    const cessAmount = taxableValue * cessPercent / 100;
    
    // Calculate Total = Taxable Value + GST Amount + Cess Amount
    const total = taxableValue + gstAmount + cessAmount;
    
    // Update all calculated fields - these ALWAYS get recalculated
    updated.TaxableValue = String(taxableValue.toFixed(2));
    updated.GSTAmount = String(gstAmount.toFixed(2));
    updated.CessAmount = String(cessAmount.toFixed(2));
    updated.Total = String(total.toFixed(2));
    
    // Update Unit Price for backward compatibility
    const totalTax = gstPercent + cessPercent;
    const unitPrice = totalTax > 0 ? mrp / (1 + totalTax / 100) : mrp;
    updated.UnitPrice = unitPrice.toFixed(2);
    
    // Update legacy SGST/CGST/Cess fields: split GST amount so SGST and CGST are equal (matches POST and Cess rounding)
    const gstAmountNum = parseFloat(String(gstAmount)) || 0;
    const half = Math.floor((gstAmountNum / 2) * 100) / 100;
    updated.SGST = half;
    updated.CGST = half;
    updated.Cess = cessAmount;
    
    // Debug log to track recalculations
    console.log(`✓ Mobile Recalculation triggered by '${changedField || 'initialization'}':`, {
      MRP: mrp,
      Qty: qty,
      OfferPrice: offerPrice,
      'GST%': gstPercent,
      'Cess%': cessPercent,
      '→ TaxableValue': updated.TaxableValue,
      '→ GSTAmount': updated.GSTAmount,
      '→ CessAmount': updated.CessAmount,
      '→ Total': updated.Total
    });
    
    return updated;
  };

  // Product section state
  const [productSearch, setProductSearch] = useState('');
  const [serialNumberSearchTerm, setSerialNumberSearchTerm] = useState('');
  
  // Serial Selection Popup states
  const [showSerialSelectionPopup, setShowSerialSelectionPopup] = useState(false);
  const [serialPopupProduct, setSerialPopupProduct] = useState<any>(null);
  const [addedSerialNumbers, setAddedSerialNumbers] = useState<Set<string>>(new Set());
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [productAddMessage, setProductAddMessage] = useState(null);
  const [productAddMessageType, setProductAddMessageType] = useState(null);
  
  // Add state for ProductEntryForm modal
  const [showProductEntryModal, setShowProductEntryModal] = useState(false);
  const productFormRef = useRef<ProductEntryFormHandle>(null);
  const [barcodeSearchTerm, setBarcodeSearchTerm] = useState('');
  const [productNameSearchTerm, setProductNameSearchTerm] = useState('');
  const [productTableSearch, setProductTableSearch] = useState('');

  // Handle "Ship to Same" functionality
  useEffect(() => {
    if (shipToSame && customer.Name && customer.Company && customer.Phone && customer.Email && customer.GST && customer.Address && customer.State) {
      // Populate shipping fields with customer data when "Ship to Same" is checked
      setShipToForm({
        Name: customer.Name,
        Company: customer.Company,
        Phone: customer.Phone,
        Email: customer.Email,
        GST: customer.GST,
        Address: customer.Address,
        State: customer.State,
      });
    }
  }, [shipToSame, customer.Name, customer.Company, customer.Phone, customer.Email, customer.GST, customer.Address, customer.State, setShipToForm]);

  // Simulate fetching dropdown data (replace with real API/react-query in production)
  const [states, setStates] = useState([]);
  const [statesLoading, setStatesLoading] = useState(false);
  const [statesError, setStatesError] = useState('');
  const [partners, setPartners] = useState([]);
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [partnersError, setPartnersError] = useState('');
  const [representatives, setRepresentatives] = useState([]);
  const [representativesLoading, setRepresentativesLoading] = useState(false);
  const [representativesError, setRepresentativesError] = useState('');

  // Placeholder product suggestions (replace with API)
  const mockProductSuggestions = [
    { Barcode: '123456', Title: 'Product A', MRP: '100', Available: 10 },
    { Barcode: '789012', Title: 'Product B', MRP: '200', Available: 5 },
  ];

  // Add state for loading and error for phone autofill
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [customerTotalDue, setCustomerTotalDue] = useState<number | null>(null);
  
  // Add state for phone suggestions - replace the static one with API-based
  const [showPhoneSuggestions, setShowPhoneSuggestions] = useState(false);
  const [phoneSuggestions, setPhoneSuggestions] = useState([]);
  const [phoneSuggestionsLoading, setPhoneSuggestionsLoading] = useState(false);
  const [phoneDropdownActiveIndex, setPhoneDropdownActiveIndex] = useState(-1);
  const phoneDropdownRef = useRef(null);

  // Rep Name dropdown states
  const [showRepNameDropdown, setShowRepNameDropdown] = useState(false);
  const [repNameSearchTerm, setRepNameSearchTerm] = useState('');

  // Filter representatives for dropdown
  const filteredRepresentatives = representatives.filter(rep => {
    if (!repNameSearchTerm.trim()) return true;
    const search = repNameSearchTerm.toLowerCase();
    return (
      (rep.Representative && rep.Representative.toLowerCase().includes(search))
    );
  });

  // Handle click outside to close Rep Name dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      if (!target.closest('.rep-name-dropdown')) {
        setShowRepNameDropdown(false);
      }
    };

    if (showRepNameDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showRepNameDropdown]);

  // Sync repNameSearchTerm with repName when repName is reset or initialized
  useEffect(() => {
    if (repName === '') {
      setRepNameSearchTerm('');
    } else if (repNameSearchTerm === '' && repName !== '') {
      // Initialize search term with current rep name value (for edit mode)
      setRepNameSearchTerm(repName);
    }
  }, [repName, repNameSearchTerm]);

  // Initialize OriginalUnitPrice for products that don't have it (e.g., from edit mode)
  useEffect(() => {
    setProducts(prev => prev.map(prod => {
      if (!prod.OriginalUnitPrice && prod.UnitPrice) {
        return { ...prod, OriginalUnitPrice: prod.UnitPrice };
      }
      return prod;
    }));
  }, []); // Run once on mount

  // Cleanup unit price validation timeout on unmount
  useEffect(() => {
    return () => {
      if (unitPriceValidationTimeout.current) {
        clearTimeout(unitPriceValidationTimeout.current);
      }
    };
  }, []);

  // Helper function to reset rep name and search term together
  const resetRepNameAndSearch = () => {
    setRepName('');
    setRepNameSearchTerm('');
  };

  // Function to fetch customer points
  const fetchCustomerPoints = async (customerCode) => {
    try {
      const baseUrl = getBaseUrl();
      const token = localStorage.getItem('token') || '';
      const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      const response = await fetch(`${baseUrl}/api/GetCustPoints?customerCode=${customerCode}`, {
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch customer points');
      const data = await response.json();
      if (data.status === 0 && Array.isArray(data.data) && data.data.length > 0) {
        const pointsData = data.data[0];
        console.log('Customer points data from API:', pointsData);
        const balancePoints = pointsData.BalancePoints || 0;
        
        // Handle points logic based on split payment and invoice total
        const invoiceTotalValue = invoiceTotal;
        let pointsRedeemValue = '0';
        
        if (!payments.splitPaymentEnabled) {
          // Split payment is unchecked
          if (balancePoints >= invoiceTotalValue) {
            // Eligible points are enough, allow Points payment type
            pointsRedeemValue = invoiceTotalValue.toString();
          } else {
            // Eligible points are not enough, make field read-only
            pointsRedeemValue = '0';
          }
        } else {
          // Split payment is checked, allow manual entry
          if (balancePoints < 0) {
            pointsRedeemValue = '0';
          }
        }
        
        // Update payments state with both eligible points and points redeem in one call
        setPayments(p => ({
          ...p,
          eligiblePoints: balancePoints.toString(),
          pointsRedeem: pointsRedeemValue
        }));
        
        console.log('Updated payments state with eligiblePoints:', balancePoints.toString(), 'pointsRedeem:', pointsRedeemValue);
        
        return balancePoints;
      }
      return 0;
    } catch (err) {
      console.error('Error fetching customer points:', err);
      return 0;
    }
  };

  // Customer autofill using API (phone or UNKNOWN)
  useEffect(() => {
    const fetchUnknownDetails = async () => {
      setPhoneLoading(true);
      setPhoneError('');
      try {
        const baseUrl = getBaseUrl();
        const token = localStorage.getItem('token') || '';
        const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
        const response = await fetch(`${baseUrl}/api/UnkonwDetails?InputValue=UNKNOWN`, {
          headers: {
            'Authorization': authToken,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) throw new Error('Failed to fetch unknown customer details');
        const data = await response.json();
        if (data.status === 0 && Array.isArray(data.data) && data.data.length > 0) {
          const customerData = data.data[0];
          setCustomer(f => ({
            ...f,
            Phone: customerData.Mobile || '',
            Name: customerData.CompanyName || 'UNKNOWN',
            Company: customerData.ContactName || 'UNKNOWN',
            Email: customerData.EmailId || '',
            Address: customerData.Address || '',
            GST: customerData.GSTNo || '',
            State: customerData.State || f.State || '',
            Locality: customerData.Locality || '',
          }));

          const dueRaw = customerData.CustomerDue ?? customerData.TotalDue;
          if (dueRaw !== undefined && dueRaw !== null && !Number.isNaN(Number(dueRaw))) {
            setCustomerTotalDue(Number(dueRaw));
          } else {
            setCustomerTotalDue(null);
          }

          if (customerData.Code) {
            setCustomer(f => ({
              ...f,
              customerCode: customerData.Code
            }));
          }
        } else {
          setCustomerTotalDue(null);
        }
      } catch (err) {
        setPhoneError('Could not fetch customer');
        setCustomerTotalDue(null);
      } finally {
        setPhoneLoading(false);
      }
    };

    const fetchCustomerByInputValue = async (inputValue) => {
      setPhoneLoading(true);
      setPhoneError('');
      try {
        const baseUrl = getBaseUrl();
        const token = localStorage.getItem('token') || '';
        const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
        const response = await fetch(`${baseUrl}/api/CustomerPhoneContains?Phone=${encodeURIComponent(inputValue)}`, {
          headers: {
            'Authorization': authToken,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) throw new Error('Failed to fetch customer');
        const data = await response.json();
        if (data.status === 0 && Array.isArray(data.data) && data.data.length > 0) {
          const customerData = data.data[0];
          console.log('Customer data from API:', customerData);
          setCustomer(f => ({
            ...f,
            Phone: customerData.Mobile || f.Phone || '',
            Name: customerData.CompanyName || '',
            Company: customerData.ContactName || '',
            Email: customerData.EmailId || '',
            Address: customerData.Address || '',
            GST: customerData.GSTNo || '',
            State: customerData.State || '',
            Locality: customerData.Locality || '',
          }));
          
          // Extract and store TotalDue from API response
          const dueRaw = customerData.CustomerDue ?? customerData.TotalDue;
          if (dueRaw !== undefined && dueRaw !== null && !Number.isNaN(Number(dueRaw))) {
            setCustomerTotalDue(Number(dueRaw));
          } else {
            setCustomerTotalDue(null);
          }
          
          // Store customer code for later use when Points payment method is selected
          if (customerData.Code) {
            setCustomer(f => ({
              ...f,
              customerCode: customerData.Code
            }));
          }
        } else {
          // Reset TotalDue when no customer data is found
          setCustomerTotalDue(null);
        }
      } catch (err) {
        setPhoneError('Could not fetch customer');
      } finally {
        setPhoneLoading(false);
      }
    };
    if (isUnknown) {
      fetchUnknownDetails();
      return;
    }
    if (customer.Phone && customer.Phone.length === 10) {
      fetchCustomerByInputValue(customer.Phone);
    }
  }, [customer.Phone, isUnknown]);

  // New function to fetch phone suggestions while typing
  const fetchPhoneSuggestions = async (phonePrefix) => {
    if (!phonePrefix || phonePrefix.length < 1) {
      setPhoneSuggestions([]);
      return;
    }
    
    setPhoneSuggestionsLoading(true);
    try {
      const baseUrl = getBaseUrl();
      const token = localStorage.getItem('token') || '';
      const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      const response = await fetch(`${baseUrl}/api/CustomerPhoneContains?Phone=${encodeURIComponent(phonePrefix)}`, {
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.status === 0 && Array.isArray(data.data)) {
          const normalized = data.data
            .filter(customer => (customer?.Mobile || '').includes(phonePrefix))
            .slice(0, 50);
          setPhoneSuggestions(normalized);
        }
      }
    } catch (err) {
      console.log('Error fetching phone suggestions:', err);
    } finally {
      setPhoneSuggestionsLoading(false);
    }
  };

  // Debounced phone suggestions
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (customer.Phone && customer.Phone.length >= 1 && customer.Phone.length < 10) {
        fetchPhoneSuggestions(customer.Phone);
      } else {
        setPhoneSuggestions([]);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [customer.Phone]);

  useEffect(() => {
    if (showPhoneSuggestions && phoneSuggestions.length > 0) {
      setPhoneDropdownActiveIndex(0);
    } else {
      setPhoneDropdownActiveIndex(-1);
    }
  }, [showPhoneSuggestions, phoneSuggestions]);

  useEffect(() => {
    if (phoneDropdownActiveIndex < 0) return;
    const items = phoneDropdownRef.current?.querySelectorAll('.phone-dropdown-item');
    const el = items && items[phoneDropdownActiveIndex];
    el?.scrollIntoView({ block: 'nearest' });
  }, [phoneDropdownActiveIndex]);

  const applyPhoneSuggestion = (suggestion) => {
    setCustomer(f => ({
      ...f,
      Phone: suggestion?.Mobile || '',
      Name: suggestion?.CompanyName || '',
      Company: suggestion?.ContactName || '',
      Email: suggestion?.EmailId || '',
      Address: suggestion?.Address || '',
      GST: suggestion?.GSTNo || '',
      State: suggestion?.State || '',
      Locality: suggestion?.Locality || '',
      customerCode: suggestion?.Code || f.customerCode,
    }));
    const dueRaw = suggestion?.CustomerDue ?? suggestion?.TotalDue;
    const dueNum = dueRaw === null || dueRaw === undefined || Number.isNaN(Number(dueRaw)) ? null : Number(dueRaw);
    setCustomerTotalDue(dueNum);
    setShowPhoneSuggestions(false);
  };

  // Unknown logic
  useEffect(() => {
    if (isUnknown) {
      setCustomer(f => ({ ...f, Name: 'UNKNOWN', Company: 'UNKNOWN' }));
      setShowPhoneSuggestions(false);
      setPhoneSuggestions([]);
    } else {
      setCustomer(f => ({
        ...f,
        Name: f.Name === 'UNKNOWN' ? '' : f.Name,
        Company: f.Company === 'UNKNOWN' ? '' : f.Company,
      }));
    }
  }, [isUnknown]);

  // Function to format due amount display based on value
  const formatDueAmount = (totalDue: number | null) => {
    if (totalDue === null || totalDue === undefined) {
      return { text: 'Due: Not Available', clickable: false };
    }
    
    const absValue = Math.abs(totalDue);
    const formattedAmount = `Rs. ${absValue.toFixed(2)}`;
    
    if (totalDue < 0) {
      return { text: `We need to pay: ${formattedAmount}`, clickable: true };
    } else if (totalDue === 0) {
      return { text: `Due: ${formattedAmount}`, clickable: true };
    } else {
      return { text: `Due: ${formattedAmount}`, clickable: true };
    }
  };

  // Function to handle navigation to Customer Ledger
  const handleDueAmountClick = () => {
    if (customerTotalDue !== null && customer.Name) {
      // Navigate to Customer Ledger page with customer name pre-populated
      navigate('/reports/customer-ledger', { 
        state: { 
          customerName: customer.Name,
          searchTerm: customer.Name 
        } 
      });
    }
  };

  // New states for fetching locations and companies
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationsError, setLocationsError] = useState('');
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [companiesError, setCompaniesError] = useState('');

  // Company dropdown logic - only in create mode; in edit mode preserve CustState from API
  useEffect(() => {
    if (customer.SelectCompany && companies.length > 0 && !isEditMode) {
      const company = companies.find(c => String(c.Id) === String(customer.SelectCompany));
      if (company && company.State && customer.State !== company.State) {
        setCustomer(f => ({ ...f, State: company.State }));
      }
    }
  }, [customer.SelectCompany, companies, isEditMode]);

  // Location dropdown logic
  useEffect(() => {
    if (customer.Location) {
      // const location = mockCompanies.find(c => c.Name === customer.Location);
      // if (location && location.State !== customer.State) {
      //   setCustomer(f => ({ ...f, State: location.State }));
      // }
    }
  }, [customer.Location]);

  // Partner dropdown logic
  useEffect(() => {
    if (partners.length > 0 && !customer.Partner) {
      setCustomer(f => ({ ...f, Partner: partners[0].Id }));
    }
  }, [partners]);

  // State dropdown logic - only in create mode; in edit mode preserve CustState from API
  useEffect(() => {
    if (states.length > 0 && !customer.State && !isEditMode) {
      setCustomer(f => ({ ...f, State: states[0].State }));
      setPrevState(states[0].State);
    }
  }, [states, isEditMode]);

  // Fetch states from API on mount
  useEffect(() => {
    const fetchStates = async () => {
      setStatesLoading(true);
      setStatesError('');
      try {
        const baseUrl = getBaseUrl();
        const token = localStorage.getItem('token') || '';
        const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
        const response = await fetch(`${baseUrl}/api/States`, {
          headers: {
            'Authorization': authToken,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) throw new Error('Failed to fetch states');
        const data = await response.json();
        setStates(data.data || []);
      } catch (err) {
        setStatesError('Could not load states');
        setStates([]);
      } finally {
        setStatesLoading(false);
      }
    };
    fetchStates();
  }, []);

  // Fetch partners from API on mount
  useEffect(() => {
    const fetchPartners = async () => {
      setPartnersLoading(true);
      setPartnersError('');
      try {
        const baseUrl = getBaseUrl();
        const token = localStorage.getItem('token') || '';
        const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
        const response = await fetch(`${baseUrl}/api/Partners`, {
          headers: {
            'Authorization': authToken,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) throw new Error('Failed to fetch partners');
        const data = await response.json();
        setPartners(data.data || []);
      } catch (err) {
        setPartnersError('Could not load partners');
        setPartners([]);
      } finally {
        setPartnersLoading(false);
      }
    };
    fetchPartners();
  }, []);

  // Fetch representatives
  useEffect(() => {
    const fetchRepresentatives = async () => {
      try {
        setRepresentativesLoading(true);
        setRepresentativesError('');
        const baseUrl = getBaseUrl();
        const token = localStorage.getItem('token') || '';
        const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
        const response = await fetch(`${baseUrl}/api/Representative`, {
          headers: {
            'Authorization': authToken,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
        });
        if (!response.ok) throw new Error('Failed to fetch representatives');
        const data = await response.json();
        const reps = (data?.data || data?.Data || []).map((item: any) => ({
          ...item,
          Representative: item.RepName || item.Representative || '',
        }));
        setRepresentatives(reps);
      } catch (err: any) {
        setRepresentativesError(err?.message || 'Error loading representatives');
        setRepresentatives([]);
      } finally {
        setRepresentativesLoading(false);
      }
    };
    fetchRepresentatives();
  }, []);

  // Confirmation dialogs for company/location/state change
  const handleCompanyChange = (newCompany) => {
    if (customer.SelectCompany && newCompany !== customer.SelectCompany && products.length > 0) {
      setPendingCompany(newCompany);
      setShowCompanyConfirm(true);
    } else {
      setCustomer(f => ({ ...f, SelectCompany: newCompany }));
      setPrevCompany(newCompany);
    }
  };
  const handleLocationChange = (newLocation) => {
    if (customer.Location && newLocation !== customer.Location && products.length > 0) {
      setPrevLocation(customer.Location); // capture current location so Cancel restores it
      setPendingLocation(newLocation);
      setShowLocationConfirm(true);
    } else {
      setCustomer(f => ({ ...f, Location: newLocation }));
      setPrevLocation(newLocation);
    }
  };
  const handleStateChange = (newState) => {
    if (customer.State && newState !== customer.State && products.length > 0) {
      setPendingState(newState);
      setShowStateConfirm(true);
    } else {
      setCustomer(f => ({ ...f, State: newState }));
      setPrevState(newState);
    }
  };

  // Handlers to move between sections - each card operates independently
  const goToSection = (section: 'customer' | 'product' | 'payment') => {
    // Special handling for payment section - allow access since we have real-time validation
    if (section === 'payment') {
      setHasAccessedPaymentSection(true);
    }
    
    // Normal section switching logic
    if (openSection === section) {
      const sections = ['customer', 'product', 'payment'];
      const currentIndex = sections.indexOf(section);
      const nextIndex = (currentIndex + 1) % sections.length;
      setOpenSection(sections[nextIndex] as typeof openSection);
    } else {
      setOpenSection(section as typeof openSection);
    }
  };

  // Helper: match serial by trimmed, case-insensitive comparison (supports text like "one" matching "one " in SerialDetails)
  const serialMatches = (serialFromApi: string | undefined, searchTerm: string) =>
    (serialFromApi ?? '').trim().toLowerCase() === searchTerm.trim().toLowerCase();

  // Handle serial number search (when user searches by serial number)
  const handleSerialNumberSearch = async (serialNumber: string) => {
    if (!serialNumber.trim() || !customer.Partner || !customer.Location) return;
    
    try {
      const latest = await fetchProductsFiltered(serialNumber);
      const searchTrimmed = serialNumber.trim();
      const found = latest.data?.find((p: any) => {
        // Check if any serial detail matches the searched serial number (text or number, trimmed & case-insensitive)
        if (p.SerialDetails && Array.isArray(p.SerialDetails)) {
          return p.SerialDetails.some((sd: any) => 
            serialMatches(sd.SerialNumber, searchTrimmed)
          );
        }
        return false;
      });
      
      if (found) {
        const hasSerial = found.hasSerial === 1 || found.HasSerialFlag === 1;
        if (hasSerial && found.SerialDetails && found.SerialDetails.length > 0) {
          // Find the matching serial detail (trimmed, case-insensitive)
          const matchingSerial = found.SerialDetails.find((sd: any) => 
            serialMatches(sd.SerialNumber, searchTrimmed)
          );
          
          const alreadyAdded = matchingSerial && [...addedSerialNumbers].some((added) => serialMatches(added, searchTrimmed));
          if (matchingSerial && !alreadyAdded) {
            // Add product with this serial number
            addProductToTableWithSerial(found, [matchingSerial]);
            setSerialNumberSearchTerm('');
            setProductAddMessage('Product added with serial number.');
            setProductAddMessageType('success');
            // Track added serial number (use actual value from API so removal works)
            setAddedSerialNumbers(prev => new Set(prev).add(matchingSerial.SerialNumber ?? ''));
          } else if (alreadyAdded) {
            setProductAddMessage('Serial number already added.');
            setProductAddMessageType('error');
          }
        }
        // If hasSerial = 0, do nothing (as per requirements)
      } else {
        // No product has this serial in SerialDetails
        setProductAddMessage("The serial number doesn't exist.");
        setProductAddMessageType('error');
      }
    } catch (error) {
      console.error('Error searching serial number:', error);
    }
  };

  // Add product to DataTable with serial numbers
  const addProductToTableWithSerial = (productDetails: any, serialDetails: any[]) => {
    if (!serialDetails || serialDetails.length === 0) {
      addProductToTable(productDetails);
      return;
    }

    // Add one product row per serial number
    serialDetails.forEach((serialDetail) => {
      const mrp = parseFloat(productDetails.MRP) || 0;
      const qty = 1; // Each serial is quantity 1
      const gst = parseFloat(productDetails.GST) || 0;
      const cess = parseFloat(productDetails.CessPer) || 0;
      
      // Use serial rate if available, otherwise use product OfferPrice or MRP
      const serialRate = parseFloat(serialDetail.Rate) || 0;
      const offerPrice = serialRate > 0 ? serialRate : (productDetails.OfferPrice 
        ? parseFloat(productDetails.OfferPrice) 
        : mrp);
      
      // Calculate Display DiscountPer = 100 - (OfferPrice * 100 / MRP)
      const displayDiscountPer = mrp > 0 ? 100 - (offerPrice * 100 / mrp) : 0;
      
      // Calculate Display discount = MRP - OfferPrice * Qty
      const displayDiscount = (mrp - offerPrice) * qty;
      
      // Calculate Taxable Value = OfferPrice * qty / (1 + (GstPercent + CessPercent) / 100)
      const taxableValue = (offerPrice * qty) / (1 + (gst + cess) / 100);
      
      // Calculate SGST+CGST or IGST = Taxable Value * GstPercent / 100
      const gstAmount = taxableValue * gst / 100;
      
      // Calculate Cess Amount = Taxable Value * CessPercent / 100
      const cessAmount = taxableValue * cess / 100;
      
      // Calculate Total = Taxable Value + GST Amount + Cess Amount
      const total = taxableValue + gstAmount + cessAmount;
      
      // Calculate Unit Price for backward compatibility
      const totalTax = gst + cess;
      const unitPrice = totalTax > 0 ? mrp / (1 + totalTax / 100) : mrp;
      
      const newProduct = {
        ...productDetails,
        Qty: qty,
        DelQty: qty,
        UnitPrice: String(unitPrice.toFixed(2)),
        OriginalUnitPrice: String(unitPrice.toFixed(2)),
        DiscountPer: String(displayDiscountPer.toFixed(2)),
        Discount: String(displayDiscount.toFixed(2)),
        OfferPrice: String(offerPrice.toFixed(2)),
        TaxableValue: String(taxableValue.toFixed(2)),
        GSTAmount: String(gstAmount.toFixed(2)),
        CessAmount: String(cessAmount.toFixed(2)),
        Total: String(total.toFixed(2)),
        SerialNumber: serialDetail.SerialNumber, // Add serial number
        SerialDetails: [serialDetail], // Store serial detail
        hasSerial: true,
      };
      
      setProducts(prev => {
        if (invoiceForm.AddInFIFO) {
          // FIFO: add to bottom
          return [...prev, newProduct];
        } else {
          // LIFO: add to top
          return [newProduct, ...prev];
        }
      });
    });
    
    setProductAddMessage(`${serialDetails.length} product(s) added with serial numbers.`);
    setProductAddMessageType('success');
  };

  // Handle serial selection from popup
  const handleSerialSelection = (selectedSerials: any[]) => {
    if (!serialPopupProduct || selectedSerials.length === 0) return;
    
    // Track added serial numbers
    const newSerialNumbers = new Set(addedSerialNumbers);
    selectedSerials.forEach(serial => {
      newSerialNumbers.add(serial.SerialNumber);
    });
    setAddedSerialNumbers(newSerialNumbers);
    
    // Add products with selected serials
    addProductToTableWithSerial(serialPopupProduct, selectedSerials);
    
    // Close popup
    setShowSerialSelectionPopup(false);
    setSerialPopupProduct(null);
  };

  // Add product to table. When Duplicate Invoice Line Items enabled, always add as new row; otherwise merge by barcode.
  const addProductToTable = (product) => {
    const duplicateLineItems = salesInvoiceSettings?.duplicateInvoiceLineItems ?? false;
    setProducts(prev => {
      const idx = duplicateLineItems ? -1 : prev.findIndex(p => p.Barcode === product.Barcode);
      const addQty = Number(product.Qty) || 1;
      if (idx !== -1) {
        // Product exists, update quantity
        const existing = prev[idx];
        const newQty = (Number(existing.Qty) || 1) + addQty;
        const existingDelQty = Number(existing.DelQty ?? existing.Qty ?? 1);
        const newDelQty = existingDelQty + addQty;
        const updated = { 
          ...existing, 
          Qty: newQty,
          DelQty: newDelQty
        };
        const newArr = [...prev];
        newArr[idx] = updated;
        return newArr;
      }
      // New product
      const mrp = parseFloat(product.MRP) || 0;
      const gst = parseFloat(product.GST) || 0;
      const cess = parseFloat(product.CessPer) || 0;
      
      // Use OfferPrice from API if available, otherwise use MRP
      const offerPrice = product.OfferPrice 
        ? parseFloat(product.OfferPrice) 
        : mrp;
      
      // Calculate Display DiscountPer = 100 - (OfferPrice * 100 / MRP)
      const displayDiscountPer = mrp > 0 ? 100 - (offerPrice * 100 / mrp) : 0;
      
      // Calculate Display discount = (MRP - OfferPrice) * Qty
      const displayDiscount = (mrp - offerPrice) * addQty;
      
      // Calculate Taxable Value = OfferPrice * qty / (1 + (GstPercent + CessPercent) / 100)
      const taxableValue = (offerPrice * addQty) / (1 + (gst + cess) / 100);
      
      // Calculate SGST+CGST or IGST = Taxable Value * GstPercent / 100
      const gstAmount = taxableValue * gst / 100;
      
      // Calculate Cess Amount = Taxable Value * CessPercent / 100
      const cessAmount = taxableValue * cess / 100;
      
      // Calculate Total = Taxable Value + GST Amount + Cess Amount
      const total = taxableValue + gstAmount + cessAmount;
      
      // Calculate Unit Price for backward compatibility
      const totalTax = gst + cess;
      const unitPrice = totalTax > 0 ? mrp / (1 + totalTax / 100) : mrp;
      
      const newProduct = {
        ...product,
        Qty: addQty,
        DelQty: addQty,
        UnitPrice: String(unitPrice.toFixed(2)),
        OriginalUnitPrice: String(unitPrice.toFixed(2)),
        DiscountPer: String(displayDiscountPer.toFixed(2)),
        Discount: String(displayDiscount.toFixed(2)),
        OfferPrice: String(offerPrice.toFixed(2)),
        TaxableValue: String(taxableValue.toFixed(2)),
        GSTAmount: String(gstAmount.toFixed(2)),
        CessAmount: String(cessAmount.toFixed(2)),
        Total: String(total.toFixed(2))
      };
      
      if (invoiceForm.AddInFIFO) {
        // FIFO: add to bottom
        return [...prev, newProduct];
      } else {
        // LIFO: add to top
        return [newProduct, ...prev];
      }
    });
    setProductAddMessage('Product added.');
    setProductAddMessageType('success');
  };

  // Remove product
  const handleRemoveProduct = (index) => {
    const removed = products[index];
    const serialToRemove = removed?.SerialNumber
      || removed?.SerialDetails?.[0]?.SerialNumber
      || null;
    if (serialToRemove) {
      setAddedSerialNumbers(prev => {
        const next = new Set(prev);
        next.delete(serialToRemove);
        return next;
      });
    }
    setProducts(prev => prev.filter((_, i) => i !== index));
  };

  // Handler for Unit Price validation - Confirm
  const handleUnitPriceConfirm = () => {
    // Clear any pending validation timeout
    if (unitPriceValidationTimeout.current) {
      clearTimeout(unitPriceValidationTimeout.current);
    }

    if (unitPriceValidationData) {
      const { index, enteredValue } = unitPriceValidationData;
      
      // Apply the entered value and recalculate
      const qty = parseFloat(products[index].Qty) || 1;
      const unitPrice = parseFloat(enteredValue) || 0;
      const gst = parseFloat(products[index].GST) || 0;
      const cess = parseFloat(products[index].CessPer) || 0;
      const totalTax = gst + cess;
      const mrp = unitPrice * (1 + totalTax / 100);
      
      setProducts(prev => prev.map((p, i) => i === index ? {
        ...p,
        UnitPrice: enteredValue,
        MRP: mrp.toFixed(2),
        OfferPrice: enteredValue,
        Total: (qty * unitPrice).toFixed(2),
      } : p));
    }
    
    setShowUnitPriceValidation(false);
    setUnitPriceValidationData(null);
  };

  // Handler for Unit Price validation - Cancel
  const handleUnitPriceCancel = () => {
    // Clear any pending validation timeout
    if (unitPriceValidationTimeout.current) {
      clearTimeout(unitPriceValidationTimeout.current);
    }

    if (unitPriceValidationData) {
      const { index, originalValue } = unitPriceValidationData;
      
      // Revert to original unit price and recalculate
      const qty = parseFloat(products[index].Qty) || 1;
      const unitPrice = parseFloat(originalValue) || 0;
      const gst = parseFloat(products[index].GST) || 0;
      const cess = parseFloat(products[index].CessPer) || 0;
      const totalTax = gst + cess;
      const mrp = unitPrice * (1 + totalTax / 100);
      
      setProducts(prev => prev.map((p, i) => i === index ? {
        ...p,
        UnitPrice: originalValue,
        MRP: mrp.toFixed(2),
        OfferPrice: originalValue,
        Total: (qty * unitPrice).toFixed(2),
      } : p));
    }
    
    setShowUnitPriceValidation(false);
    setUnitPriceValidationData(null);
  };

  // Handler for Tab/Enter key navigation in product table
  const handleFieldKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number, currentField: string) => {
    if (e.key === 'Tab' || e.key === 'Enter') {
      e.preventDefault(); // Prevent default Tab/Enter behavior
      
      // Define the field order for navigation
      const fieldOrder = ['MRP', 'UnitPrice', 'DiscountPer', 'Discount', 'CostPrice', 'Qty', 'OfferPrice'];
      const currentFieldIndex = fieldOrder.indexOf(currentField);
      const isShiftTab = e.key === 'Tab' && e.shiftKey;
      
      // Determine the next field
      let nextField: string | null = null;
      let nextRowIndex = index;
      
      if (isShiftTab) {
        // Navigate backwards
        if (currentFieldIndex > 0) {
          nextField = fieldOrder[currentFieldIndex - 1];
          
          // Skip Cost Price field if it's not visible
          if (nextField === 'CostPrice' && !salesInvoiceSettings.showCostPrice) {
            if (currentFieldIndex - 2 >= 0) {
              nextField = fieldOrder[currentFieldIndex - 2];
            } else if (index > 0) {
              // Move to last field of previous row
              nextField = fieldOrder[fieldOrder.length - 1];
              nextRowIndex = index - 1;
            } else {
              nextField = null;
            }
          }
        } else if (index > 0) {
          // Move to last field of previous row
          nextField = fieldOrder[fieldOrder.length - 1];
          nextRowIndex = index - 1;
        }
      } else {
        // Navigate forwards
        if (currentFieldIndex < fieldOrder.length - 1) {
          nextField = fieldOrder[currentFieldIndex + 1];
          
          // Skip Cost Price field if it's not visible
          if (nextField === 'CostPrice' && !salesInvoiceSettings.showCostPrice) {
            if (currentFieldIndex + 2 < fieldOrder.length) {
              nextField = fieldOrder[currentFieldIndex + 2];
            } else if (index < products.length - 1) {
              // Move to first field of next row
              nextField = fieldOrder[0];
              nextRowIndex = index + 1;
            } else {
              nextField = null;
            }
          }
        } else if (index < products.length - 1) {
          // Move to first field of next row
          nextField = fieldOrder[0];
          nextRowIndex = index + 1;
        }
      }
      
      // Move focus to the next field after a short delay to ensure recalculation completes
      if (nextField) {
        setTimeout(() => {
          const nextInput = document.querySelector(
            `input[data-field="${nextField}"][data-index="${nextRowIndex}"]`
          ) as HTMLInputElement;
          if (nextInput) {
            nextInput.focus();
            nextInput.select();
          }
        }, 50); // Small delay to ensure state updates complete
      }
    }
  };

  // Auto-hide product add message
  useEffect(() => {
    if (productAddMessage) {
      const timer = setTimeout(() => {
        setProductAddMessage(null);
        setProductAddMessageType(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [productAddMessage]);



  // 4. Fetch products for dropdowns using real API
  const fetchProductsFiltered = async (search) => {
    const partnerId = customer.Partner || (partners[0] && partners[0].Id) || '';
    const locationId = customer.Location || (locations[0] && locations[0].ID) || '';
    if (!locationId || !partnerId) {
      return { data: [] };
    }
    const baseUrl = getBaseUrl();
    const token = localStorage.getItem('token') || '';
    const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    // Debug log to verify correct IDs are used
    console.log('fetchProductsFiltered', { partnerId, locationId, search, partners, locations });
    const response = await fetch(
      `${baseUrl}/api/AllProducts?partnerID=${partnerId}&LocationID=${locationId}&FilterValue=${encodeURIComponent(search)}`,
      {
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json',
        },
      }
    );
    if (!response.ok) throw new Error('Failed to fetch products');
    const data = await response.json();
    if (Array.isArray(data.data)) {
      data.data = data.data.map((item) => ({
        ...item,
        Title: item.title,
        Barcode: item.barcode,
        Category: item.Category,
        HSN: item.HSN,
        MRP: item.rate,
        CostPrice: item.costprice,
        UQCName: item.UQCName,
        BrandName: item.BrandName,
        Available: item.available,
        OfferPrice: item.OfferPrice,
        CessPer: item.CessPer,
        GST: item.gstper !== undefined && item.gstper !== null ? item.gstper : 0,
        isTraceable: item.isTraceable, // Add isTraceable field
        hasSerial: item.hasSerial || item.HasSerialFlag || 0, // Preserve hasSerial
        HasSerialFlag: item.HasSerialFlag || item.hasSerial || 0, // Preserve HasSerialFlag
        SerialDetails: item.SerialDetails || [], // Preserve SerialDetails
      }));
    }
    return data;
  };

  const {
    data: productSearchResponse,
    isLoading: isProductSearchLoading,
    refetch: refetchProductSearch,
  } = useQuery({
    queryKey: ['products-search', productSearch, customer.Partner, customer.Location],
    queryFn: () => fetchProductsFiltered(productSearch),
    enabled: !!customer.Partner && !!customer.Location, // Remove productSearch requirement for edit mode
    staleTime: 1000 * 60 * 5,
  });
  const productSearchResults = productSearchResponse?.data || [];

  // Add this effect to refetch when location/partner are set
  useEffect(() => {
    if (customer.Partner && customer.Location) {
      refetchProductSearch();
    }
  }, [customer.Partner, customer.Location, refetchProductSearch]);

  // Ensure partner and location are set in edit mode
  useEffect(() => {
    if (isEditMode && editingInvoiceId) {
      // If partner is not set but partners are available, set to first partner
      if (!customer.Partner && partners.length > 0) {
        setCustomer(f => ({ ...f, Partner: partners[0].Id.toString() }));
      }
      
      // If location is not set but locations are available, set to first location
      if (!customer.Location && locations.length > 0) {
        setCustomer(f => ({ ...f, Location: locations[0].ID }));
      }
    }
  }, [isEditMode, editingInvoiceId]); // Remove dependencies that cause continuous re-runs

  // Debug products array in edit mode - only run once when products are loaded
  useEffect(() => {
    if (isEditMode && editingInvoiceId && products.length > 0) {
      console.log('Edit mode - Products loaded successfully:', products.length);
    }
  }, [isEditMode, editingInvoiceId, products.length]); // Only depend on products.length

  // Debug customer state changes in edit mode
  // useEffect(() => {
  //   if (isEditMode && editingInvoiceId) {
  //     console.log('Edit mode - Customer state updated:', {
  //       Partner: customer.Partner,
  //       Location: customer.Location,
  //       SelectCompany: customer.SelectCompany,
  //       Name: customer.Name,
  //       Company: customer.Company
  //     });
  //   }
  // }, [isEditMode, editingInvoiceId, customer.Partner, customer.Location, customer.SelectCompany, customer.Name, customer.Company]);

  // Handle navigation from SaleProductEntryForm - open product section when returning
  useEffect(() => {
    console.log('SalesInvoiceMobile: Checking location.state:', location.state);
    console.log('SalesInvoiceMobile: Checking sessionStorage for productAdded:', sessionStorage.getItem('productAdded'));
    
    // Check both location.state and sessionStorage for return indicators
    const shouldReturnToProducts = (location.state?.scrollToSection === 'products' && location.state?.productAdded) ||
                                  (sessionStorage.getItem('productAdded') === 'true');
    
    if (shouldReturnToProducts) {
      console.log('SalesInvoiceMobile: Opening product section');
      // Open product section when returning from product form
      setOpenSection('product');
      
      // Show success message for product added
      toast({
        title: 'Product Added Successfully',
        description: 'Product has been added to inventory. You can now search for it.',
        className: 'bg-green-600 text-white border-green-700'
      });
      
      // Clear the sessionStorage flags
      sessionStorage.removeItem('productAdded');
      sessionStorage.removeItem('returnSection');
      
      // Clear the state so it doesn't trigger again
      if (location.state?.scrollToSection) {
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [location.state, navigate, location.pathname]);

  // Additional effect to check sessionStorage on component mount
  useEffect(() => {
    console.log('SalesInvoiceMobile: Component mounted, checking sessionStorage');
    const productAdded = sessionStorage.getItem('productAdded') === 'true';
    
    if (productAdded) {
      console.log('SalesInvoiceMobile: Product added detected on mount, opening product section');
      // Open product section when returning from product form
      setOpenSection('product');
      
      // Show success message for product added
      toast({
        title: 'Product Added Successfully',
        description: 'Product has been added to inventory. You can now search for it.',
        className: 'bg-green-600 text-white border-green-700'
      });
      
      // Clear the sessionStorage flags
      sessionStorage.removeItem('productAdded');
      sessionStorage.removeItem('returnSection');
    }
  }, []); // Empty dependency array - runs only on mount

  // 1. Add state for payment methods and calculations
  // Remove local payment state variables
  // Use payments and setPayments from context
  // Example usage:
  // value={payments.cashPayment}
  // onChange={e => setPayments(p => ({ ...p, cashPayment: e.target.value }))}
  const [walletProvider, setWalletProvider] = useState(payments.walletProvider || 'PhonePe');
  const [walletTxNo, setWalletTxNo] = useState(payments.walletTxNo || '');
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);

  const invoiceTotal = products.reduce((sum, product) => {
    const offer = parseFloat(product.OfferPrice) || 0;
    const qty = parseFloat(product.Qty) || 0;
    return sum + (offer * qty);
  }, 0);
  // Calculate points value (1 point = ₹1)
  const pointsValue = (parseFloat(payments.pointsRedeem) || 0);
  const eligiblePoints = parseFloat(payments.eligiblePoints) || 0;
  const roundedEligiblePoints = Math.round(eligiblePoints * 100) / 100;
  const remainingPointsValue = Math.max(0, roundedEligiblePoints - pointsValue);
  const formattedEligiblePoints = roundedEligiblePoints.toFixed(2);
  const formattedRemainingPoints = remainingPointsValue.toFixed(2);
  
  // Validate points redemption
  const isPointsValid = eligiblePoints >= 0 && pointsValue <= eligiblePoints;
  
  // Calculate valid coupon amount (exclude if exceeds maximum allowed)
  const getValidCouponAmount = () => {
    // In edit mode, if there's a coupon amount, include it even without validation data
    if (isEditMode && editingInvoiceId && payments.couponAmount) {
      return parseFloat(payments.couponAmount) || 0;
    }
    
    // For create mode, require validation data
    if (!couponValidationData || !payments.couponAmount) return 0;
    const enteredAmount = parseFloat(payments.couponAmount) || 0;
    const maxAmount = parseFloat(couponValidationData.Amount) || 0;
    return enteredAmount <= maxAmount ? enteredAmount : 0;
  };
  
  // Calculate total paid from all payment methods (rounded)
  const totalPaid =
    roundPayment(payments.cashPayment) +
    roundPayment(payments.cardPayment) +
    roundPayment(payments.walletAmount) +
    roundPayment(payments.chequeAmount) +
    roundPayment(getValidCouponAmount()) +
    roundPayment(payments.creditNoteAmount) +
    roundPayment(pointsValue);
  // Allow negative balance to show overpayment
  const balanceDue = Math.round((invoiceTotal - totalPaid) * 100) / 100;

  // Handle split payment toggle and update points logic
  useEffect(() => {
    const eligiblePoints = parseFloat(payments.eligiblePoints) || 0;
    const invoiceTotalValue = invoiceTotal;
    const currentPointsRedeem = parseFloat(payments.pointsRedeem) || 0;
    const isEditMode = editingInvoiceId !== null;
    
    // In edit mode, preserve the API value
    if (isEditMode) {
      console.log('In edit mode, preserving API points value:', currentPointsRedeem);
      return;
    }
    
    // Only proceed with points logic in create mode
    if (payments.selectedPaymentMethod === 'Points') {
      if (eligiblePoints <= 0) {
        // No eligible points, set to 0 and make read-only
        setPayments(p => ({
          ...p,
          pointsRedeem: '0'
        }));
      } else if (!payments.splitPaymentEnabled) {
        // Split payment is unchecked and Points is selected
        if (eligiblePoints >= invoiceTotalValue) {
          // Eligible points are enough, auto-fill with invoice total
          setPayments(p => ({
            ...p,
            pointsRedeem: invoiceTotalValue.toString()
          }));
        } else {
          // Eligible points are not enough, set to 0 and make read-only
          setPayments(p => ({
            ...p,
            pointsRedeem: '0'
          }));
        }
      } else {
        // Split payment is enabled: preserve existing pointsRedeem
        // Do not override in edit mode
      }
    }
  }, [payments.splitPaymentEnabled, payments.selectedPaymentMethod, payments.eligiblePoints, invoiceTotal, editingInvoiceId]);

  // Show/clear credit note exceeds error when products or credit note amount changes
  useEffect(() => {
    const cnAmt = parseFloat(String(payments.creditNoteAmount)) || 0;
    if (cnAmt <= 0) return;
    if (cnAmt > invoiceTotal) {
      setCreditNoteError(
        `Credit Note Amount Exceeds Invoice Total\nThe selected credit note amount (${cnAmt.toFixed(2)}) is greater than the invoice total (${invoiceTotal.toFixed(2)}).\nPlease add more products or remove the credit note to continue.`
      );
    } else {
      setCreditNoteError(prev => {
        if (prev && prev.includes('Credit Note Amount Exceeds Invoice Total')) return '';
        return prev;
      });
    }
  }, [invoiceTotal, payments.creditNoteAmount]);

  // Debug: Monitor payments state changes
  useEffect(() => {
    console.log('Payments state changed (Mobile):', {
      eligiblePoints: payments.eligiblePoints,
      pointsRedeem: payments.pointsRedeem,
      selectedPaymentMethod: payments.selectedPaymentMethod
    });
  }, [payments.eligiblePoints, payments.pointsRedeem, payments.selectedPaymentMethod]);

  // Fetch eligible points when component loads in edit mode with Points payment method
  useEffect(() => {
    if (isEditMode && editingInvoiceId && payments.selectedPaymentMethod === 'Points' && customer.customerCode) {
      console.log('Edit mode: Fetching eligible points for Points payment method (Mobile)');
      fetchPointsWhenSelected();
    }
  }, [isEditMode, editingInvoiceId, payments.selectedPaymentMethod, customer.customerCode]);

  // Clear coupon validation data when payment method changes
  // Modified to preserve coupon validation data when switching payment methods
  useEffect(() => {
    if (payments.selectedPaymentMethod !== 'Coupon') {
      setCouponError('');
      // Don't clear couponValidationData to preserve validation state
      // setCouponValidationData(null); // Commented out to preserve validation
    }
  }, [payments.selectedPaymentMethod]);

  // Sync local walletTxNo and walletProvider state with payments object
  useEffect(() => {
    console.log('SalesInvoiceMobile: Sync useEffect triggered');
    console.log('SalesInvoiceMobile: Current walletTxNo:', walletTxNo);
    console.log('SalesInvoiceMobile: payments.walletTxNo:', payments.walletTxNo);
    console.log('SalesInvoiceMobile: payments.walletProvider:', payments.walletProvider);
    console.log('SalesInvoiceMobile: Current walletProvider:', walletProvider);
    console.log('SalesInvoiceMobile: isEditMode:', isEditMode);
    
    // In edit mode, always prefer the API value from payments.walletTxNo
    if (isEditMode && payments.walletTxNo !== walletTxNo) {
      console.log('SalesInvoiceMobile: Edit mode - syncing walletTxNo from payments to local state:', payments.walletTxNo);
      setWalletTxNo(payments.walletTxNo);
    }
    
    // Sync wallet provider if different
    if (payments.walletProvider && payments.walletProvider !== walletProvider) {
      console.log('SalesInvoiceMobile: Syncing walletProvider from payments to local state:', payments.walletProvider);
      setWalletProvider(payments.walletProvider);
    }
  }, [payments.walletTxNo, walletTxNo, payments.walletProvider, walletProvider, isEditMode]);

  // Debug useEffect to log walletTxNo changes
  useEffect(() => {
    console.log('SalesInvoiceMobile: walletTxNo changed to:', walletTxNo);
    console.log('SalesInvoiceMobile: payments.walletTxNo:', payments.walletTxNo);
  }, [walletTxNo, payments.walletTxNo]);

  // Ensure walletTxNo is updated when payments.walletTxNo is set from API
  useEffect(() => {
    if (isEditMode && payments.walletTxNo && payments.walletTxNo !== walletTxNo) {
      console.log('SalesInvoiceMobile: Updating walletTxNo from API data:', payments.walletTxNo);
      setWalletTxNo(payments.walletTxNo);
    }
  }, [isEditMode, payments.walletTxNo, walletTxNo]);

  // Safety net: if on edit screen with Wallets selected and walletTxNo is still empty,
  // refetch once to populate from API (handles rare race conditions on first load)
  useEffect(() => {
    const needsRefill = isEditMode && editingInvoiceId && payments.selectedPaymentMethod === 'Wallets' && !payments.walletTxNo;
    if (needsRefill) {
      console.log('SalesInvoiceMobile: walletTxNo empty on edit – refetching invoice details to populate PaidDetails');
      fetchAndLogInvoiceDetails(editingInvoiceId as any, true);
    }
    // eslint-disable-next-line
  }, [isEditMode, editingInvoiceId, payments.selectedPaymentMethod, payments.walletTxNo]);

  // In SalesInvoiceMobile component state:
  // const [repName, setRepName] = useState(''); // This line is removed as repName is now from context

  // Fetch locations from API on mount
  useEffect(() => {
    const fetchLocations = async () => {
      setLocationsLoading(true);
      setLocationsError('');
      try {
        const baseUrl = getBaseUrl();
        const token = localStorage.getItem('token') || '';
        const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
        const response = await fetch(`${baseUrl}/api/Locations`, {
          headers: {
            'Authorization': authToken,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) throw new Error('Failed to fetch locations');
        const data = await response.json();
        setLocations(data.data || []);
      } catch (err) {
        setLocationsError('Could not load locations');
        setLocations([]);
      } finally {
        setLocationsLoading(false);
      }
    };
    fetchLocations();
  }, []);

  // Fetch companies from API on mount
  useEffect(() => {
    const fetchCompanies = async () => {
      setCompaniesLoading(true);
      setCompaniesError('');
      try {
        const baseUrl = getBaseUrl();
        const token = localStorage.getItem('token') || '';
        const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
        const response = await fetch(`${baseUrl}/api/Companies`, {
          headers: {
            'Authorization': authToken,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) throw new Error('Failed to fetch companies');
        const data = await response.json();
        setCompanies(data.data || []);
        // Sync State with selected company when companies load - only in create mode; in edit mode preserve CustState from API
        if (data.data && data.data.length > 0 && customer.SelectCompany && !isEditMode) {
          const selectedCompany = data.data.find((c: any) => String(c.Id) === String(customer.SelectCompany));
          if (selectedCompany && selectedCompany.State) {
            setCustomer(f => ({ ...f, State: selectedCompany.State }));
          }
        }
      } catch (err) {
        setCompaniesError('Could not load companies');
        setCompanies([]);
      } finally {
        setCompaniesLoading(false);
      }
    };
    fetchCompanies();
  }, []);

  // Set default company from Settings (Primary Company ID) when companies load - only in create mode
  useEffect(() => {
    if (companies.length > 0 && !customer.SelectCompany && !isEditMode) {
      const defaultCompany = primaryCompanyId
        ? companies.find((c: any) => String(c.Id) === primaryCompanyId || String(c.CompanyID) === primaryCompanyId)
        : null;
      const companyToUse = defaultCompany || companies[0];
      setCustomer(f => ({ ...f, SelectCompany: companyToUse.Id, State: companyToUse.State || '' }));
    }
  }, [companies, primaryCompanyId, isEditMode]);

  // Set default customer.Location when locations are loaded
  useEffect(() => {
    if (locations.length > 0 && !customer.Location) {
      setCustomer(f => ({ ...f, Location: locations[0].ID }));
    }
  }, [locations]);

  // Set default customer.Partner when partners are loaded
  useEffect(() => {
    if (partners.length > 0 && !customer.Partner) {
      setCustomer(f => ({ ...f, Partner: partners[0].Id }));
    }
  }, [partners]);

  // Add state for toggling more fields in each product card
  const [showMoreProductFields, setShowMoreProductFields] = useState({});

  // Add state for toggling Invoice Settings
  const [showInvoiceSettings, setShowInvoiceSettings] = useState(false);
  
  // Add state for Tag fields (Quote No, Mode, Vehicle No)
  // const [invoiceForm, setInvoiceForm] = useState({
  //   QuoteNo: '',
  //   Mode: '',
  //   VehicleNo: ''
  // });

  // Debug logs removed to prevent continuous console output

  const [showPartnerConfirm, setShowPartnerConfirm] = useState(false);
  const [pendingPartner, setPendingPartner] = useState('');
  const [prevPartner, setPrevPartner] = useState('');

  const handlePartnerChange = (newPartner) => {
    if (customer.Partner && newPartner !== customer.Partner && products.length > 0) {
      setPendingPartner(newPartner);
      setShowPartnerConfirm(true);
    } else {
      setCustomer(f => ({ ...f, Partner: newPartner }));
      setPrevPartner(newPartner);
    }
  };

  // Helper to get selected company state
  const getSelectedCompanyState = () => {
    if (!customer.SelectCompany || companies.length === 0) return '';
    const company = companies.find(c => String(c.Id) === String(customer.SelectCompany));
    return company?.State || '';
  };
  const companyState = getSelectedCompanyState();
  const isIGST = customer.State && companyState && customer.State !== companyState;
  // Determine if selected company is Non-GST (GST !== 1)
  const selectedCompany = companies && companies.find((c: any) => String(c.Id) === String(customer.SelectCompany));
  const isNonGSTCompany = selectedCompany ? selectedCompany.GST !== 1 : false;

  const searchInputRef = React.useRef(null);

  // Add at the top of the component:
  // Barcode scanner logic removed as per user request

  // Add useEffect to auto-select State in Ship To popup based on selected company
  useEffect(() => {
    if (showShipToPopup && customer.SelectCompany && companies.length > 0) {
      const company = companies.find(c => String(c.Id) === String(customer.SelectCompany));
      if (company && company.State && (!shipToForm.State || shipToForm.State !== company.State)) {
        setShipToForm(f => ({ ...f, State: company.State }));
      }
    }
    // eslint-disable-next-line
  }, [showShipToPopup, customer.SelectCompany, companies]);

  // Add useEffect to set Name/Company to 'UNKNOWN' in Ship To popup if isUnknown is checked
  useEffect(() => {
    if (showShipToPopup && isUnknown) {
      setShipToForm(prev => ({ ...prev, Name: 'UNKNOWN', Company: 'UNKNOWN' }));
    }
    // eslint-disable-next-line
  }, [showShipToPopup, isUnknown, setShipToForm]);

  // Add this useEffect after invoiceTotal is defined
  React.useEffect(() => {
    if (!payments.splitPaymentEnabled && payments.selectedPaymentMethod) {
      const isEditModeLocal = editingInvoiceId !== null;
      
      // Calculate the total amount to set for the selected payment method (whole number)
      const totalAmount = Math.round(invoiceTotal).toString();
      
      setPayments(p => ({
        ...p,
        // Set the total amount for the selected payment method when split payment is unchecked
        cashPayment: p.selectedPaymentMethod === 'Cash' ? totalAmount : '',
        cardPayment: p.selectedPaymentMethod === 'Card' ? totalAmount : '',
        walletAmount: p.selectedPaymentMethod === 'Wallets' ? totalAmount : '',
        chequeAmount: p.selectedPaymentMethod === 'Cheque' ? totalAmount : '',
        // Keep coupon/credit note amounts independent of split toggle
        couponAmount: p.couponAmount || '0',
        creditNoteAmount: p.creditNoteAmount || '0',
        // For Points, handle based on eligible points
        pointsRedeem: p.selectedPaymentMethod === 'Points' ? 
          (isEditModeLocal ? p.pointsRedeem : 
           (parseFloat(p.eligiblePoints) >= invoiceTotal ? totalAmount : '0')) : '',
        eligiblePoints: p.eligiblePoints, // Always preserve eligible points
      }));
    }
  }, [payments.splitPaymentEnabled, payments.selectedPaymentMethod, editingInvoiceId, invoiceTotal]);

  // Add this handler at the top of the component
  const handlePaymentFieldFocus = (e) => {
    if (payments.splitPaymentEnabled && !e.target.readOnly && !e.target.disabled) {
      e.target.select();
    }
  };

  // Helper to fetch invoice full details
  const fetchInvoiceFullDetails = async (invoiceId) => {
    const baseUrl = getBaseUrl();
    const token = localStorage.getItem('token') || '';
    const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    const url = `${baseUrl}/api/InvoiceFullDetails?InvoiceID=${invoiceId}`;
    
    console.log('[fetchInvoiceFullDetails] Fetching from URL:', url);
    
    const response = await fetch(url, {
      headers: {
        'Authorization': authToken,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      console.error('[fetchInvoiceFullDetails] HTTP Error:', response.status, response.statusText);
      throw new Error(`Failed to fetch invoice details: ${response.status} ${response.statusText}`);
    }
    
    const result = await response.json();
    console.log('[fetchInvoiceFullDetails] API Response:', JSON.stringify(result, null, 2));
    
    return result;
  };

  const [bluetoothDevices, setBluetoothDevices] = useState<any[]>([]);

  const [isScanning, setIsScanning] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [lastUsedDevice, setLastUsedDevice] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectedDeviceName, setConnectedDeviceName] = useState<string>('');

  // Additional state variables for unified Bluetooth modal
  const [isScanningBluetooth, setIsScanningBluetooth] = useState(false);
  const [bluetoothScanError, setBluetoothScanError] = useState<string>('');
  const [selectedBluetoothDevice, setSelectedBluetoothDevice] = useState<string>('');
  const [isBluetoothConnected, setIsBluetoothConnected] = useState(false);
  const [connectedBluetoothDeviceName, setConnectedBluetoothDeviceName] = useState<string>('');
  const [showBluetoothModal, setShowBluetoothModal] = useState(false);
  const [isBluetoothUILoading, setIsBluetoothUILoading] = useState(false);

  useEffect(() => {
    // Load last used device from sessionStorage (persists only during app session)
    const saved = sessionStorage.getItem('lastBluetoothPrinter');
    const savedName = sessionStorage.getItem('lastBluetoothPrinterName');
    console.log('[SalesInvoiceMobile] Initializing Bluetooth state:', { saved, savedName });
    if (saved) {
      setLastUsedDevice(saved);
      setConnectedDeviceName(savedName || '');
      // Immediately add it to the device list so it's available
      setBluetoothDevices([{ 
        address: saved, 
        name: savedName || 'Unknown Device', 
        isPaired: true,
        isConnected: false // Will be updated by checkLastDeviceConnection
      }]);
      // Check if device is still connected
      checkLastDeviceConnection(saved);
    } else {
      console.log('[SalesInvoiceMobile] No saved Bluetooth device found in sessionStorage');
    }
  }, []);

  const checkLastDeviceConnection = async (deviceAddress: string) => {
    if (!deviceAddress || typeof window === 'undefined' || !window.bluetoothSerial) return;
    
    console.log('[checkLastDeviceConnection] Checking connection for device:', deviceAddress);
    try {
      // Try to check if device is still connected by attempting to connect
      // If already connected, this will succeed quickly
      await connectBluetoothDevice(deviceAddress);
      setIsConnected(true);
      // Update the device list to mark device as connected
      setBluetoothDevices(prev => prev.map(device => 
        device.address === deviceAddress 
          ? { ...device, isConnected: true }
          : device
      ));
      console.log('[checkLastDeviceConnection] Device still connected:', deviceAddress);
    } catch (err) {
      setIsConnected(false);
      // Update the device list to mark device as disconnected
      setBluetoothDevices(prev => prev.map(device => 
        device.address === deviceAddress 
          ? { ...device, isConnected: false }
          : device
      ));
      console.log('[checkLastDeviceConnection] Device not connected:', deviceAddress);
    }
  };

  const scanBluetoothDevices = async () => {
    setIsScanning(true);
    setScanError(null);
    
    try {
      // First check if we have a last used device that's still connected
      if (lastUsedDevice && isConnected) {
        console.log('[scanBluetoothDevices] Using already connected device:', lastUsedDevice);
        // If we already have a device list and the connected device is in it, no need to scan again
        if (bluetoothDevices.length > 0 && bluetoothDevices.some(d => d.address === lastUsedDevice)) {
          console.log('[scanBluetoothDevices] Device list already available, skipping scan');
          setIsScanning(false);
          setScanError(null);
          return;
        }
        
        // Get the full device list but prioritize the connected device
        try {
          const found = await listBluetoothDevices();
          const devices = Array.isArray(found) ? found : [];
          
          // Put the connected device first, then sort others
          const connectedDevice = devices.find(d => d.address === lastUsedDevice);
          const otherDevices = devices.filter(d => d.address !== lastUsedDevice);
          
          const sortedOthers = otherDevices.sort((a, b) => {
            const aIsPrinter = isPrinterDevice(a) ? 1 : 0;
            const bIsPrinter = isPrinterDevice(b) ? 1 : 0;
            return bIsPrinter - aIsPrinter;
          });
          
          const finalDevices = connectedDevice ? [connectedDevice, ...sortedOthers] : sortedOthers;
          setBluetoothDevices(finalDevices);
          setIsScanning(false);
          setScanError(null);
          return;
        } catch (err) {
          console.log('[scanBluetoothDevices] Error getting device list, but will use connected device');
          // Still set the device list with just the connected device
          if (lastUsedDevice && connectedDeviceName) {
            setBluetoothDevices([{ 
              address: lastUsedDevice, 
              name: connectedDeviceName, 
              isPaired: true,
              isConnected: true 
            }]);
          }
          setIsScanning(false);
          return;
        }
      }

      // Check if Bluetooth is enabled
      const isEnabled = await checkBluetoothEnabled();
      if (!isEnabled) {
        setScanError('Bluetooth is disabled. Attempting to enable...');
        try {
          await enableBluetooth();
          await new Promise(resolve => setTimeout(resolve, 2000));
        } catch (enableErr: any) {
          // Don't clear the device list if we have a saved device, just show error
          if (!lastUsedDevice) {
          setBluetoothDevices([]);
          }
          setIsScanning(false);
          setScanError('Bluetooth is required for printing. Please enable Bluetooth manually and try again.');
          return;
        }
      }

      // Scan for devices
      setScanError('Scanning for Bluetooth printers...');
      const found = await listBluetoothDevices();
      const devices = Array.isArray(found) ? found : [];
      
      // Prioritize last used device if found, then printer devices
      const lastUsedDeviceObj = devices.find(d => d.address === lastUsedDevice);
      const otherDevices = devices.filter(d => d.address !== lastUsedDevice);
      
      const sortedOthers = otherDevices.sort((a, b) => {
        const aIsPrinter = isPrinterDevice(a) ? 1 : 0;
        const bIsPrinter = isPrinterDevice(b) ? 1 : 0;
        return bIsPrinter - aIsPrinter;
      });

      const finalDevices = lastUsedDeviceObj ? [lastUsedDeviceObj, ...sortedOthers] : sortedOthers;
      setBluetoothDevices(finalDevices);
      setIsScanning(false);
      setScanError(null);
      
      if (finalDevices.length === 0) {
        setScanError('No Bluetooth devices found. Make sure your printer is paired and in range.');
      } else {
        console.log(`Found ${finalDevices.length} Bluetooth device(s):`, finalDevices);
      }
    } catch (err: any) {
      console.error('Bluetooth scan error:', err);
      // Don't clear device list if we have a connected device
      if (!lastUsedDevice || !isConnected) {
      setBluetoothDevices([]);
      }
      setIsScanning(false);
      setScanError(err?.message || 'Failed to scan for Bluetooth devices. Please check permissions and try again.');
    }
  };

  // Additional Bluetooth functions for unified modal
  const scanBluetoothDevicesForModal = async () => {
    setIsScanningBluetooth(true);
    setBluetoothScanError('');
    
    try {
      // Check if Bluetooth is enabled
      const isEnabled = await checkBluetoothEnabled();
      if (!isEnabled) {
        setBluetoothScanError('Bluetooth is disabled. Attempting to enable...');
        try {
          await enableBluetooth();
        } catch (err) {
          setBluetoothScanError('Failed to enable Bluetooth. Please enable it manually and try again.');
          setIsScanningBluetooth(false);
          return;
        }
      }

      setBluetoothScanError('Scanning for Bluetooth printers...');
      const devices = await listBluetoothDevices();
      
      if (devices && devices.length > 0) {
        // Sort devices: printers first, then others
        const sortedDevices = devices.sort((a, b) => {
          const aIsPrinter = isPrinterDevice(a) ? 1 : 0;
          const bIsPrinter = isPrinterDevice(b) ? 1 : 0;
          return bIsPrinter - aIsPrinter;
        });
        
        setBluetoothDevices(sortedDevices);
        setBluetoothScanError('');
      } else {
        setBluetoothDevices([]);
        setBluetoothScanError('No Bluetooth devices found. Make sure your printer is paired and in range.');
      }
    } catch (err: any) {
      console.error('Bluetooth scan error:', err);
      setBluetoothDevices([]);
      setBluetoothScanError(err?.message || 'Failed to scan for Bluetooth devices. Please check permissions and try again.');
    } finally {
      setIsScanningBluetooth(false);
    }
  };

  const connectToBluetoothPrinter = async (deviceAddress: string) => {
    try {
      const device = bluetoothDevices.find(d => d.address === deviceAddress);
      await connectBluetoothDevice(deviceAddress, device?.name);
      const deviceName = device ? formatDeviceName(device) : 'Unknown Device';
      
      setIsBluetoothConnected(true);
      setConnectedBluetoothDeviceName(deviceName);
      setSelectedBluetoothDevice(deviceAddress);
      // Keep PrintOptionsModal in sync so it shows "Connected" / "Disconnect"
      setLastUsedDevice(deviceAddress);
      setConnectedDeviceName(deviceName);
      setIsConnected(true);
      sessionStorage.setItem('lastBluetoothPrinter', deviceAddress);
      sessionStorage.setItem('lastBluetoothPrinterName', deviceName);
      console.log('Bluetooth printer connected:', deviceAddress, deviceName);
    } catch (err: any) {
      console.error('Bluetooth connect error:', err);
      toast({ 
        title: 'Connection Failed', 
        description: err?.message || 'Failed to connect to printer', 
        variant: 'destructive' 
      });
    }
  };

  const disconnectFromBluetoothPrinter = async () => {
    try {
      await disconnectBluetoothDevice();
      setIsBluetoothConnected(false);
      setConnectedBluetoothDeviceName('');
      setSelectedBluetoothDevice('');
      console.log('Bluetooth printer disconnected');
    } catch (err: any) {
      console.error('Bluetooth disconnect error:', err);
    }
  };

  const printToBluetoothPrinter = async () => {
    if (!pendingInvoiceDetails) {
      toast({ title: 'Print Error', description: 'No invoice details available for printing', variant: 'destructive' });
      return;
    }

    try {
      setIsPrinting(true);
      
      // Use the same print logic as the existing handlePrintOptionSelect
      const printResult = await printKohineer({
        invoiceDetails: pendingInvoiceDetails,
        size: '3inch', // Default to 3-inch for Bluetooth
        companyImage: null,
        companyInfo: null,
        showSerialInPrint,
        serialPrintTemplate
      });

      if (printResult && typeof printResult === 'object' && 'success' in printResult && printResult.success) {
        toast({ 
          title: 'Print Success', 
          description: 'Invoice printed successfully to Bluetooth printer', 
          className: 'bg-green-600 text-white border-green-700' 
        });
        setShowBluetoothModal(false);
      } else {
        const message = printResult && typeof printResult === 'object' && 'message' in printResult ? String(printResult.message) : 'Print failed';
        throw new Error(message);
      }
    } catch (err: any) {
      console.error('Bluetooth print error:', err);
      toast({ 
        title: 'Print Error', 
        description: err?.message || 'Failed to print to Bluetooth printer', 
        variant: 'destructive' 
      });
    } finally {
      setIsPrinting(false);
    }
  };

  const handleCreateInvoice = async () => {
    if (balanceDue < 0) {
      toast({
        title: 'Invalid Balance',
        description: 'Cannot create invoice with negative balance. Please adjust payments.',
        variant: 'destructive'
      });
      return;
    }
    setIsCreatingInvoice(true);
    console.log('[handleCreateInvoice] Create Invoice clicked. printInvoice:', printInvoice);
    console.log('=== CREATE/UPDATE INVOICE DEBUG ===');
    console.log('Operation Type:', isEditMode ? 'UPDATE' : 'CREATE');
    console.log('Editing Invoice ID:', editingInvoiceId || 'N/A (New Invoice)');
    console.log('Is Edit Mode:', isEditMode);
    console.log('Editing Invoice ID from context:', editingInvoiceId);
    console.log('Editing Invoice ID type:', typeof editingInvoiceId);
    console.log('payments object:', payments);
    console.log('payments.payBefore:', payments.payBefore);
    console.log('payments.payBefore type:', typeof payments.payBefore);
    console.log('payments.payBefore length:', payments.payBefore?.length);
    console.log('=== END CREATE/UPDATE INVOICE DEBUG ===');
    
    // Validate customer phone when not Unknown: must be exactly 10 digits
    if (!isUnknown) {
      const phoneDigits = (customer.Phone || '').replace(/\D/g, '');
      if (phoneDigits.length !== 10) {
        toast({
          description: 'Enter correct phone number',
          variant: 'destructive'
        });
        setIsCreatingInvoice(false);
        return;
      }
    }

    // Validate credit note amount does not exceed invoice total
    const creditNoteAmt = parseFloat(String(payments.creditNoteAmount)) || 0;
    if (creditNoteAmt > invoiceTotal) {
      toast({
        title: 'Credit Note Amount Exceeds Invoice Total',
        description: `The selected credit note amount (₹${creditNoteAmt.toFixed(2)}) is greater than the invoice total (₹${invoiceTotal.toFixed(2)}). Please add more products or remove the credit note to continue.`,
        variant: 'destructive'
      });
      setIsCreatingInvoice(false);
      return;
    }
    
    // Calculate valid coupon amount for API call
    const validCouponAmount = getValidCouponAmount();
    const paymentsForApi = {
      ...payments,
      couponAmount: validCouponAmount.toString()
    };
    
    await createInvoiceApiCall({
      customer,
      products,
      payments: paymentsForApi,
      notes,
      repName,
      shipToForm,
      printInvoice,
      companyState: getCurrentCompanyState(),
      isUnknown,
      invoiceDate: customer.InvoiceDate || new Date().toISOString().slice(0, 10),
      payBefore: payments.payBefore,
      partners,
      companies, // Pass companies array
      invoiceForm,
      editingInvoiceId,
      onSuccess: async (result) => {
        console.log('[handleCreateInvoice] Invoice created, printInvoice:', printInvoice, 'result:', result);
        
        // Extract InvoiceID and InvoiceNumber from API response
        let invoiceId = null; // Column2 - for API calls
        let invoiceNumber = null; // Column1 - for display
        
        // Extract Column2 (Invoice ID for API calls)
        if (result?.data?.[0]?.Column2) {
          invoiceId = result.data[0].Column2;
        }
        else if (result?.data?.data?.[0]?.Column2) {
          invoiceId = result.data.data[0].Column2;
        }
        else if (result?.Column2) {
          invoiceId = result.Column2;
        }
        
        // Extract Column1 (Invoice Number for display)
        if (result?.data?.[0]?.Column1) {
          invoiceNumber = result.data[0].Column1;
        }
        else if (result?.data?.data?.[0]?.Column1) {
          invoiceNumber = result.data.data[0].Column1;
        }
        else if (result?.Column1) {
          invoiceNumber = result.Column1;
        }
        
        console.log('[handleCreateInvoice] Extracted invoiceId (Column2):', invoiceId);
        console.log('[handleCreateInvoice] Extracted invoiceNumber (Column1):', invoiceNumber);
        
        // In edit mode, if invoiceId extraction failed, use the editingInvoiceId as fallback
        if (!invoiceId && isEditMode && editingInvoiceId) {
          invoiceId = editingInvoiceId;
          console.log('[handleCreateInvoice] Using editingInvoiceId as fallback for invoiceId:', invoiceId);
        }
        
        // If we still don't have an invoice number, try to extract from message (message contains display number)
        if (!invoiceNumber && result?.message) {
          const messageMatch = result.message.match(/invoice\s*#?(\d+)/i);
          if (messageMatch) {
            invoiceNumber = messageMatch[1];
            console.log('[handleCreateInvoice] Extracted invoiceNumber from message:', invoiceNumber);
          }
        }
        
        // Immediately stop the loading state
        setIsCreatingInvoice(false);
        
        // Invalidate product search cache for fresh suggestions in next invoice (regardless of print)
        console.log('[Invoice Success] Invalidating product search cache...');
        queryClient.invalidateQueries({ queryKey: ['products-search'] });
        queryClient.removeQueries({ queryKey: ['products-search'] });
        queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'products-search' });
        console.log('[Invoice Success] Cache invalidation complete');
        
        // If printInvoice is checked, start print flow
        if (printInvoice && invoiceId) {
          // On native platforms with Bluetooth available, show Bluetooth modal
          const isNativeWithBluetooth = Capacitor.isNativePlatform() && typeof window !== 'undefined' && (window as any).bluetoothSerial;
          
          if (isNativeWithBluetooth) {
            // Show PrintOptionsModal for Android/iOS native with format selection
            setShowPrintOptions(true);
            // Only show loading if we don't have a connected device
            console.log('[handleCreateInvoice] Connection state check:', {
              lastUsedDevice,
              isConnected,
              bluetoothDevicesLength: bluetoothDevices.length
            });
            
            // Check if we have a saved device and it should be connected
            const savedDevice = sessionStorage.getItem('lastBluetoothPrinter');
            const savedDeviceName = sessionStorage.getItem('lastBluetoothPrinterName');
            
            if (savedDevice && savedDeviceName && isConnected) {
              console.log('[handleCreateInvoice] Device already connected, no loading needed');
              // Ensure the device is in the device list
              if (bluetoothDevices.length === 0) {
                setBluetoothDevices([{ 
                  address: savedDevice, 
                  name: savedDeviceName, 
                  isPaired: true,
                  isConnected: true 
                }]);
              }
            } else if (!lastUsedDevice || !isConnected || bluetoothDevices.length === 0) {
              console.log('[handleCreateInvoice] Setting loading state to true');
              setIsBluetoothUILoading(true);
            } else {
              console.log('[handleCreateInvoice] Device already connected, no loading needed');
            }
          } else {
            // Mobile web: direct print (no modal) - same as desktop, use Invoice Print Template V2 setting
            setIsMobileWebPrintLoading(true);
          }
          
          // Fetch invoice details and (for native) scan devices in background
          // For mobile web: fetch, generate PDF, open print dialog directly
          (async () => {
            try {
              console.log('[handleCreateInvoice] Fetching invoice full details for InvoiceID:', invoiceId);
              
              // Try to fetch with the extracted invoiceId first
              let details = await fetchInvoiceFullDetails(invoiceId);
              
              // If no data found and we used Column1, try Column2 as fallback
              if ((!details || !details.data || details.data.length === 0) && result?.data?.[0]?.Column2) {
                console.log('[handleCreateInvoice] No data with Column1, trying Column2:', result.data[0].Column2);
                details = await fetchInvoiceFullDetails(result.data[0].Column2);
              }
              
              let invData: { data?: any[]; InvSeqNo?: string; [key: string]: any } | null = null;
              if (details && details.data && details.data.length > 0) {
                invData = { ...details.data[0], data: details.data };
                setPendingInvoiceDetails(invData);
                setPendingInvoiceId(invoiceId);
                console.log('[handleCreateInvoice] Invoice details fetched successfully');
              } else {
                console.error('[handleCreateInvoice] No invoice data found in response:', details);
                toast({ title: 'Print Error', description: 'No invoice data found', variant: 'destructive' });
                setShowPrintOptions(false);
                setIsBluetoothUILoading(false);
                setIsMobileWebPrintLoading(false);
                return;
              }
              
              // Native Android: scan for Bluetooth devices
              if (Capacitor.isNativePlatform() && typeof window !== 'undefined' && window.bluetoothSerial) {
                if (!lastUsedDevice || !isConnected || bluetoothDevices.length === 0) {
                  console.log('[handleCreateInvoice] Scanning for Bluetooth devices...');
                  try {
                    await scanBluetoothDevicesForModal();
                  } finally {
                    setIsBluetoothUILoading(false);
                  }
                } else {
                  setIsBluetoothUILoading(false);
                }
              } else {
                // Mobile web: direct print - no modal, use Invoice Print Template V2 (0=2inch, 1=3inch, 2=A4)
                try {
                  const templateValue = salesInvoiceSettings.invoicePrintTemplateV2 || '0';
                  let blobUrl: string;

                  if (templateValue === '2') {
                    const apiEndpoint = `/api/GetInvoicePDF?InvoiceID=${invoiceId}&format=${encodeURIComponent('multiple page modern')}`;
                    const pdfBlob = await fetchPDF(apiEndpoint);
                    blobUrl = URL.createObjectURL(pdfBlob);
                  } else if (invData) {
                    const paperWidth = templateValue === '1' ? 80 : 58;
                    const doc = new jsPDF({ unit: 'mm', format: [paperWidth, 200] });
                    await drawInvoicePdf(doc, invData, paperWidth, 0, String(invoiceId), { showSerialInPrint, serialPrintTemplate });
                    const pdfBlob = doc.output('blob');
                    blobUrl = URL.createObjectURL(pdfBlob);
                  } else {
                    throw new Error('No invoice data for print');
                  }

                  const printWindow = window.open(blobUrl, '_blank');
                  if (!printWindow) {
                    toast({ title: 'Print Error', description: 'Please allow popups to print invoices.', variant: 'destructive' });
                    URL.revokeObjectURL(blobUrl);
                    return;
                  }
                  setTimeout(() => {
                    try {
                      if (!printWindow.closed) {
                        printWindow.focus();
                        printWindow.print();
                      }
                    } catch (e) {
                      console.error('Print trigger error:', e);
                    }
                  }, 1500);
                  const checkClosed = setInterval(() => {
                    if (printWindow.closed) {
                      clearInterval(checkClosed);
                      URL.revokeObjectURL(blobUrl);
                    }
                  }, 1000);
                  setTimeout(() => {
                    clearInterval(checkClosed);
                    try { URL.revokeObjectURL(blobUrl); } catch {}
                  }, 600000);

                  toast({ title: 'Print', description: 'Print dialog opened', className: 'bg-green-600 text-white border-green-700' });

                  // Form reset and edit mode handling (same as after print in modal)
                  if (!isEditMode) {
                    const currentCompany = customer.SelectCompany;
                    const currentLocation = customer.Location;
                    const currentPartner = customer.Partner;
                    const selectedCompany = companies.find(c => String(c.Id) === String(currentCompany));
                    const companyState = selectedCompany ? selectedCompany.State || '' : '';
                    const keepUnknown = salesInvoiceSettings?.unknownUserTicketed ?? false;
                    setCustomer({ ...initialCustomer, SelectCompany: currentCompany, Location: currentLocation, Partner: currentPartner, State: companyState, Name: keepUnknown ? 'UNKNOWN' : '', Company: keepUnknown ? 'UNKNOWN' : '' });
                    setProducts([]);
                    setPayments({ splitPaymentEnabled: false, selectedPaymentMethod: getDefaultPaymentMethod(), cashPayment: '', cardPayment: '', walletProvider: 'PhonePe', walletAmount: '', walletTxNo: '', chequeAmount: '', chequeNo: '', couponNo: '', couponAmount: '0', couponAmountLocked: false, creditNoteNo: '', creditNoteAmount: '0', pointsRedeem: '', eligiblePoints: '', payBefore: getPayBeforeDateFromInvoiceDaysSetting(getSetting('Invoice No of Day for Due')), });
                    setNotes('');
                    resetRepNameAndSearch();
                    setInvoiceForm({ QuoteNo: '', Mode: '', VehicleNo: '' });
                    setProductSearch('');
                    setProductTableSearch('');
                    setBarcodeSearchTerm('');
                    setProductNameSearchTerm('');
                    setShowProductDropdown(false);
                    setProductAddMessage(null);
                    setProductAddMessageType(null);
                    setPhoneSuggestions([]);
                    setShowPhoneSuggestions(false);
                    setPhoneLoading(false);
                    setPhoneError('');
                    if (refetchProductSearch) refetchProductSearch();
                    setShowMoreCustomerFields(false);
                    setIsUnknown(keepUnknown);
                    setCustomerTotalDue(null);
                    setShipToSame(true);
                    setShowNotes(false);
                    setShowShipToPopup(false);
                    setPrintInvoice(false);
                    setOpenSection('customer');
                    setHasAccessedPaymentSection(false);
                    setShipToForm({ Name: keepUnknown ? 'UNKNOWN' : '', Company: keepUnknown ? 'UNKNOWN' : '', Phone: '', Email: '', GST: '', Address: '', State: '' });
                    setLastShipToForm(null);
                    setLastShipToSameForm(null);
                    const invNumber = invData?.InvSeqNo || invData?.data?.[0]?.InvSeqNo || invoiceNumber;
                    toast({ title: 'Print Completed - Ready for Next Invoice', description: 'Customer section opened for next invoice. Enter new customer details.', className: 'bg-blue-600 text-white border-blue-700' });
                  } else {
                    const invNumber = invData?.InvSeqNo || invData?.data?.[0]?.InvSeqNo || invoiceNumber;
                    const successMessage = `Invoice ${invNumber ? `#${invNumber}` : ''} updated and printed successfully`;
                    onUpdateSuccess?.(successMessage);
                  }
                } catch (err) {
                  console.error('[handleCreateInvoice] Mobile web direct print error:', err);
                  toast({ title: 'Print Error', description: err instanceof Error ? err.message : 'Failed to print invoice', variant: 'destructive' });
                } finally {
                  setIsMobileWebPrintLoading(false);
                }
              }
              
              console.log('[handleCreateInvoice] Background tasks completed');
            } catch (err) {
              console.error('[handleCreateInvoice] Background print setup error:', err);
              toast({ title: 'Print Error', description: err.message || 'Failed to setup printing', variant: 'destructive' });
              setIsBluetoothUILoading(false);
              setIsMobileWebPrintLoading(false);
            }
          })();
          
          return; // Do not reset form or navigate here if printing
        }

        if (!isEditMode) {
          // Reset form completely for new invoice and auto-open customer section (only in create mode)
          const currentCompany = customer.SelectCompany;
          const currentLocation = customer.Location;
          const currentPartner = customer.Partner;
          
          // Get the company's default state instead of keeping old customer's state
          const selectedCompany = companies.find(c => String(c.Id) === String(currentCompany));
          const companyState = selectedCompany ? selectedCompany.State || '' : '';
          const keepUnknownCreate = salesInvoiceSettings?.unknownUserTicketed ?? false;
          
          // Clear ALL customer details for fresh entry (when Unknown is kept, set Name/Company to UNKNOWN)
          setCustomer({
            ...initialCustomer,
            SelectCompany: currentCompany, // Keep company settings
            Location: currentLocation, // Keep location
            Partner: currentPartner, // Keep partner
            State: companyState, // Reset to company's state, not old customer's state
            Name: keepUnknownCreate ? 'UNKNOWN' : '',
            Company: keepUnknownCreate ? 'UNKNOWN' : '',
          });
          setProducts([]);
          setPayments({
            splitPaymentEnabled: false,
            selectedPaymentMethod: getDefaultPaymentMethod(),
            cashPayment: '',
            cardPayment: '',
            walletProvider: 'PhonePe',
            walletAmount: '',
            walletTxNo: '',
            chequeAmount: '',
            chequeNo: '',
            couponNo: '',
            couponAmount: '0',
            couponAmountLocked: false,
            creditNoteNo: '',
            creditNoteAmount: '0',
            pointsRedeem: '',
            eligiblePoints: '',
            payBefore: getPayBeforeDateFromInvoiceDaysSetting(getSetting('Invoice No of Day for Due')),
          });
          setNotes('');
          resetRepNameAndSearch();
          
          // Reset invoice form fields (Tag1, Tag2, Tag3)
          setInvoiceForm({
            QuoteNo: '',
            Mode: '',
            VehicleNo: ''
          });
          
          // Clear product search and reset suggestions
          setProductSearch('');
          setProductTableSearch('');
          setBarcodeSearchTerm('');
          setProductNameSearchTerm('');
          setShowProductDropdown(false);
          setProductAddMessage(null);
          setProductAddMessageType(null);
          
          // Clear phone suggestions and reset phone-related states
          setPhoneSuggestions([]);
          setShowPhoneSuggestions(false);
          setPhoneLoading(false);
          setPhoneError('');
          
          // Force refetch of product suggestions for next search
          if (refetchProductSearch) {
            refetchProductSearch();
          }
          
          // Reset ALL UI states for completely fresh invoice (preserve Unknown when "Unknown User Ticketed" is enabled)
          setShowMoreCustomerFields(false); // Reset to basic view
          setIsUnknown(keepUnknownCreate); // Reset unknown customer state
          setCustomerTotalDue(null);
          setShipToSame(true); // Reset ship to same
          setShowNotes(false); // Hide notes section
          setShowShipToPopup(false); // Hide ship to popup
          setPrintInvoice(false); // Reset print checkbox
          setOpenSection('customer'); // ALWAYS open customer section for new entry
          setHasAccessedPaymentSection(false); // Reset payment section access state

          
          // Reset Ship To form and clear persisted values (but preserve in edit mode)
          if (!isEditMode || !editingInvoiceId) {
            setShipToForm({ Name: keepUnknownCreate ? 'UNKNOWN' : '', Company: keepUnknownCreate ? 'UNKNOWN' : '', Phone: '', Email: '', GST: '', Address: '', State: '' });
            setLastShipToForm(null);
            setLastShipToSameForm(null);
          }
        }
        
        if (isEditMode && onUpdateSuccess) {
          // In edit mode, call the success callback to return to invoice reports
          const successMessage = `Invoice ${invoiceNumber ? `#${invoiceNumber}` : ''} updated successfully!`;
          onUpdateSuccess(successMessage);
        } else {
          // In create mode, show success message
          toast({ 
            title: 'Invoice Created Successfully', 
            description: `Invoice ${invoiceNumber ? `#${invoiceNumber}` : ''} created. Customer section opened for next invoice. Enter new customer details.`, 
            className: 'bg-green-600 text-white border-green-700' 
          });
        }
        
        // Auto-focus on phone field for immediate entry (only in create mode)
        if (!isEditMode) {
          setTimeout(() => {
            const phoneInput = document.querySelector('input[placeholder*="Phone"]') as HTMLInputElement;
            if (phoneInput) {
              phoneInput.focus();
              phoneInput.select(); // Select any existing text for quick replacement
            }
          }, 200);
        }
      },
      onError: (error) => {
        console.error('[handleCreateInvoice] Error creating invoice:', error);
    setIsCreatingInvoice(false);
      },
    });
  };

  const handleRetryScan = async () => {
    // Disconnect current device if connected
    if (isConnected && lastUsedDevice) {
      try {
        await disconnectBluetoothDevice();
        setIsConnected(false);
        setConnectedDeviceName('');
        setLastUsedDevice('');
        // Clear session storage
        sessionStorage.removeItem('lastBluetoothPrinter');
        sessionStorage.removeItem('lastBluetoothPrinterName');
        console.log('[handleRetryScan] Disconnected from current device');
      } catch (err) {
        console.error('[handleRetryScan] Error disconnecting:', err);
      }
    }
    
    // Start fresh scan
    await scanBluetoothDevices();
  };





  const handleDownloadPDF = async () => {
    if (!pendingInvoiceDetails) {
      toast({ title: 'Download Error', description: 'No invoice details available for download', variant: 'destructive' });
      return;
    }
    
    try {
      const doc = new jsPDF({ unit: 'mm', format: [80, 200] });
      await drawInvoicePdf(doc, pendingInvoiceDetails, 80, 0, pendingInvoiceId, { showSerialInPrint, serialPrintTemplate });
      
      const fileName = `Invoice_${pendingInvoiceDetails.data?.[0]?.InvSeqNo || 'download'}`;
      const pdfDataUri = doc.output('datauristring');
      
      // Use the unified download utility
      await downloadPdf(
        pdfDataUri,
        fileName,
        () => {
          toast({ 
            title: 'Download Success', 
            description: Capacitor.isNativePlatform() 
              ? "PDF saved to device" 
              : "Invoice PDF downloaded successfully", 
            className: 'bg-green-600 text-white border-green-700' 
          });
        },
        (error) => {
          toast({ 
            title: 'Download Error', 
            description: error, 
            variant: 'destructive' 
          });
        }
      );
      
      // Close the print modal and clear pending invoice details after download
      setShowPrintOptions(false);
      setPendingInvoiceDetails(null);
      setPendingInvoiceId(null);
      
      // Handle form reset based on mode
      if (!isEditMode) {
        // Reset form for next invoice (create mode only)
        const currentCompany = customer.SelectCompany;
        const currentLocation = customer.Location;
        const currentPartner = customer.Partner;
        
        // Get the company's default state instead of keeping old customer's state
        const selectedCompany = companies.find(c => String(c.Id) === String(currentCompany));
        const companyState = selectedCompany ? selectedCompany.State || '' : '';
        const keepUnknownReset = salesInvoiceSettings?.unknownUserTicketed ?? false;
        
        // Clear ALL customer details for fresh entry (when Unknown is kept, set Name/Company to UNKNOWN)
        setCustomer({
          ...initialCustomer,
          SelectCompany: currentCompany, // Keep company settings
          Location: currentLocation, // Keep location
          Partner: currentPartner, // Keep partner
          State: companyState, // Reset to company's state, not old customer's state
          Name: keepUnknownReset ? 'UNKNOWN' : '',
          Company: keepUnknownReset ? 'UNKNOWN' : '',
        });
        
        // Clear products
        setProducts([]);
        
        // Reset payments (default from settings)
        setPayments({
          splitPaymentEnabled: false,
          selectedPaymentMethod: getDefaultPaymentMethod(),
          cashPayment: '',
          cardPayment: '',
          walletProvider: 'PhonePe',
          walletAmount: '',
          walletTxNo: '',
          chequeAmount: '',
          chequeNo: '',
          couponNo: '',
          couponAmount: '0',
          couponAmountLocked: false,
          creditNoteNo: '',
          creditNoteAmount: '0',
          pointsRedeem: '',
          eligiblePoints: '',
          payBefore: getPayBeforeDateFromInvoiceDaysSetting(getSetting('Invoice No of Day for Due')),
        });
        
        // Clear notes and rep name
        setNotes('');
        resetRepNameAndSearch();
        
        // Reset invoice form fields (Tag1, Tag2, Tag3)
        setInvoiceForm({
          QuoteNo: '',
          Mode: '',
          VehicleNo: ''
        });
        
        // Clear product search and reset suggestions
        setProductSearch('');
        setProductTableSearch('');
        setBarcodeSearchTerm('');
        setProductNameSearchTerm('');
        setShowProductDropdown(false);
        setProductAddMessage(null);
        setProductAddMessageType(null);
        
        // Clear phone suggestions and reset phone-related states
        setPhoneSuggestions([]);
        setShowPhoneSuggestions(false);
        setPhoneLoading(false);
        setPhoneError('');
        
        // Reset all UI states and open customer section (preserve Unknown when "Unknown User Ticketed" is enabled)
        setShowMoreCustomerFields(false);
        setIsUnknown(keepUnknownReset);
        setCustomerTotalDue(null);
        setShipToSame(true);
        setShowNotes(false);
        setShowShipToPopup(false);
        setPrintInvoice(false);
        setOpenSection('customer'); // Open customer section for new entry
        setHasAccessedPaymentSection(false); // Reset payment section access state

        
        // Reset Ship To form and clear persisted values
        setShipToForm({ Name: keepUnknownReset ? 'UNKNOWN' : '', Company: keepUnknownReset ? 'UNKNOWN' : '', Phone: '', Email: '', GST: '', Address: '', State: '' });
        setLastShipToForm(null);
        setLastShipToSameForm(null);
        
        // Force refetch of product suggestions for next search
        if (refetchProductSearch) {
          refetchProductSearch();
        }
        
        // Auto-focus on phone field for immediate entry
        setTimeout(() => {
          const phoneInput = document.querySelector('input[placeholder*="Phone"]') as HTMLInputElement;
          if (phoneInput) {
            phoneInput.focus();
            phoneInput.select(); // Select any existing text for quick replacement
          }
        }, 200);
      } else {
        // In edit mode, navigate back to reports after successful download
        setTimeout(() => {
          if (onUpdateSuccess) {
            // Get invoice number from pending invoice details
            const invoiceNumber = pendingInvoiceDetails?.data?.[0]?.InvSeqNo || pendingInvoiceDetails?.InvSeqNo;
            const successMessage = `Invoice ${invoiceNumber ? `#${invoiceNumber}` : ''} updated and downloaded successfully`;
            onUpdateSuccess(successMessage);
          }
        }, 1500); // Give user time to see the success message
      }
      
    } catch (error) {
      console.error('Download error:', error);
      toast({ 
        title: 'Download Error', 
        description: 'Failed to generate or download PDF', 
        variant: 'destructive' 
      });
    }
  };



  // Updated handlePrintOptionSelect to directly print without preview
  const handlePrintOptionSelect = async (options) => {
    console.log('[handlePrintOptionSelect] Print option selected:', options);
    if (!pendingInvoiceDetails) {
      setShowPrintOptions(false);
      return;
    }
    if (isPrinting) return; // Prevent duplicate print clicks (Android Bluetooth)

    if (options.format === 'kohineer') {
      setIsPrinting(true);
      let btStatus = { connected: false };
      let deviceAddress = options.deviceAddress || lastUsedDevice;

      // Only check Bluetooth on Android
      if (Capacitor.isNativePlatform() && typeof window !== 'undefined' && window.bluetoothSerial) {
        console.log('[handlePrintOptionSelect] Running on Android, checking Bluetooth...');
        if (!deviceAddress) {
          toast({ title: 'Bluetooth Error', description: 'Please select a Bluetooth printer', variant: 'destructive' });
          setIsPrinting(false);
          setPendingInvoiceDetails(null);
          setPendingInvoiceId(null);
          setShowPrintOptions(false);
          console.error('[handlePrintOptionSelect] No deviceAddress provided for Bluetooth printer.');
          return;
        }
        
        // If we think we're connected, test the connection first
        if (deviceAddress === lastUsedDevice && isConnected) {
          console.log('[handlePrintOptionSelect] Using existing connection to:', deviceAddress);
          btStatus = { connected: true };
        } else {
          // Attempt to connect
          try {
            const device = bluetoothDevices.find(d => d.address === deviceAddress);
            console.log('[handlePrintOptionSelect] Attempting to connect to Bluetooth printer:', deviceAddress);
            await connectBluetoothDevice(deviceAddress, device?.name);
            btStatus = { connected: true };
            const deviceName = device?.name ?? connectedDeviceName ?? 'Unknown Device';
            
            sessionStorage.setItem('lastBluetoothPrinter', deviceAddress);
            sessionStorage.setItem('lastBluetoothPrinterName', deviceName);
            setLastUsedDevice(deviceAddress);
            setConnectedDeviceName(deviceName);
            setIsConnected(true);
            
            console.log('[handlePrintOptionSelect] Bluetooth printer connected:', deviceAddress, deviceName);
          } catch (err) {
            console.error('[handlePrintOptionSelect] Bluetooth connect error:', err);
            // Only set known properties on btStatus
            btStatus = { connected: false };
            
            // Try one more time before giving up
            try {
              const retryDevice = bluetoothDevices.find(d => d.address === deviceAddress);
              console.log('[handlePrintOptionSelect] Retrying connection to:', deviceAddress);
              await connectBluetoothDevice(deviceAddress, retryDevice?.name);
              btStatus = { connected: true };
              setIsConnected(true);
              console.log('[handlePrintOptionSelect] Bluetooth printer connected on retry');
            } catch (retryErr) {
              setIsConnected(false);
              console.error('[handlePrintOptionSelect] Bluetooth retry failed:', retryErr);
            }
          }
        }

        // Do not proceed to print if Bluetooth connection failed
        if (!btStatus.connected) {
          toast({
            title: 'Bluetooth printer not connected',
            description: 'Could not connect to the printer. Ensure the printer is on, in range, and try again.',
            variant: 'destructive',
          });
          setIsPrinting(false);
          setPendingInvoiceDetails(null);
          setPendingInvoiceId(null);
          setShowPrintOptions(false);
          return;
        }
      } else {
        // Web/iOS - skip Bluetooth check and proceed with print for testing
        console.log('[handlePrintOptionSelect] Not on Android - proceeding with print for testing');
        btStatus = { connected: true };
      }

      // Fetch fresh company information for GST registration check
      let companyImage = '';
      let companyInfo = null;
      
      try {
        console.log('[handlePrintOptionSelect] Fetching fresh company data for GST check...');
        const baseUrl = getBaseUrl();
        const token = localStorage.getItem('token') || '';
        const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
        
        const response = await fetch(`${baseUrl}/api/Companies`, {
          headers: {
            'Authorization': authToken,
            'Content-Type': 'application/json',
          },
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch company data');
        }
        
        const data = await response.json();
        console.log('[handlePrintOptionSelect] Fresh company API response:', data);
        
        if (data.status === 0 && Array.isArray(data.data)) {
          const company = data.data.find(c => String(c.Id) === String(customer.SelectCompany));
          if (company) {
            companyImage = company.Image || '';
            companyInfo = company;
            console.log('[handlePrintOptionSelect] Found company for GST check:', companyInfo);
            console.log('[handlePrintOptionSelect] Company GST status:', companyInfo.GST === 1 ? 'GST-Registered' : 'Non-GST');
          } else {
            console.warn('[handlePrintOptionSelect] Company not found in API response');
          }
        } else {
          console.warn('[handlePrintOptionSelect] Invalid company API response');
        }
      } catch (error) {
        console.error('[handlePrintOptionSelect] Error fetching company data:', error);
        // Fallback to cached company data if API fails
        if (companies && companies.length > 0) {
          const company = companies.find(c => String(c.Id) === String(customer.SelectCompany));
          companyImage = company?.Image || '';
          companyInfo = company || null;
          console.log('[handlePrintOptionSelect] Using cached company data as fallback');
        }
      }

      // Print
      try {
        if (Capacitor.isNativePlatform()) {
          // Native platform (Android/iOS) - use Bluetooth printing
          if (options.size === '2inch') {
            console.log('[handlePrintOptionSelect] Calling printKohineer2mm with:', { invoiceDetails: pendingInvoiceDetails, companyImage });
            await printKohineer2mm({ invoiceDetails: pendingInvoiceDetails, companyImage });
            toast({ title: 'Print Success', description: 'Invoice sent to 2mm printer', className: 'bg-green-600 text-white border-green-700' });
            console.log('[handlePrintOptionSelect] PrintKohineer2mm success.');
          } else {
            console.log('[handlePrintOptionSelect] Calling printKohineer with:', { invoiceDetails: pendingInvoiceDetails, size: options.size, companyImage, companyInfo });
            await printKohineer({ invoiceDetails: pendingInvoiceDetails, size: options.size, companyImage, companyInfo, showSerialInPrint, serialPrintTemplate });
            toast({ title: 'Print Success', description: 'Invoice sent to 3mm printer', className: 'bg-green-600 text-white border-green-700' });
            console.log('[handlePrintOptionSelect] PrintKohineer success.');
          }
        } else {
          // Web platform (including mobile web) - open PDF in new tab and trigger browser print dialog
          // Matches Windows/Desktop behavior: no download, direct print preview
          console.log('[handlePrintOptionSelect] Web platform detected, opening print dialog directly');
          
          // Create PDF document
          const doc = new jsPDF({ unit: 'mm', format: options.size === '2inch' ? [80, 200] : [80, 200] });
          await drawInvoicePdf(doc, pendingInvoiceDetails, 80, 0, pendingInvoiceId, { showSerialInPrint, serialPrintTemplate });
          
          const pdfBlob = doc.output('blob');
          const blobUrl = URL.createObjectURL(pdfBlob);
          
          try {
            // Open PDF directly in new tab - browser's native viewer (same as Windows/Desktop)
            // No automatic download - triggers native print preview
            const printWindow = window.open(blobUrl, '_blank');
            
            if (!printWindow) {
              toast({
                title: "Error",
                description: "Popup blocked. Please allow popups and try again.",
                variant: "destructive",
              });
              URL.revokeObjectURL(blobUrl);
              return;
            }

            // Wait for PDF to load, then trigger print dialog (same timing as InvoiceReportTab/Desktop)
            setTimeout(() => {
              try {
                if (!printWindow.closed) {
                  printWindow.focus();
                  printWindow.print();
                }
              } catch (e) {
                console.error('Print trigger error:', e);
              }
            }, 1500);

            // Cleanup blob URL when print window closes
            const checkClosed = setInterval(() => {
              if (printWindow.closed) {
                clearInterval(checkClosed);
                URL.revokeObjectURL(blobUrl);
              }
            }, 1000);
            setTimeout(() => {
              clearInterval(checkClosed);
              try { URL.revokeObjectURL(blobUrl); } catch {}
            }, 600000);
            
            toast({ 
              title: 'Print Dialog Opening', 
              description: 'PDF will open with browser print preview.', 
              className: 'bg-blue-600 text-white border-blue-700' 
            });
            
          } catch (error) {
            console.error('Mobile web print error:', error);
            try { URL.revokeObjectURL(blobUrl); } catch {}
            toast({ 
              title: 'Print Error', 
              description: 'Failed to open print dialog. Please try again.', 
              variant: 'destructive' 
            });
          }
        }
      } catch (err) {
        console.error('[handlePrintOptionSelect] Print error:', err);
        toast({ title: 'Print Error', description: err.message || 'Failed to print invoice', variant: 'destructive' });
      }
      setIsPrinting(false);
      setPendingInvoiceDetails(null);
      setPendingInvoiceId(null);
      setShowPrintOptions(false); // Only close modal after print is done
      
      // Handle form reset based on mode
      if (!isEditMode) {
        // Reset form after print and auto-open customer section (create mode only)
        const currentCompany = customer.SelectCompany;
        const currentLocation = customer.Location;
        const currentPartner = customer.Partner;
        
        // Get the company's default state instead of keeping old customer's state
        const selectedCompany = companies.find(c => String(c.Id) === String(currentCompany));
        const companyState = selectedCompany ? selectedCompany.State || '' : '';
        const keepUnknownPrint2 = salesInvoiceSettings?.unknownUserTicketed ?? false;
        
        setCustomer({
          ...initialCustomer,
          SelectCompany: currentCompany, // Keep company settings
          Location: currentLocation, // Keep location
          Partner: currentPartner, // Keep partner
          State: companyState, // Reset to company's state, not old customer's state
          Name: keepUnknownPrint2 ? 'UNKNOWN' : '',
          Company: keepUnknownPrint2 ? 'UNKNOWN' : '',
        });
        setProducts([]);
        setPayments({
          splitPaymentEnabled: false,
          selectedPaymentMethod: getDefaultPaymentMethod(),
          cashPayment: '',
          cardPayment: '',
          walletProvider: 'PhonePe',
          walletAmount: '',
          walletTxNo: '',
          chequeAmount: '',
          chequeNo: '',
          couponNo: '',
          couponAmount: '0',
          couponAmountLocked: false,
          creditNoteNo: '',
          creditNoteAmount: '0',
          pointsRedeem: '',
          eligiblePoints: '',
          payBefore: getPayBeforeDateFromInvoiceDaysSetting(getSetting('Invoice No of Day for Due')),
        });
        setNotes('');
        resetRepNameAndSearch();
        
        // Clear product search and reset suggestions
        setProductSearch('');
        setProductTableSearch('');
        setBarcodeSearchTerm('');
        setProductNameSearchTerm('');
        
        // Clear phone suggestions and reset phone-related states
        setPhoneSuggestions([]);
        setShowPhoneSuggestions(false);
        setPhoneLoading(false);
        setPhoneError('');
        
        // Reset all UI states and open customer section (preserve Unknown when "Unknown User Ticketed" is enabled)
        setShowMoreCustomerFields(false);
        setIsUnknown(keepUnknownPrint2);
        setCustomerTotalDue(null);
        setShipToSame(true);
        setShowNotes(false);
        setShowShipToPopup(false);
        setPrintInvoice(false);
        setOpenSection('customer'); // Open customer section for new entry
        setHasAccessedPaymentSection(false); // Reset payment section access state

        
        // Reset Ship To form and clear persisted values
        setShipToForm({ Name: keepUnknownPrint2 ? 'UNKNOWN' : '', Company: keepUnknownPrint2 ? 'UNKNOWN' : '', Phone: '', Email: '', GST: '', Address: '', State: '' });
        setLastShipToForm(null);
        setLastShipToSameForm(null);
        
        toast({ 
          title: 'Print Completed - Ready for Next Invoice', 
          description: 'Customer section opened for next invoice. Enter new customer details.', 
          className: 'bg-blue-600 text-white border-blue-700' 
        });
        
        // Auto-focus on phone field for immediate entry
        setTimeout(() => {
          const phoneInput = document.querySelector('input[placeholder*="Phone"]') as HTMLInputElement;
          if (phoneInput) {
            phoneInput.focus();
            phoneInput.select(); // Select any existing text for quick replacement
          }
        }, 200);
              } else {
          // In edit mode, navigate back to reports after successful print
          setTimeout(() => {
            if (onUpdateSuccess) {
              // Get invoice number from pending invoice details
              const invoiceNumber = pendingInvoiceDetails?.data?.[0]?.InvSeqNo || pendingInvoiceDetails?.InvSeqNo;
              const successMessage = `Invoice ${invoiceNumber ? `#${invoiceNumber}` : ''} updated and printed successfully`;
              onUpdateSuccess(successMessage);
            }
          }, 1500); // Give user time to see the success message
        }
    }
  };

  // Handler for Cancel button
  const handleCancel = () => {
    // Navigate to the Transactions tab
    window.dispatchEvent(new CustomEvent('setCurrentPage', { detail: 'transactions' }));
  };

  // Add state to persist shipping form values
  const [lastShipToForm, setLastShipToForm] = useState(null);
  const [lastCustomerShipToForm, setLastCustomerShipToForm] = useState(null);
  const [originalApiShippingData, setOriginalApiShippingData] = useState(null);

  // When Ship to Same is toggled, preserve the current form values
  useEffect(() => {
    if (isEditMode && editingInvoiceId) {
      console.log('SalesInvoiceMobile: Ship to Same checkbox changed in edit mode');
      console.log('SalesInvoiceMobile: shipToSame value:', shipToSame);
      console.log('SalesInvoiceMobile: Current shipToForm before change:', shipToForm);
      
      // In edit mode, handle checkbox state properly
      if (shipToSame) {
        // When "Ship to Same" is checked in edit mode, show customer data
        console.log('Edit mode: Ship to Same checked - showing customer data');
        setShipToForm({
          Name: customer.Name || '',
          Company: customer.Company || '',
          Phone: customer.Phone || '',
          Email: customer.Email || '',
          GST: customer.GST || '',
          Address: customer.Address || '',
          State: customer.State || '',
        });
      } else {
        // When "Ship to Same" is unchecked in edit mode, show API shipping data
        console.log('Edit mode: Ship to Same unchecked - showing API shipping data');
        if (originalApiShippingData) {
          console.log('SalesInvoiceMobile: Restoring original API shipping data:', originalApiShippingData);
          setShipToForm(originalApiShippingData);
        } else {
          console.log('SalesInvoiceMobile: No original API shipping data available');
        }
      }
      return;
    }
    
    // Only handle Ship to Same logic in create mode
    if (shipToSame) {
      // Save current shipping form values before switching to "Ship to Same"
      setLastCustomerShipToForm(shipToForm);
      
      // Set shipping form to match customer details (only in create mode)
      setShipToForm({
        Name: customer.Name || '',
        Company: customer.Company || '',
        Phone: customer.Phone || '',
        Email: customer.Email || '',
        GST: customer.GST || '',
        Address: customer.Address || '',
        State: customer.State || '',
      });
    } else {
      // When "Ship to Same" is unchecked, populate with customer data as defaults
      // but preserve any existing custom shipping data if it exists
      const hasExistingShippingData = Boolean(shipToForm.Name || shipToForm.Company || shipToForm.Phone || 
                                     shipToForm.Email || shipToForm.GST || shipToForm.Address || shipToForm.State);
      
      if (!hasExistingShippingData) {
        // If no existing shipping data, populate with customer data as defaults
        setShipToForm({
          Name: customer.Name || '',
          Company: customer.Company || '',
          Phone: customer.Phone || '',
          Email: customer.Email || '',
          GST: customer.GST || '',
          Address: customer.Address || '',
          State: customer.State || '',
        });
      }
      // If there's existing shipping data, keep it unchanged
    }
  }, [shipToSame, isEditMode, editingInvoiceId, customer]);

  // Update shipping form when customer data changes and "Ship to Same" is checked
  useEffect(() => {
    if (isEditMode && editingInvoiceId) {
      // In edit mode, NEVER update shipping form based on customer changes
      // The API data should always be preserved
      return;
    }
    
    if (shipToSame) {
      // Only update shipping form if "Ship to Same" is checked and we're not in edit mode
      setShipToForm({
        Name: customer.Name || '',
        Company: customer.Company || '',
        Phone: customer.Phone || '',
        Email: customer.Email || '',
        GST: customer.GST || '',
        Address: customer.Address || '',
        State: customer.State || '',
      });
    }
  }, [shipToSame, customer, setShipToForm, isEditMode, editingInvoiceId]);

  // Initialize shipping form when popup opens
  useEffect(() => {
    if (showShipToPopup) {
      if (isEditMode && editingInvoiceId) {
        // In edit mode, handle based on checkbox state
        if (shipToSame) {
          // When "Ship to Same" is checked, show customer data
          console.log('Edit mode: Ship to Same checked - showing customer data in popup');
          setShipToForm({
            Name: customer.Name || '',
            Company: customer.Company || '',
            Phone: customer.Phone || '',
            Email: customer.Email || '',
            GST: customer.GST || '',
            Address: customer.Address || '',
            State: customer.State || '',
          });
        } else {
          // When "Ship to Same" is unchecked, show API shipping data
          console.log('Edit mode: Ship to Same unchecked - showing API shipping data in popup');
          if (originalApiShippingData) {
            console.log('SalesInvoiceMobile: Restoring original API shipping data in popup:', originalApiShippingData);
            setShipToForm(originalApiShippingData);
          } else {
            console.log('SalesInvoiceMobile: No original API shipping data available for popup');
            console.log('Current shipToForm (API data):', shipToForm);
            // Try to fetch the API data again if not available
            if (editingInvoiceId) {
              console.log('SalesInvoiceMobile: Attempting to fetch API data again...');
              fetchAndLogInvoiceDetails(editingInvoiceId, false); // Don't populate payments for this call
            }
          }
        }
      } else {
        // In create mode, check if we have existing shipping data
        const hasExistingShippingData = Boolean(shipToForm.Name || shipToForm.Company || shipToForm.Phone || 
                                       shipToForm.Email || shipToForm.GST || shipToForm.Address || shipToForm.State);
        
        if (!hasExistingShippingData) {
          // If no existing shipping data, initialize with customer values
          let companyState = '';
          if (customer.SelectCompany && companies.length > 0) {
            const company = companies.find(c => String(c.Id) === String(customer.SelectCompany));
            companyState = company?.State || '';
          }
          setShipToForm({
            Name: customer.Name || '',
            Company: customer.Company || '',
            Phone: customer.Phone || '',
            Email: customer.Email || '',
            GST: customer.GST || '',
            Address: customer.Address || '',
            State: customer.State || ''
          });
        }
        // If there's existing shipping data, keep it unchanged
      }
    }
    // eslint-disable-next-line
  }, [showShipToPopup, isEditMode, editingInvoiceId]);

  // Update effect for company state change in popup (only if user hasn't changed state)
  useEffect(() => {
    if (showShipToPopup && !lastShipToForm) {
      setShipToForm(f => ({ ...f, State: customer.State || '' }));
    }
    // eslint-disable-next-line
  }, [customer.SelectCompany, companies, showShipToPopup]);

  // Add state to persist last Ship To form values for Ship to Same popup
  const [lastShipToSameForm, setLastShipToSameForm] = useState(null);

  // Reset persisted Ship To form values on component mount
  useEffect(() => {
    setLastShipToForm(null);
    setLastShipToSameForm(null);
  }, []);

  // Preserve shipping data in edit mode when component loads
  useEffect(() => {
    if (isEditMode && editingInvoiceId) {
      // Check if we have existing shipping data that should be preserved
      const hasExistingShippingData = Boolean(shipToForm.Name || shipToForm.Company || shipToForm.Phone || 
                                     shipToForm.Email || shipToForm.GST || shipToForm.Address || shipToForm.State);
      
      console.log('SalesInvoiceMobile: Entered edit mode for invoice:', editingInvoiceId);
      console.log('SalesInvoiceMobile: Initial shipToForm state:', shipToForm);
      console.log('SalesInvoiceMobile: Has existing shipping data:', hasExistingShippingData);
      
      if (hasExistingShippingData) {
        console.log('SalesInvoiceMobile: Shipping data found in context:', shipToForm);
        // The shipping data is already loaded in the context, so we don't need to do anything
        // Just ensure it's not overwritten by other effects
      } else {
        console.log('SalesInvoiceMobile: No shipping data found in context');
      }
    }
  }, [isEditMode, editingInvoiceId]);

  // Debug: Track when shipToForm changes
  useEffect(() => {
    if (isEditMode && editingInvoiceId) {
      console.log('SalesInvoiceMobile: shipToForm changed in edit mode:', shipToForm);
      
      // Check if API shipping data has been loaded
      const hasApiShippingData = Boolean(shipToForm.Name || shipToForm.Company || shipToForm.Phone || 
                                shipToForm.Email || shipToForm.GST || shipToForm.Address || shipToForm.State);
      
      if (hasApiShippingData && !originalApiShippingData) {
        console.log('SalesInvoiceMobile: API shipping data detected, saving to originalApiShippingData:', shipToForm);
        setOriginalApiShippingData({ ...shipToForm });
      }
    }
  }, [shipToForm, isEditMode, editingInvoiceId, originalApiShippingData]);

  // Helper to get current company state
  const getCurrentCompanyState = () => {
    if (customer.SelectCompany && companies.length > 0) {
      const company = companies.find(c => String(c.Id) === String(customer.SelectCompany));
      return company?.State || '';
    }
    return '';
  };

  // When Ship to Same popup is opened, initialize form
  useEffect(() => {
    if (showShipToPopup) {
      // Check if we have existing shipping data (from edit mode or previous session)
      const hasExistingShippingData = Boolean(shipToForm.Name || shipToForm.Company || shipToForm.Phone || 
                                     shipToForm.Email || shipToForm.GST || shipToForm.Address || shipToForm.State);
      
      console.log('Popup opening - Shipping data check:', {
        showShipToPopup,
        hasExistingShippingData,
        shipToForm,
        isEditMode,
        editingInvoiceId
      });
      
      if (hasExistingShippingData) {
        // Use existing shipping data - ensure it's properly set in the form
        console.log('Popup opened with existing shipping data:', shipToForm);
        // The data is already in shipToForm, so we don't need to set it again
        // Just ensure the form fields will display the existing values
      } else if (lastShipToSameForm) {
        console.log('Using lastShipToSameForm data');
        setShipToForm(lastShipToSameForm);
      } else {
        console.log('Initializing with customer data and company state');
        const companyState = getCurrentCompanyState();
        setShipToForm({ 
          Name: customer.Name || '', 
          Company: customer.Company || '', 
          Phone: customer.Phone || '', 
          Email: customer.Email || '', 
          GST: customer.GST || '', 
          Address: customer.Address || '', 
          State: customer.State || '' 
        });
      }
    }
    // eslint-disable-next-line
  }, [showShipToPopup]); // Remove dependencies that cause infinite loops

  // If company changes while popup is open and user hasn't changed State, update State field
  useEffect(() => {
    if (showShipToPopup && !lastShipToSameForm) {
      setShipToForm(f => ({ ...f, State: customer.State || '' }));
    }
    // eslint-disable-next-line
  }, [customer.SelectCompany, companies, showShipToPopup]);

  const [showScanner, setShowScanner] = useState(false);
  const [scannerError, setScannerError] = useState('');
  const [scanTriggered, setScanTriggered] = useState(false); // <-- add this
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const scannerRunningRef = useRef(false);
  const isMobile = /Android|iPhone|iPad|iPod/i.test(window.navigator.userAgent);

  const handleOpenScanner = async () => {
    setScannerError('');
    
    // Always use HTML5 scanner for better compatibility on Android WebView
    console.log('Opening barcode scanner...');
    setShowScanner(true);
  };

  // Add this useEffect to auto-add product after scan
  useEffect(() => {
    if (scanTriggered && productSearch && productSearchResults.length > 0) {
      const found = productSearchResults.find(p => p.Barcode === productSearch);
      if (found) {
        // Check if product is available based on isTraceable logic
        const available = Number(found.Available) || 0;
        const isTraceable = found.isTraceable || false;
        
        // If available is 0 and isTraceable is true, show "Out of Stock"
        if (available <= 0 && isTraceable) {
          setProductAddMessage('Out of Stock');
          setProductAddMessageType('error');
          setProductSearch('');
          setShowProductDropdown(false);
        } else if (available > 0 || !isTraceable) {
          // If isTraceable is false, allow adding even if available is 0
          // If available > 0, allow adding regardless of isTraceable
          addProductToTable({ ...found, Qty: 1 });
          setProductSearch('');
          setShowProductDropdown(false);
        } else {
          setProductAddMessage('Out of Stock');
          setProductAddMessageType('error');
          setProductSearch('');
          setShowProductDropdown(false);
        }
      }
      setScanTriggered(false);
    }
  }, [scanTriggered, productSearch, productSearchResults]);

  useEffect(() => {
    if (showScanner && scannerRef.current) {
      const html5QrCode = new Html5Qrcode(scannerRef.current.id);
      html5QrCodeRef.current = html5QrCode;
      scannerRunningRef.current = false;

      // Get available cameras and start with the best option
      Html5Qrcode.getCameras()
        .then((devices) => {
          console.log('Available cameras:', devices);
          
          if (devices && devices.length > 0) {
            // Prefer back camera (environment) if available
            let cameraId = devices[0].id;
            const backCamera = devices.find(device => 
              device.label.toLowerCase().includes('back') || 
              device.label.toLowerCase().includes('environment')
            );
            if (backCamera) {
              cameraId = backCamera.id;
            }

            console.log('Using camera:', cameraId);

            // Start scanning with selected camera
            return html5QrCode.start(
              cameraId,
              {
                fps: 10,
                qrbox: { width: 250, height: 250 },
                aspectRatio: 1.0
              },
              (decodedText) => {
                console.log('Barcode scanned:', decodedText);
                setProductSearch(decodedText);
                setScanTriggered(true);
                setShowScanner(false);
                setScannerError('');
                if (scannerRunningRef.current) {
                  html5QrCode
                    .stop()
                    .then(() => html5QrCode.clear())
                    .catch(() => {});
                  scannerRunningRef.current = false;
                }
              },
              (error) => {
                // Ignore scanning errors - they're very frequent
              }
            );
          } else {
            // Fallback to default camera constraints
            return html5QrCode.start(
              { facingMode: { ideal: 'environment' } },
              {
                fps: 10,
                qrbox: { width: 250, height: 250 },
                aspectRatio: 1.0
              },
              (decodedText) => {
                console.log('Barcode scanned:', decodedText);
                setProductSearch(decodedText);
                setScanTriggered(true);
                setShowScanner(false);
                setScannerError('');
                if (scannerRunningRef.current) {
                  html5QrCode
                    .stop()
                    .then(() => html5QrCode.clear())
                    .catch(() => {});
                  scannerRunningRef.current = false;
                }
              },
              (error) => {
                // Ignore scanning errors
              }
            );
          }
        })
        .then(() => {
          scannerRunningRef.current = true;
          console.log('QR Code scanner started successfully');
        })
        .catch((err) => {
          console.error('Camera access error:', err);
          let errorMessage = 'Camera access failed';
          if (err.name === 'NotAllowedError') {
            errorMessage = 'Camera permission denied. Please allow camera access and try again.';
          } else if (err.name === 'NotFoundError') {
            errorMessage = 'No camera found. Please ensure your device has a camera and try again.';
          } else if (err.name === 'NotSupportedError') {
            errorMessage = 'Camera not supported by your browser. Please try a different browser.';
          } else if (err.name === 'NotReadableError') {
            errorMessage = 'Camera is already in use by another application.';
          } else {
            errorMessage = 'Camera access failed: ' + (err?.message || err);
          }
          setScannerError(errorMessage);
        });
    }

    return () => {
      if (html5QrCodeRef.current && scannerRunningRef.current) {
        html5QrCodeRef.current
          .stop()
          .then(() => {
            html5QrCodeRef.current?.clear();
            console.log('QR Code scanner stopped');
          })
          .catch(() => {});
        scannerRunningRef.current = false;
        html5QrCodeRef.current = null;
      }
    };
  }, [showScanner]);

  const [showPrintOptions, setShowPrintOptions] = useState(false);
  const [pendingInvoiceDetails, setPendingInvoiceDetails] = useState(null);
  const [pendingInvoiceId, setPendingInvoiceId] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isMobileWebPrintLoading, setIsMobileWebPrintLoading] = useState(false);







  useEffect(() => {
    // Request Bluetooth and Location permissions at runtime (Android)
    const requestPermissions = async () => {
      if (typeof window !== 'undefined' && window.cordova && window.cordova.plugins && window.cordova.plugins.permissions) {
        const permissions = window.cordova.plugins.permissions;
        
        console.log('Requesting runtime permissions for Bluetooth functionality...');
        
        // Request location permissions first (required for Bluetooth device discovery on Android 6+)
        try {
          await new Promise<void>((resolve) => {
            permissions.requestPermission(permissions.ACCESS_FINE_LOCATION, function(status) {
              if (status.hasPermission) {
                console.log('Fine location permission granted');
              } else {
                console.warn('Fine location permission denied. Bluetooth device discovery may not work.');
              }
              resolve();
            }, () => {
              console.warn('Failed to request fine location permission');
              resolve();
            });
          });

          await new Promise<void>((resolve) => {
            permissions.requestPermission(permissions.ACCESS_COARSE_LOCATION, function(status) {
              if (status.hasPermission) {
                console.log('Coarse location permission granted');
              } else {
                console.warn('Coarse location permission denied.');
              }
              resolve();
            }, () => {
              console.warn('Failed to request coarse location permission');
              resolve();
            });
          });
        } catch (err) {
          console.warn('Error requesting location permissions:', err);
        }

        // Request camera permission for barcode scanning
        if (permissions.CAMERA) {
          try {
            await new Promise<void>((resolve) => {
              permissions.requestPermission(permissions.CAMERA, function(status) {
                if (status.hasPermission) {
                  console.log('Camera permission granted');
                } else {
                  console.warn('Camera permission denied. Barcode scanning may not work.');
                }
                resolve();
              }, () => {
                console.warn('Failed to request camera permission');
                resolve();
              });
            });
          } catch (err) {
            console.warn('Error requesting camera permission:', err);
          }
        }

        // For Android 12+ (API 31+), request new Bluetooth permissions
        if (permissions.BLUETOOTH_CONNECT) {
          try {
            await new Promise<void>((resolve) => {
              permissions.requestPermission(permissions.BLUETOOTH_CONNECT, function(status) {
                if (status.hasPermission) {
                  console.log('Bluetooth Connect permission granted');
                } else {
                  console.warn('Bluetooth Connect permission denied. Printer connection may not work.');
                }
                resolve();
              }, () => {
                console.warn('Failed to request Bluetooth Connect permission');
                resolve();
              });
            });
          } catch (err) {
            console.warn('Error requesting Bluetooth Connect permission:', err);
          }
        }

        if (permissions.BLUETOOTH_SCAN) {
          try {
            await new Promise<void>((resolve) => {
              permissions.requestPermission(permissions.BLUETOOTH_SCAN, function(status) {
                if (status.hasPermission) {
                  console.log('Bluetooth Scan permission granted');
                } else {
                  console.warn('Bluetooth Scan permission denied. Device discovery may not work.');
                }
                resolve();
              }, () => {
                console.warn('Failed to request Bluetooth Scan permission');
                resolve();
              });
            });
          } catch (err) {
            console.warn('Error requesting Bluetooth Scan permission:', err);
          }
        }

        console.log('Bluetooth permission requests completed');
      } else {
        console.log('Cordova permissions plugin not available (web environment or missing plugin)');
      }
    };

    // Call permission request function
    requestPermissions();
  }, []);

  useEffect(() => {
    if (isUnknown) {
      goToSection('product');
    }
  }, [isUnknown]);

  // Set default partner and location when data is loaded (only once)
  useEffect(() => {
    if (partners.length > 0 && locations.length > 0) {
      // If partner is not set but partners are available, set to first partner
      if (!customer.Partner && partners.length > 0) {
        setCustomer(f => ({ ...f, Partner: partners[0].Id.toString() }));
      }
      
      // If location is not set but locations are available, set to first location
      if (!customer.Location && locations.length > 0) {
        setCustomer(f => ({ ...f, Location: locations[0].ID }));
      }
    }
  }, [partners.length, locations.length]); // Only depend on array lengths, not the entire arrays

  // Debug products array in edit mode - only run once when products are loaded
  useEffect(() => {
    if (isEditMode && editingInvoiceId && products.length > 0) {
      console.log('Edit mode - Products loaded successfully:', products.length);
    }
  }, [isEditMode, editingInvoiceId, products.length]); // Only depend on products.length

  // Function to handle points validation when split payment is unchecked
  const handlePointsValidation = (value: string) => {
    if (value && parseFloat(value) > 0 && eligiblePoints > 0) {
      const enteredPoints = parseFloat(value) || 0;
      if (enteredPoints > eligiblePoints) {
        setPointsError('Points can\'t be more than eligible points value');
        setPayments(p => ({ ...p, pointsRedeem: '0' }));
        return true; // Validation failed
      }
    }
    return false; // Validation passed
  };



  return (
    <div className="flex flex-col w-full min-h-screen bg-gray-50 android-mobile-container keyboard-aware-container">
      {/* Mobile App Bar */}
      <AppBar 
        title="Sales Invoice" 
        onBack={() => {
          if (isEditMode && onBackToReports) {
            onBackToReports();
          } else {
            handleCancel();
          }
        }}
        showBackButton={true}
      />
      
      {/* Main content with top padding for app bar */}
      <div className="flex flex-col w-full min-h-screen bg-gray-50 px-2 sm:px-3 md:px-4 pb-2 sm:pb-3 md:pb-4 pt-14 sm:pt-14 md:pt-14">
      {/* Invoice Settings Section */}
      <div className="mb-2 sm:mb-3">
        <button
          className="w-full text-left py-3 sm:py-4 px-4 sm:px-5 rounded-lg font-bold text-base sm:text-lg md:text-xl bg-white border-b-2 border-cyan-300 hover:bg-gray-50 min-h-[48px] sm:min-h-[56px] transition-all duration-200 active:scale-[0.98]"
          onClick={() => setShowInvoiceSettings(!showInvoiceSettings)}
        >
          Invoice Settings {showInvoiceSettings ? '▼' : '▶'}
        </button>
        {showInvoiceSettings && (
          <div className="p-3 sm:p-4 md:p-5 bg-white rounded-b-lg shadow-md flex flex-col gap-3 sm:gap-4 animate-fade-in">
            <div className="font-bold text-base sm:text-lg md:text-xl mb-2 sm:mb-3 text-cyan-700">Invoice Settings</div>
            {/* Rep Name Field */}
            <div className="mb-2 sm:mb-3 relative rep-name-dropdown">
              <div className="flex items-center justify-between mb-1 sm:mb-2">
                <label className="block text-sm sm:text-base font-medium text-gray-700">Rep Name</label>
                <button type="button" className="text-xs sm:text-sm text-cyan-700 hover:underline" onClick={() => setShowRepManager(true)}>New</button>
              </div>
              <input
                className="w-full h-11 sm:h-12 md:h-14 px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md bg-white placeholder-gray-400 text-sm sm:text-base transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-cyan-400"
                value={repNameSearchTerm}
                onChange={e => {
                  setRepNameSearchTerm(e.target.value);
                  setRepName(e.target.value);
                  setShowRepNameDropdown(true);
                }}
                onFocus={() => setShowRepNameDropdown(true)}
                placeholder="Rep Name"
                type="text"
              />
              {showRepNameDropdown && (
                <div className="absolute z-50 w-full max-h-48 overflow-y-auto bg-white border border-gray-300 rounded-md shadow-lg mt-1">
                  {representativesLoading ? (
                    <div className="px-3 py-2 text-sm text-gray-500">Loading...</div>
                  ) : representativesError ? (
                    <div className="px-3 py-2 text-sm text-red-500">Error loading representatives</div>
                  ) : filteredRepresentatives.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-500">No representatives found</div>
                  ) : (
                    filteredRepresentatives.map((rep, index) => (
                      <div
                        key={index}
                        className="px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                        onClick={() => {
                          setRepName(rep.Representative);
                          setRepNameSearchTerm(rep.Representative);
                          setShowRepNameDropdown(false);
                        }}
                      >
                        {rep.Representative}
                      </div>
                    ))
                  )}
                </div>
              )}
              <RepresentativeManager
                open={showRepManager}
                onOpenChange={setShowRepManager}
                onAnyChange={() => {
                  try {
                    if (queryClient) {
                      queryClient.invalidateQueries({ queryKey: ['representatives'] });
                    }
                  } catch {}
                }}
              />
            </div>
            {/* Invoice Date Field */}
            <div className="mb-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Invoice Date</label>
              <input
                type="date"
                className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md bg-white placeholder-gray-400"
                value={customer.InvoiceDate || new Date().toISOString().slice(0, 10)}
                onChange={e => setCustomer(f => ({ ...f, InvoiceDate: e.target.value }))}
              />
            </div>
            {/* Location Dropdown */}
            <div className="mb-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Location</label>
              <select
                className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
                value={customer.Location}
                onChange={e => handleLocationChange(e.target.value)}
                disabled={locationsLoading}
              >
                {locationsLoading && <option value="">Loading...</option>}
                {locations.map((loc) => (
                  <option key={loc.ID} value={loc.ID}>{loc.Name}</option>
                ))}
              </select>
            </div>
            {/* Companies Dropdown */}
            <div className="mb-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Company</label>
              <select
                className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
                value={customer.SelectCompany}
                onChange={e => handleCompanyChange(e.target.value)}
                disabled={companiesLoading}
              >
                {companiesLoading && <option value="">Loading...</option>}
                {companies.map((company) => (
                  <option key={company.Id} value={company.Id}>{company.Name}</option>
                ))}
              </select>
            </div>
            {/* Partners Dropdown - Conditionally shown based on settings */}
            {salesInvoiceSettings.invoicePartnerRequired && (
              <div className="mb-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Partners</label>
                <select
                  className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
                  value={customer.Partner}
                  onChange={e => handlePartnerChange(e.target.value)}
                  disabled={partnersLoading}
                >
                  {partnersError && <option value="">{partnersError}</option>}
                  {partners.map((partner) => (
                    <option key={partner.Id} value={partner.Id}>{partner.PartnerName}</option>
                  ))}
                </select>
              </div>
            )}
            {/* eComm # Field */}
            <div className="mb-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">eComm #</label>
              <input
                type="text"
                className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
                value={customer.eCommOrderID}
                onChange={e => {
                  // Only allow alphanumeric characters
                  const value = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                  setCustomer(f => ({ ...f, eCommOrderID: value }));
                }}
                placeholder="Enter eComm #"
                maxLength={50}
              />
            </div>
            {/* Quote No Field (Tag1) */}
            <div className="mb-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Quote No</label>
              <input
                type="text"
                className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
                value={invoiceForm.QuoteNo}
                onChange={e => setInvoiceForm(f => ({ ...f, QuoteNo: e.target.value }))}
                placeholder="Enter Quote No"
              />
            </div>
            {/* Tag 1 - Dynamic field based on settings */}
            {salesInvoiceSettings.tag1Name && (
              <div className="mb-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">{salesInvoiceSettings.tag1Name}</label>
                <input
                  type="text"
                  className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
                  value={invoiceForm.Mode}
                  onChange={e => setInvoiceForm(f => ({ ...f, Mode: e.target.value }))}
                  placeholder={salesInvoiceSettings.tag1Name}
                />
              </div>
            )}
            {/* Tag 2 - Dynamic field based on settings */}
            {salesInvoiceSettings.tag2Name && (
              <div className="mb-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">{salesInvoiceSettings.tag2Name}</label>
                <input
                  type="text"
                  className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
                  value={invoiceForm.VehicleNo}
                  onChange={e => setInvoiceForm(f => ({ ...f, VehicleNo: e.target.value }))}
                  placeholder={salesInvoiceSettings.tag2Name}
                />
              </div>
            )}
            {/* Delivered Checkbox */}
            <div className="mb-2 flex items-center gap-3">
              <input
                type="checkbox"
                id="delivered-checkbox"
                className="w-5 h-5"
                checked={invoiceForm.Delivered}
                onChange={e => setInvoiceForm(f => ({ ...f, Delivered: e.target.checked }))}
              />
              <label htmlFor="delivered-checkbox" className="text-sm font-medium text-gray-700 cursor-pointer">Delivered</label>
            </div>
            {/* FIFO Checkbox - Always visible */}
            <div className="mb-2 flex items-center gap-3">
              <input
                type="checkbox"
                id="fifo-checkbox"
                className="w-5 h-5"
                checked={invoiceForm.AddInFIFO}
                onChange={e => setInvoiceForm(f => ({ ...f, AddInFIFO: e.target.checked }))}
              />
              <label htmlFor="fifo-checkbox" className="text-sm font-medium text-gray-700 cursor-pointer">Add in FIFO</label>
            </div>
            {/* Location/Company/Partner Change Confirms */}
            {showLocationConfirm && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
                <div className="bg-white rounded-2xl shadow-2xl p-8 w-[350px] max-w-full flex flex-col items-center gap-6 relative">
                  <div className="font-semibold text-lg text-center text-gray-800">All selected items for sale are for a different location.<br/>Do you want to delete all items and add fresh items to the sale list?</div>
                  <div className="flex flex-row gap-8 mt-2">
                    <button className="h-10 px-8 rounded-lg font-semibold shadow bg-gray-200 text-gray-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 transition text-base" onClick={() => { setShowLocationConfirm(false); setPendingLocation(''); setCustomer(f => ({ ...f, Location: prevLocation })); }}>Cancel</button>
                    <button className="h-10 px-8 rounded-lg font-semibold shadow bg-cyan-600 text-white transition text-base" onClick={() => { setShowLocationConfirm(false); setCustomer(f => ({ ...f, Location: pendingLocation })); setPrevLocation(pendingLocation); setPendingLocation(''); setProducts([]); }}>OK</button>
                  </div>
                </div>
              </div>
            )}
            {showCompanyConfirm && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
                <div className="bg-white rounded-2xl shadow-2xl p-8 w-[350px] max-w-full flex flex-col items-center gap-6 relative">
                  <div className="font-semibold text-lg text-center text-gray-800">Changing the company will delete all line items from the sale list.<br/>Do you want to continue?</div>
                  <div className="flex flex-row gap-8 mt-2">
                    <button className="h-10 px-8 rounded-lg font-semibold shadow bg-gray-200 text-gray-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 transition text-base" onClick={() => { setShowCompanyConfirm(false); setPendingCompany(''); setCustomer(f => ({ ...f, SelectCompany: prevCompany })); }}>Cancel</button>
                    <button className="h-10 px-8 rounded-lg font-semibold shadow bg-cyan-600 text-white transition text-base" onClick={() => { setShowCompanyConfirm(false); setCustomer(f => ({ ...f, SelectCompany: pendingCompany })); setPrevCompany(pendingCompany); setPendingCompany(''); setProducts([]); }}>OK</button>
                  </div>
                </div>
              </div>
            )}
            {showPartnerConfirm && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
                <div className="bg-white rounded-2xl shadow-2xl p-8 w-[350px] max-w-full flex flex-col items-center gap-6 relative">
                  <div className="font-semibold text-lg text-center text-gray-800">Are you sure you want to change the rates?<br/>Existing items will be removed.</div>
                  <div className="flex flex-row gap-8 mt-2">
                    <button className="h-10 px-8 rounded-lg font-semibold shadow bg-gray-200 text-gray-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 transition text-base" onClick={() => { setShowPartnerConfirm(false); setPendingPartner(''); setCustomer(f => ({ ...f, Partner: prevPartner })); }}>Cancel</button>
                    <button className="h-10 px-8 rounded-lg font-semibold shadow bg-cyan-600 text-white transition text-base" onClick={() => { setShowPartnerConfirm(false); setCustomer(f => ({ ...f, Partner: pendingPartner })); setPrevPartner(pendingPartner); setPendingPartner(''); setProducts([]); }}>OK</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Customer Section */}
      <div className="mb-2 sm:mb-3">
        <button
          className={`w-full text-left py-3 sm:py-4 px-4 sm:px-5 rounded-lg font-bold text-base sm:text-lg md:text-xl ${openSection === 'customer' ? 'bg-cyan-100' : 'bg-white'} border-b-2 border-cyan-300 min-h-[48px] sm:min-h-[56px] transition-all duration-200 hover:shadow-md active:scale-[0.98]`}
          onClick={() => goToSection('customer')}
        >
          1. Customer Details
        </button>
        {openSection === 'customer' && (
          <div className="p-3 sm:p-4 md:p-5 bg-white rounded-b-lg shadow-md flex flex-col gap-3 sm:gap-4 animate-fade-in">
            {/* Unknown checkbox at the top */}
            <div className="flex flex-row items-center gap-4 mb-2 sm:mb-3">
              <label className="flex items-center text-sm sm:text-base cursor-pointer">
                <input
                  type="checkbox"
                  className="mr-2 w-4 h-4 sm:w-5 sm:h-5"
                  checked={isUnknown}
                  onChange={e => {
                    setIsUnknown(e.target.checked);
                    if (e.target.checked) {
                      setCustomerTotalDue(null);
                      // When Unknown is checked: in edit mode preserve CustState; in create mode use company state
                      const stateToKeep = isEditMode ? customer.State : (companies.find(c => String(c.Id) === String(customer.SelectCompany))?.State || '');
                      setCustomer(f => ({ 
                        ...f, 
                        Phone: '',
                        Mobile1: '',
                        Name: 'UNKNOWN', 
                        Company: 'UNKNOWN',
                        Email: '',
                        GST: '',
                        GSTN: '',
                        Address: '',
                        Address1: '',
                        ReferredBy: '',
                        State: stateToKeep
                      }));
                    } else {
                      setCustomerTotalDue(null);
                      // When Unknown is unchecked: preserve CustState in edit mode; in create mode use company state
                      const stateToKeep = isEditMode ? customer.State : (companies.find(c => String(c.Id) === String(customer.SelectCompany))?.State || '');
                      setCustomer(f => ({ 
                        ...f, 
                        Phone: '',
                        Mobile1: '',
                        Name: '', 
                        Company: '',
                        Email: '',
                        GST: '',
                        GSTN: '',
                        Address: '',
                        Address1: '',
                        ReferredBy: '',
                        State: stateToKeep
                      }));
                    }
                  }}
                />
                Unknown
              </label>
            </div>
            {/* Customer Fields */}
            <div className="flex flex-col gap-2 sm:gap-3">
              {/* Phone field with suggestions - keyboard dropdown like product name */}
              <div className="relative">
                <input
                  className={`w-full h-11 sm:h-12 md:h-14 px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md ${isUnknown ? 'bg-gray-100' : 'bg-white'} placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 text-sm sm:text-base transition-all duration-200`}
                  value={customer.Phone}
                  onChange={e => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setCustomer(f => ({ ...f, Phone: value }));
                    setShowPhoneSuggestions(value.length > 0);
                  }}
                  onFocus={() => setShowPhoneSuggestions(customer.Phone.length > 0)}
                  onBlur={() => setTimeout(() => setShowPhoneSuggestions(false), 150)}
                  onKeyDown={e => {
                    const len = phoneSuggestions?.length || 0;
                    if (!showPhoneSuggestions || phoneSuggestionsLoading || len === 0) return;
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setPhoneDropdownActiveIndex(prev => {
                        const next = prev + 1;
                        return next >= len ? 0 : next;
                      });
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setPhoneDropdownActiveIndex(prev => {
                        const next = prev - 1;
                        return next < 0 ? len - 1 : next;
                      });
                    } else if (e.key === 'Enter') {
                      e.preventDefault();
                      const idx = phoneDropdownActiveIndex >= 0 ? phoneDropdownActiveIndex : 0;
                      const suggestion = phoneSuggestions[idx];
                      if (suggestion) {
                        applyPhoneSuggestion(suggestion);
                        setTimeout(() => {
                          const nameInput = document.querySelector('input[placeholder="Name"]');
                          if (nameInput && !(suggestion?.CompanyName || '')) {
                            nameInput.focus();
                          }
                        }, 50);
                      }
                    }
                  }}
                  placeholder="Phone"
                  type="tel"
                  maxLength={10}
                  readOnly={isUnknown}
                />
                {showPhoneSuggestions && (
                  <div ref={phoneDropdownRef} className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-md shadow-lg z-10 mt-1 max-h-48 overflow-y-auto">
                    {phoneSuggestionsLoading && (
                      <div className="p-2 text-xs text-gray-600 border-b">Loading suggestions...</div>
                    )}
                    {!phoneSuggestionsLoading && phoneSuggestions.length === 0 && (
                      <div className="p-2 text-xs text-gray-600">No previous customers found</div>
                    )}
                    {!phoneSuggestionsLoading && phoneSuggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className={`phone-dropdown-item p-2 cursor-pointer text-sm border-b last:border-b-0 transition ${index === phoneDropdownActiveIndex ? 'bg-blue-100' : 'hover:bg-gray-100'}`}
                        onClick={() => {
                          applyPhoneSuggestion(suggestion);
                          setTimeout(() => {
                            const nameInput = document.querySelector('input[placeholder="Name"]');
                            if (nameInput && !(suggestion?.CompanyName || '')) {
                              nameInput.focus();
                            }
                          }, 50);
                        }}
                      >
                        <div className="font-medium">{suggestion?.Mobile || ''}</div>
                        {(suggestion?.CompanyName || suggestion?.ContactName) && (
                          <div className="text-xs text-gray-600">{suggestion?.CompanyName || suggestion?.ContactName || ''}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {phoneLoading && (
                <div className="text-xs text-cyan-700 mt-1">Checking customer...</div>
              )}
              {phoneError && (
                <div className="text-xs text-red-600 mt-1">{phoneError}</div>
              )}
              <input
                className="w-full h-11 sm:h-12 md:h-14 px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 text-sm sm:text-base transition-all duration-200"
                value={customer.Name}
                onChange={e => setCustomer(f => ({ ...f, Name: e.target.value }))}
                placeholder="Name"
                type="text"
                readOnly={isUnknown}
              />
              <input
                className="w-full h-11 sm:h-12 md:h-14 px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 text-sm sm:text-base transition-all duration-200"
                value={customer.Company}
                onChange={e => setCustomer(f => ({ ...f, Company: e.target.value }))}
                placeholder="Company"
                type="text"
                readOnly={isUnknown}
              />
              <div>
                <input
                  className={cn('w-full h-11 sm:h-12 md:h-14 px-3 sm:px-4 py-2 sm:py-3 border rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 text-sm sm:text-base transition-all duration-200', isUnknown ? 'bg-gray-100 border-gray-300' : 'bg-white', customerGstError ? 'border-red-500' : 'border-gray-300')}
                  value={customer.GST}
                  onChange={e => {
                    const { value, hadInvalidInput } = sanitizeGstInput(e.target.value);
                    setCustomer(f => ({ ...f, GST: value }));
                    setCustomerGstError(hadInvalidInput);
                  }}
                  onBlur={() => setCustomerGstError(false)}
                  placeholder="GST #"
                  type="text"
                  maxLength={15}
                  readOnly={isUnknown}
                />
                {customerGstError && <p className="text-red-500 text-xs mt-0.5">{GST_VALIDATION_MESSAGE}</p>}
              </div>
              <input
                className={`w-full h-11 sm:h-12 md:h-14 px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md ${isUnknown ? 'bg-gray-100' : 'bg-white'} placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 text-sm sm:text-base transition-all duration-200`}
                value={customer.Locality}
                onChange={e => setCustomer(f => ({ ...f, Locality: e.target.value }))}
                placeholder="Locality"
                type="text"
                readOnly={isUnknown}
              />
              {/* State Dropdown */}
              <select
                className={`w-full h-11 sm:h-12 md:h-14 px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md ${isUnknown || statesLoading ? 'bg-gray-100' : 'bg-white'} focus:outline-none focus:ring-2 focus:ring-cyan-400 text-sm sm:text-base transition-all duration-200`}
                value={customer.State}
                onChange={e => handleStateChange(e.target.value)}
                disabled={isUnknown || statesLoading}
              >
                <option value="">{statesLoading ? 'Loading...' : 'Select State'}</option>
                {statesError && <option value="">{statesError}</option>}
                {states.map(state => (
                  <option key={state.Code} value={state.State}>{state.State}</option>
                ))}
              </select>
              {/* More button for additional fields */}
              <button
                type="button"
                className="text-cyan-700 underline text-sm sm:text-base text-left w-fit mt-2 mb-2 py-1 px-2 rounded hover:bg-cyan-50 transition-colors duration-200"
                onClick={() => setShowMoreCustomerFields(v => !v)}
              >
                {showMoreCustomerFields ? 'Hide' : 'More'} ▼
              </button>
              {/* Additional fields, hidden by default */}
              {showMoreCustomerFields && (
                <div className="flex flex-col gap-2 sm:gap-3 animate-fade-in">
                  <input
                    className="w-full h-11 sm:h-12 md:h-14 px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 text-sm sm:text-base transition-all duration-200"
                    value={customer.Email}
                    onChange={e => setCustomer(f => ({ ...f, Email: e.target.value }))}
                    placeholder="Email"
                    type="email"
                    readOnly={isUnknown}
                  />
                  <input
                    className="w-full h-11 sm:h-12 md:h-14 px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 text-sm sm:text-base transition-all duration-200"
                    value={customer.Address}
                    onChange={e => setCustomer(f => ({ ...f, Address: e.target.value }))}
                    placeholder="Address"
                    type="text"
                    readOnly={isUnknown}
                  />
                  <input
                    className="w-full h-11 sm:h-12 md:h-14 px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 text-sm sm:text-base transition-all duration-200"
                    value={customer.ReferredBy}
                    onChange={e => setCustomer(f => ({ ...f, ReferredBy: e.target.value }))}
                    placeholder="Referred By"
                    type="text"
                    readOnly={isUnknown}
                  />
                </div>
              )}
            </div>
            <div className="flex flex-row items-center gap-4 mt-3 sm:mt-4">
              {/* Ship to Same - Conditionally shown based on settings */}
              {salesInvoiceSettings.shipAddressNeeded && (
                <label className="flex items-center text-sm sm:text-base cursor-pointer">
                  <input
                    type="checkbox"
                    className="mr-2 w-4 h-4 sm:w-5 sm:h-5"
                    checked={shipToSame}
                    onChange={e => {
                      const isChecked = e.target.checked;
                      setShipToSame(isChecked);
                      if (!isChecked) {
                        // When unchecking, show popup with preserved values
                        setShowShipToPopup(true);
                      }
                    }}
                  />
                  Ship to (Same)
                </label>
              )}
              <button
                className="ml-auto px-3 sm:px-4 py-2 sm:py-2.5 rounded bg-cyan-200 text-cyan-900 text-sm sm:text-base font-semibold min-h-[44px] hover:bg-cyan-300 transition-colors duration-200 active:scale-95"
                onClick={() => setShowNotes(true)}
                type="button"
              >
                Notes
              </button>
            </div>
            {showNotes && (
              <div className="flex flex-col gap-2 mt-2 bg-cyan-50 p-2 rounded">
                <textarea
                  className="w-full h-24 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-400"
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Enter notes..."
                />
                <div className="flex flex-row gap-2 justify-end">
                  <button className="px-4 py-1 rounded bg-cyan-600 text-white font-bold" onClick={() => setShowNotes(false)}>Save</button>
                  <button className="px-4 py-1 rounded bg-gray-200 text-cyan-700 font-bold" onClick={() => { setShowNotes(false); setNotes(''); }}>Close</button>
                </div>
              </div>
            )}
            {/* Ship To Popup */}
            {showShipToPopup && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
                <div className="bg-white rounded-xl shadow-lg p-6 min-w-[340px] max-w-[95vw] w-full flex flex-col gap-4 relative">
                  <div className="mb-2 font-bold text-lg text-center">Shipping Address</div>
                  <div className="grid grid-cols-1 gap-3">
                    <input className={`w-full h-8 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-400 transition ${isUnknown ? 'bg-gray-100' : 'bg-white'}`} value={shipToForm.Name} onChange={e => setShipToForm(f => ({ ...f, Name: e.target.value }))} placeholder="Name" readOnly={isUnknown} />
                    <input className={`w-full h-8 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-400 transition ${isUnknown ? 'bg-gray-100' : 'bg-white'}`} value={shipToForm.Company} onChange={e => setShipToForm(f => ({ ...f, Company: e.target.value }))} placeholder="Company" readOnly={isUnknown} />
                    <input className={`w-full h-8 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-400 transition ${isUnknown ? 'bg-gray-100' : 'bg-white'}`} value={shipToForm.Phone} onChange={e => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setShipToForm(f => ({ ...f, Phone: value }));
                    }} placeholder="Phone" maxLength={10} readOnly={isUnknown} />
                    <input className={`w-full h-8 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-400 transition ${isUnknown ? 'bg-gray-100' : 'bg-white'}`} value={shipToForm.Email} onChange={e => setShipToForm(f => ({ ...f, Email: e.target.value }))} placeholder="Email" readOnly={isUnknown} />
                    <div>
                      <input
                        className={cn('w-full h-8 px-2 py-1 border rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-400 transition', isUnknown ? 'bg-gray-100 border-gray-300' : 'bg-white', shipToGstError ? 'border-red-500' : 'border-gray-300')}
                        value={shipToForm.GST}
                        onChange={e => {
                          const { value, hadInvalidInput } = sanitizeGstInput(e.target.value);
                          setShipToForm(f => ({ ...f, GST: value }));
                          setShipToGstError(hadInvalidInput);
                        }}
                        onBlur={() => setShipToGstError(false)}
                        placeholder="GST Number"
                        maxLength={15}
                        readOnly={isUnknown}
                      />
                      {shipToGstError && <p className="text-red-500 text-xs mt-0.5">{GST_VALIDATION_MESSAGE}</p>}
                    </div>
                    <input className={`w-full h-8 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-400 transition ${isUnknown ? 'bg-gray-100' : 'bg-white'}`} value={shipToForm.Address} onChange={e => setShipToForm(f => ({ ...f, Address: e.target.value }))} placeholder="Address" readOnly={isUnknown} />
                    <select className={`w-full h-8 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-400 transition ${isUnknown ? 'bg-gray-100' : 'bg-white'}`} value={shipToForm.State} onChange={e => setShipToForm(f => ({ ...f, State: e.target.value }))} disabled={isUnknown}>
                      <option value="">Select State</option>
                      {states.map(state => (
                        <option key={state.Code} value={state.State}>{state.State}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-row gap-4 justify-end mt-4">
                    <button className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2 px-6 rounded-lg shadow focus:outline-none" onClick={() => {
                      setShowShipToPopup(false);
                      setShipToSame(true);
                      setLastShipToSameForm(null); // Clear saved form on Cancel
                    }}>Close</button>
                    <button className="bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-2 px-6 rounded-lg shadow focus:outline-none" onClick={() => {
                      setShowShipToPopup(false);
                      setShipToSame(false);
                      setLastShipToSameForm(shipToForm); // Save form on Update
                    }}>Update</button>
                  </div>

                </div>
              </div>
            )}
            {/* State Change Confirm */}
            {showStateConfirm && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
                <div className="bg-white rounded-2xl shadow-2xl p-8 w-[350px] max-w-full flex flex-col items-center gap-6 relative">
                  <div className="font-semibold text-lg text-center text-gray-800">All the line items will be deleted due to change in state. Do you want to continue?</div>
                  <div className="flex flex-row gap-8 mt-2">
                    <button className="h-10 px-8 rounded-lg font-semibold shadow bg-gray-200 text-gray-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 transition text-base" onClick={() => { setShowStateConfirm(false); setPendingState(''); }}>Cancel</button>
                    <button className="h-10 px-8 rounded-lg font-semibold shadow bg-cyan-600 text-white transition text-base" onClick={() => { setShowStateConfirm(false); setCustomer(f => ({ ...f, State: pendingState })); setPrevState(pendingState); setPendingState(''); setProducts([]); }}>OK</button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Due Amount Display */}
            {customerTotalDue !== null && (
              <div className="mt-3 sm:mt-4">
                <button
                  type="button"
                  onClick={handleDueAmountClick}
                  className="inline-flex items-center gap-2 text-sm sm:text-base font-medium text-blue-600 hover:text-blue-800 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-400 rounded transition-colors"
                  title="Click to view customer ledger details"
                >
                  <span>{formatDueAmount(customerTotalDue).text}</span>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </button>
              </div>
            )}
            
            <button className="w-full mt-4 sm:mt-5 py-3 sm:py-4 rounded-lg bg-cyan-600 text-white font-bold text-base sm:text-lg min-h-[48px] sm:min-h-[56px] hover:bg-cyan-700 transition-all duration-200 active:scale-[0.98] shadow-md" onClick={() => goToSection('product')}>Next: Product</button>
          </div>
        )}
      </div>

      {/* Product Section */}
      <div className="mb-2 sm:mb-3">
        <button
          className={`w-full text-left py-3 sm:py-4 px-4 sm:px-5 rounded-lg font-bold text-base sm:text-lg md:text-xl ${openSection === 'product' ? 'bg-cyan-100' : 'bg-white'} border-b-2 border-cyan-300 min-h-[48px] sm:min-h-[56px] transition-all duration-200 hover:shadow-md active:scale-[0.98]`}
          onClick={() => goToSection('product')}
        >
          2. Product Details
        </button>
        {openSection === 'product' && (
          <div id="product-section" className="p-3 sm:p-4 md:p-5 bg-white rounded-b-lg shadow-md flex flex-col gap-3 sm:gap-4 animate-fade-in">
            {/* Move New Product Checkbox here */}
            <span
              className="text-cyan-700 underline cursor-pointer text-sm sm:text-base mb-2 sm:mb-3 inline-block py-2 px-3 rounded hover:bg-cyan-50 transition-colors duration-200"
              onClick={() => setShowProductEntryModal(true)}
              role="button"
              tabIndex={0}
              onKeyPress={e => { 
                if (e.key === 'Enter' || e.key === ' ') {
                  setShowProductEntryModal(true);
                }
              }}
            >
              New Product
            </span>
            {/* Single Product Search Field */}
            <div className="flex flex-col gap-2 sm:gap-3 relative">
              <div className="flex flex-row gap-2 sm:gap-3 items-center relative mb-2 sm:mb-3">
                <input
                  ref={searchInputRef}
                  className="w-full h-11 sm:h-12 md:h-14 px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md bg-white placeholder-gray-400 text-sm sm:text-base transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-cyan-400"
                  value={productSearch}
                  onChange={e => { setProductSearch(e.target.value); setShowProductDropdown(true); }}
                  onFocus={() => setShowProductDropdown(true)}
                  placeholder="Search by barcode or product name..."
                  type="text"
                  onKeyDown={e => {
                    if (e.key === 'Enter' && productSearch.length > 0) {
                      const found = productSearchResults.find(p => p.Barcode === productSearch);
                      if (found) {
                        // Check if product is available based on isTraceable logic
                        const available = Number(found.Available) || 0;
                        const isTraceable = found.isTraceable || false;
                        
                        // If available is 0 or negative and isTraceable is true, don't add the product
                        if (available <= 0 && isTraceable) {
                          return; // Don't add the product
                        }
                        
                        // If isTraceable is false, allow adding even if available is 0
                        // If available > 0, allow adding regardless of isTraceable
                        if (available > 0 || !isTraceable) {
                          // Check if product has serial numbers
                          const hasSerial = found.hasSerial === 1 || found.HasSerialFlag === 1;
                          if (hasSerial && found.SerialDetails && found.SerialDetails.length > 0) {
                            // Open serial selection popup
                            setSerialPopupProduct(found);
                            setShowSerialSelectionPopup(true);
                            setProductSearch('');
                            setShowProductDropdown(false);
                          } else {
                            // No serial numbers, proceed with normal flow
                            addProductToTable({ ...found, Qty: 1 });
                            setProductSearch('');
                            setShowProductDropdown(false);
                          }
                        }
                      }
                    }
                  }}
                />
                <button
                  type="button"
                  className="ml-2 p-2 sm:p-3 rounded-lg bg-cyan-100 hover:bg-cyan-200 focus:bg-cyan-300 min-w-[44px] min-h-[44px] sm:min-w-[48px] sm:min-h-[48px] transition-all duration-200 active:scale-95"
                  title="Scan Product"
                  onClick={handleOpenScanner}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="24" height="24">
                    <rect x="3" y="3" width="4" height="4" stroke="currentColor" strokeWidth="2"/>
                    <rect x="17" y="3" width="4" height="4" stroke="currentColor" strokeWidth="2"/>
                    <rect x="3" y="17" width="4" height="4" stroke="currentColor" strokeWidth="2"/>
                    <rect x="17" y="17" width="4" height="4" stroke="currentColor" strokeWidth="2"/>
                    <line x1="7" y1="12" x2="17" y2="12" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </button>
              </div>
              {/* Serial Number Search Field - only shown when Serial Text setting has a value */}
              {showSerialField && (
              <div className="flex items-center gap-2 mt-2">
                <input
                  className="flex-1 min-w-0 h-11 sm:h-12 md:h-14 px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md bg-white placeholder-gray-400 text-sm sm:text-base transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-cyan-400"
                  value={serialNumberSearchTerm}
                  onChange={e => setSerialNumberSearchTerm(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && serialNumberSearchTerm.trim()) {
                      e.preventDefault();
                      handleSerialNumberSearch(serialNumberSearchTerm.trim());
                    }
                  }}
                  placeholder={`Search by ${serialText.toLowerCase()}...`}
                  type="text"
                />
                <button
                  type="button"
                  className="shrink-0 p-2 sm:p-3 rounded-lg bg-cyan-100 hover:bg-cyan-200 focus:bg-cyan-300 min-w-[44px] min-h-[44px] sm:min-w-[48px] sm:min-h-[48px] transition-all duration-200 active:scale-95 flex items-center justify-center"
                  title={`Search by ${serialText.toLowerCase()}`}
                  onClick={() => {
                    if (serialNumberSearchTerm.trim()) {
                      handleSerialNumberSearch(serialNumberSearchTerm.trim());
                    }
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="24" height="24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>
              </div>
              )}
              {showProductDropdown && (productSearch.length > 0 || (isEditMode && productSearchResults.length > 0)) && (
                <div
                  className="bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto absolute z-10 w-full"
                  style={{ top: '100%', left: 0 }}
                >
                  {isProductSearchLoading ? (
                    <div className="p-3 sm:p-4 text-sm sm:text-base text-gray-500">Loading...</div>
                  ) : productSearchResults.length > 0 ? (
                    productSearchResults.map((product, i) => {
                      const available = Number(product.Available) || 0;
                      const isTraceable = product.isTraceable || false;
                      const isOutOfStock = available <= 0 && isTraceable;
                      
                      return (
                        <div
                          key={product.Barcode}
                          className={`p-3 sm:p-4 text-sm sm:text-base border-b last:border-b-0 flex flex-row items-center gap-2 sm:gap-3 transition-colors duration-200 ${
                            isOutOfStock 
                              ? 'bg-gray-100 cursor-not-allowed opacity-60' 
                              : 'hover:bg-blue-100 cursor-pointer'
                          }`}
                          onClick={() => {
                            if (!isOutOfStock) {
                              // Check if product is available based on isTraceable logic
                              if (available > 0 || !isTraceable) {
                                // Check if product has serial numbers
                                const hasSerial = product.hasSerial === 1 || product.HasSerialFlag === 1;
                                if (hasSerial && product.SerialDetails && product.SerialDetails.length > 0) {
                                  // Open serial selection popup
                                  setSerialPopupProduct(product);
                                  setShowSerialSelectionPopup(true);
                                  setProductSearch('');
                                  setShowProductDropdown(false);
                                } else {
                                  // No serial numbers, proceed with normal flow
                                  setProductSearch('');
                                  setShowProductDropdown(false);
                                  addProductToTable({ ...product, Qty: 1 });
                                }
                              }
                            }
                          }}
                        >
                          <img
                            src={product.imgstr || '/placeholder.svg'}
                            alt={product.Title}
                            className="w-10 h-10 sm:w-12 sm:h-12 object-contain rounded border"
                            onError={e => { e.currentTarget.src = '/placeholder.svg'; }}
                          />
                          <div>
                            <div className="font-medium">{product.Title}</div>
                            <div className="text-gray-600">{product.Barcode}</div>
                            {isOutOfStock && (
                              <div className="text-red-500 font-semibold text-xs">Out of Stock</div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="p-3 sm:p-4 text-sm sm:text-base text-gray-500">No products found</div>
                  )}
                </div>
              )}
            </div>
            {/* Serial field for future implementation */}
            {/* <input
              className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md bg-white placeholder-gray-400"
              value={serial}
              onChange={e => setSerial(e.target.value)}
              placeholder="Serial (coming soon)"
              type="text"
              disabled
            /> */}
            {productAddMessage && (
              <div className={`text-sm sm:text-base mt-1 sm:mt-2 ${productAddMessageType === 'success' ? 'text-green-600' : 'text-red-600'}`}>{productAddMessage}</div>
            )}
            {/* Product Cards List */}
            <div className="flex flex-col gap-3 sm:gap-4">
              {products.length === 0 ? (
                <div className="text-center text-gray-400 py-6 sm:py-8 text-base sm:text-lg">
                  No items
                  {isEditMode && (
                    <div className="mt-2 space-y-2">
                      <button
                        className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
                        onClick={() => {
                          console.log('Manual product search trigger');
                          console.log('Current customer.Partner:', customer.Partner);
                          console.log('Current customer.Location:', customer.Location);
                          refetchProductSearch();
                        }}
                      >
                        Refresh Products
                      </button>
                      <div className="text-xs text-gray-500">
                        Partner: {customer.Partner} | Location: {customer.Location}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {isEditMode && !showProductDropdown && (
                    <div className="text-center mb-4">
                      <button
                        className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
                        onClick={() => setShowProductDropdown(true)}
                      >
                        Add More Products ({productSearchResults.length} available)
                      </button>
                    </div>
                  )}
                  {products.filter(prod => {
                    if (!productTableSearch.trim()) return true;
                    const search = productTableSearch.toLowerCase();
                    return (
                      (prod.Title && prod.Title.toLowerCase().includes(search)) ||
                      (prod.Barcode && prod.Barcode.toLowerCase().includes(search)) ||
                      (prod.HSN && prod.HSN.toLowerCase().includes(search)) ||
                      (prod.MRP && String(prod.MRP).toLowerCase().includes(search)) ||
                      (prod.OfferPrice && String(prod.OfferPrice).toLowerCase().includes(search)) ||
                      (prod.Category && prod.Category.toLowerCase().includes(search)) ||
                      (prod.BrandName && prod.BrandName.toLowerCase().includes(search))
                    );
                  }).map((prod, idx) => {
                    const mrp = parseFloat(prod.MRP) || 0;
                    const offer = parseFloat(prod.OfferPrice) || 0;
                    const qty = parseFloat(prod.Qty) || 1;
                    const discount = mrp ? (mrp - offer) : 0;
                    const discountPer = mrp ? (100 * (mrp - offer) / mrp) : 0;
                    // Calculate GST and Cess amounts
                    const gstAmount = (parseFloat(prod.UnitPrice) || 0) * (parseFloat(prod.GST) || 0) / 100;
                    const cessAmount = (parseFloat(prod.UnitPrice) || 0) * (parseFloat(prod.CessPer) || 0) / 100;
                    const discountValue = parseFloat(prod.Discount) || 0;
                    const offerPriceValue = (parseFloat(prod.UnitPrice) || 0) - discountValue + gstAmount + cessAmount;
                    return (
                      <div key={prod.Barcode || idx} className="bg-gray-50 border rounded-lg shadow-sm p-3 sm:p-4 flex flex-col gap-2 sm:gap-3 hover:shadow-md transition-shadow duration-200">
                        <div className="flex flex-row gap-3 sm:gap-4 items-center">
                          {salesInvoiceSettings.imageInInvoice && (
                            <img
                              src={prod.imgstr || '/placeholder.svg'}
                              alt={prod.Title}
                              className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 object-contain rounded border flex-shrink-0"
                              onError={e => { e.currentTarget.src = '/placeholder.svg'; }}
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="font-bold text-sm sm:text-base md:text-lg text-blue-900 truncate">{prod.Title}</div>
                            <div className="text-xs sm:text-sm text-gray-600 flex flex-row items-center gap-1">
                              <span>Barcode:</span>
                              <span className="font-mono">{prod.Barcode}</span>
                            </div>
                            {showSerialField && prod.SerialNumber && (
                              <div className="text-xs sm:text-sm text-gray-600 flex flex-row items-center gap-1 mt-1">
                                <span>{serialText}:</span>
                                <span className="font-mono font-semibold">{prod.SerialNumber}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-2">
                          {/* Main fields */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">MRP</label>
                            <Input
                              type="number"
                              className="w-full"
                              value={prod.MRP}
                              onChange={e => {
                                if (!canEditPrice) return;
                                const value = e.target.value;
                                // Use the recalculateProductFields helper to ensure all fields update correctly
                                setProducts(prev => prev.map((p, i) => 
                                  i === idx ? recalculateProductFields(p, 'MRP', value) : p
                                ));
                              }}
                              onBlur={e => {
                                if (!canEditPrice) return;
                                const value = e.target.value;
                                if (value === '' || value === null || value === undefined) {
                                  setProducts(prev => prev.map((p, i) => i === idx ? { ...p, MRP: '0.00' } : p));
                                }
                              }}
                              onKeyDown={e => handleFieldKeyDown(e, idx, 'MRP')}
                              data-field="MRP"
                              data-index={idx}
                              readOnly={!canEditPrice}
                            />
                          </div>
                          {/* <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Unit Price</label>
                            <Input
                              type="number"
                              className="w-full"
                              value={prod.UnitPrice || ''}
                              onChange={e => {
                                const value = e.target.value;
                                const qty = parseFloat(prod.Qty) || 1;
                                const unitPrice = parseFloat(value) || 0;
                                const gst = parseFloat(prod.GST) || 0;
                                const cess = parseFloat(prod.CessPer) || 0;
                                const totalTax = gst + cess;
                                const mrp = unitPrice * (1 + totalTax / 100);
                                setProducts(prev => prev.map((p, i) => i === idx ? {
                                  ...p,
                                  UnitPrice: value,
                                  MRP: mrp.toFixed(2),
                                  OfferPrice: value,
                                  Total: (qty * unitPrice).toFixed(2),
                                } : p));

                                // Clear any existing timeout
                                if (unitPriceValidationTimeout.current) {
                                  clearTimeout(unitPriceValidationTimeout.current);
                                }

                                // Set a new timeout to validate after 1 second
                                unitPriceValidationTimeout.current = setTimeout(() => {
                                  const originalUnitPrice = parseFloat(prod.OriginalUnitPrice || '0') || 0;
                                  const enteredUnitPrice = parseFloat(value) || 0;
                                  
                                  // Check if entered value is less than original (only validate if original is greater than 0)
                                  if (enteredUnitPrice > 0 && originalUnitPrice > 0 && enteredUnitPrice < originalUnitPrice) {
                                    setUnitPriceValidationData({
                                      index: idx,
                                      enteredValue: value,
                                      originalValue: prod.OriginalUnitPrice || '0.00'
                                    });
                                    setShowUnitPriceValidation(true);
                                  }
                                }, 1000); // 1 second delay
                              }}
                              onBlur={e => {
                                const value = e.target.value;
                                if (value === '' || value === null || value === undefined) {
                                  setProducts(prev => prev.map((p, i) => i === idx ? { ...p, UnitPrice: '0.00' } : p));
                                }
                              }}
                              onKeyDown={e => handleFieldKeyDown(e, idx, 'UnitPrice')}
                              data-field="UnitPrice"
                              data-index={idx}
                            />
                          </div> */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Discount %</label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              className="w-full"
                              value={prod.DiscountPer ?? discountPer.toFixed(2)}
                              onChange={e => {
                                if (!canEditDiscount) return;
                                const value = e.target.value;
                                // Use the recalculateProductFields helper to ensure all fields update correctly
                                setProducts(prev => prev.map((p, i) => 
                                  i === idx ? recalculateProductFields(p, 'DiscountPer', value) : p
                                ));
                              }}
                              onBlur={e => {
                                if (!canEditDiscount) return;
                                const value = e.target.value;
                                if (value === '' || value === null || value === undefined) {
                                  setProducts(prev => prev.map((p, i) => i === idx ? { ...p, DiscountPer: '0.00' } : p));
                                }
                              }}
                              onKeyDown={e => handleFieldKeyDown(e, idx, 'DiscountPer')}
                              data-field="DiscountPer"
                              data-index={idx}
                              readOnly={!canEditDiscount}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Discount</label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              className="w-full"
                              value={prod.Discount ?? discount.toFixed(2)}
                              onChange={e => {
                                if (!canEditDiscount) return;
                                const value = e.target.value;
                                // Use the recalculateProductFields helper to ensure all fields update correctly
                                setProducts(prev => prev.map((p, i) => 
                                  i === idx ? recalculateProductFields(p, 'Discount', value) : p
                                ));
                              }}
                              onBlur={e => {
                                if (!canEditDiscount) return;
                                const value = e.target.value;
                                if (value === '' || value === null || value === undefined) {
                                  setProducts(prev => prev.map((p, i) => i === idx ? { ...p, Discount: '0.00' } : p));
                                }
                              }}
                              onKeyDown={e => handleFieldKeyDown(e, idx, 'Discount')}
                              data-field="Discount"
                              data-index={idx}
                              readOnly={!canEditDiscount}
                            />
                          </div>
                          {/* Cost Price - Conditionally shown based on settings */}
                          {salesInvoiceSettings.showCostPrice && (
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Cost Price</label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              className="w-full"
                              value={prod.CostPrice || '0.00'}
                              readOnly
                              data-field="CostPrice"
                              data-index={idx}
                            />
                          </div>
                          )}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Qty</label>
                            <Input
                              type="number"
                              min="1"
                              className="w-full"
                              value={prod.Qty}
                              onChange={e => {
                                const value = e.target.value;
                                const qty = parseFloat(value) || 1;
                                
                                // Check for quantity exceeded validation for traceable products
                                const isTraceable = prod.isTraceable || false;
                                const available = Number(prod.Available) || 0;
                                
                                if (isTraceable && qty > available) {
                                  // Check if quantity popup is required
                                  if (salesInvoiceSettings.invoiceQtyPopupRequired) {
                                    // Show popup for quantity exceeded
                                    setQuantityExceededProductIndex(idx);
                                    setQuantityExceededAvailable(available);
                                    setShowQuantityExceededPopup(true);
                                    return; // Don't update the product quantity
                                  } else {
                                    // Auto-correct quantity to available amount without popup
                                    const correctedQty = available.toString();
                                    // Use the recalculateProductFields helper to ensure all fields update correctly
                                    setProducts(prev => prev.map((p, i) => 
                                      i === idx ? recalculateProductFields(p, 'Qty', correctedQty) : p
                                    ));
                                    return; // Don't proceed with original quantity
                                  }
                                }
                                
                                // Use the recalculateProductFields helper to ensure all fields update correctly
                                setProducts(prev => prev.map((p, i) => 
                                  i === idx ? recalculateProductFields(p, 'Qty', value) : p
                                ));
                              }}
                              onBlur={e => {
                                const value = e.target.value;
                                if (value === '' || value === null || value === undefined) {
                                  setProducts(prev => prev.map((p, i) => i === idx ? { ...p, Qty: '1', DelQty: '1' } : p));
                                }
                              }}
                              onKeyDown={e => handleFieldKeyDown(e, idx, 'Qty')}
                              data-field="Qty"
                              data-index={idx}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Total</label>
                            <Input
                              type="number"
                              className="w-full"
                              value={prod.Total || (() => {
                                // Total = Taxable Value + GST + Cess Amount
                                const offerPrice = parseFloat(prod.OfferPrice) || 0;
                                const qty = parseFloat(prod.Qty || 1);
                                const gstPercent = parseFloat(prod.GST) || 0;
                                const cessPercent = parseFloat(prod.CessPer) || 0;
                                const taxableValue = (offerPrice * qty) / (1 + gstPercent / 100);
                                const gstAmount = (offerPrice * qty) - taxableValue;
                                const cessAmount = taxableValue * cessPercent / 100;
                                const total = taxableValue + gstAmount + cessAmount;
                                return total.toFixed(2);
                              })()}
                              readOnly
                              inputMode="none"
                              onKeyDown={e => e.preventDefault()}
                              onPaste={e => e.preventDefault()}
                              onWheel={e => (e.currentTarget as HTMLInputElement).blur()}
                              onFocus={e => e.currentTarget.blur()}
                              style={{ userSelect: 'none', pointerEvents: 'none' }}
                            />
                          </div>
                          {/* Offer Price */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Offer Price</label>
                            <Input
                              type="number"
                              className="w-full"
                              value={prod.OfferPrice !== undefined && prod.OfferPrice !== null ? prod.OfferPrice : ''}
                              onChange={e => {
                                if (!canEditPrice) return;
                                const value = e.target.value;
                                const offerPrice = parseFloat(value) || 0;
                                const mrp = parseFloat(prod.MRP) || 0;
                                
                                // Validate: Check if Offer Price exceeds MRP
                                if (offerPrice > mrp) {
                                  // Show error toast and don't update
                                  toast({
                                    title: 'Invalid Offer Price',
                                    description: `Offer Price (${offerPrice.toFixed(2)}) cannot exceed MRP (${mrp.toFixed(2)})`,
                                    variant: 'destructive',
                                  });
                                  // Force re-render to reset the input field to original value
                                  setProducts([...products]);
                                  return; // Don't update the value
                                }
                                
                                // Use the recalculateProductFields helper to ensure all fields update correctly
                                setProducts(prev => prev.map((p, i) => 
                                  i === idx ? recalculateProductFields(p, 'OfferPrice', value) : p
                                ));
                              }}
                              onBlur={e => {
                                if (!canEditPrice) return;
                                const value = e.target.value;
                                if (value === '' || value === null || value === undefined) {
                                  setProducts(prev => prev.map((p, i) => i === idx ? { ...p, OfferPrice: '0.00' } : p));
                                }
                              }}
                              onKeyDown={e => handleFieldKeyDown(e, idx, 'OfferPrice')}
                              data-field="OfferPrice"
                              data-index={idx}
                              readOnly={!canEditPrice}
                            />
                          </div>
                          {/* Delivered Qty - only show for non-GST companies */}
                          {isNonGSTCompany && (
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Delivered Qty</label>
                              <Input
                                type="number"
                                min="0"
                                className="w-full"
                                value={!invoiceForm.Delivered ? 0 : (prod.deliveredQty ?? prod.DelQty ?? prod.Qty)}
                                onChange={e => {
                                  const value = e.target.value;
                                  setProducts(prev => prev.map((p, i) => i === idx ? { ...p, DelQty: value } : p));
                                }}
                                onBlur={e => {
                                  const value = e.target.value;
                                  if (value === '' || value === null || value === undefined) {
                                    setProducts(prev => prev.map((p, i) => i === idx ? { ...p, DelQty: '1' } : p));
                                  }
                                }}
                                disabled={!invoiceForm.Delivered}
                              />
                            </div>
                          )}
                          {!isNonGSTCompany && (
                            <>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">{isIGST ? 'IGST Amount (₹)' : 'GST Amount (₹)'}</label>
                                <Input
                                  type="number"
                                  className="w-full"
                                  value={(() => {
                                    // Always use desktop formula so mobile matches desktop (avoids raw SGST+CGST from edit load)
                                    const offerPrice = parseFloat(prod.OfferPrice) || 0;
                                    const qty = parseFloat(prod.Qty) || 1;
                                    const gstPercent = parseFloat(prod.GST) || 0;
                                    const cessPercent = parseFloat(prod.CessPer) || 0;
                                    const taxableValue = (offerPrice * qty) / (1 + (gstPercent + cessPercent) / 100);
                                    const gstAmount = taxableValue * gstPercent / 100;
                                    return gstAmount.toFixed(2);
                                  })()}
                                  readOnly
                                  tabIndex={-1}
                                  inputMode="none"
                                  onKeyDown={e => e.preventDefault()}
                                  onPaste={e => e.preventDefault()}
                                  onWheel={e => (e.currentTarget as HTMLInputElement).blur()}
                                  onFocus={e => e.currentTarget.blur()}
                                  style={{ userSelect: 'none', pointerEvents: 'none' }}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Taxable Value</label>
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  className="w-full"
                                  value={prod.TaxableValue || (() => {
                                    // Match desktop: include Cess% in denominator
                                    const offerPrice = parseFloat(prod.OfferPrice) || 0;
                                    const qty = parseFloat(prod.Qty || 1);
                                    const gstPercent = parseFloat(prod.GST) || 0;
                                    const cessPercent = parseFloat(prod.CessPer) || 0;
                                    const taxableValue = (offerPrice * qty) / (1 + (gstPercent + cessPercent) / 100);
                                    return taxableValue.toFixed(2);
                                  })()}
                                  onChange={e => {
                                    const value = e.target.value;
                                    const taxableValue = parseFloat(value) || 0;
                                    const gstPercent = parseFloat(prod.GST) || 0;
                                    const cessPercent = parseFloat(prod.CessPer) || 0;
                                    const qty = parseFloat(prod.Qty) || 1;
                                    
                                    // Match desktop: OfferPrice * Qty = TaxableValue * (1 + (GST% + Cess%)/100)
                                    const offerPrice = taxableValue * (1 + (gstPercent + cessPercent) / 100) / qty;
                                    const mrp = parseFloat(prod.MRP) || 0;
                                    
                                    // Recalculate discount based on new offer price
                                    const displayDiscountPer = mrp > 0 ? 100 - (offerPrice * 100 / mrp) : 0;
                                    const displayDiscount = (mrp - offerPrice) * qty;
                                    
                                    // Match desktop: GST = TaxableValue * GST% / 100, Cess = TaxableValue * Cess% / 100
                                    const gstAmount = taxableValue * gstPercent / 100;
                                    const cessAmount = taxableValue * cessPercent / 100;
                                    
                                    // Calculate Total
                                    const total = taxableValue + gstAmount + cessAmount;
                                    
                                    setProducts(prev => prev.map((p, i) => i === idx ? {
                                      ...p,
                                      TaxableValue: value,
                                      OfferPrice: offerPrice.toFixed(2),
                                      DiscountPer: displayDiscountPer.toFixed(2),
                                      Discount: displayDiscount.toFixed(2),
                                      GSTAmount: gstAmount.toFixed(2),
                                      CessAmount: cessAmount.toFixed(2),
                                      Total: total.toFixed(2),
                                    } : p));
                                  }}
                                  onBlur={e => {
                                    const value = e.target.value;
                                    if (value === '' || value === null || value === undefined) {
                                      setProducts(prev => prev.map((p, i) => i === idx ? { ...p, TaxableValue: '0.00' } : p));
                                    }
                                  }}
                                  onKeyDown={e => handleFieldKeyDown(e, idx, 'TaxableValue')}
                                  data-field="TaxableValue"
                                  data-index={idx}
                                />
                              </div>
                            </>
                          )}
                        </div>
                        {/* More button and hidden fields */}
                        <div className="flex flex-row items-center gap-2 mt-1">
                          {!isNonGSTCompany && (
                            <button
                              type="button"
                              className="text-cyan-700 underline text-xs text-left w-fit"
                              onClick={() => setShowMoreProductFields(v => ({ ...v, [idx]: !v[idx] }))}
                            >
                              {showMoreProductFields[idx] ? 'Hide' : 'More'} ▼
                            </button>
                          )}
                          <button
                            type="button"
                            className="ml-auto flex items-center justify-center w-8 h-8 rounded-full transition bg-transparent hover:bg-red-100 focus:bg-red-200"
                            title="Delete"
                            onClick={() => handleRemoveProduct(idx)}
                            style={{ lineHeight: 1 }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="red"
                              width="24"
                              height="24"
                            >
                              <path d="M3 6h18v2H3V6zm2 3h14l-1.5 13.5A2 2 0 0 1 15.5 24h-7a2 2 0 0 1-1.99-1.5L5 9zm5 2v8a1 1 0 0 0 2 0v-8a1 1 0 0 0-2 0zm-4-7V3a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v1h5a1 1 0 1 1 0 2H4a1 1 0 1 1 0-2h5z"/>
                            </svg>
                          </button>
                        </div>
                        {(!isNonGSTCompany && showMoreProductFields[idx]) && (
                          <div className="grid grid-cols-2 gap-2 mt-2 animate-fade-in">
                            {/* Delivered Qty and Cess - for GST companies only */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Delivered Qty</label>
                              <Input
                                type="number"
                                min="0"
                                className="w-full"
                                value={!invoiceForm.Delivered ? 0 : (prod.deliveredQty ?? prod.DelQty ?? prod.Qty)}
                                onChange={e => {
                                  const value = e.target.value;
                                  setProducts(prev => prev.map((p, i) => i === idx ? { ...p, DelQty: value } : p));
                                }}
                                disabled={!invoiceForm.Delivered}
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Cess Amount</label>
                              <Input
                                type="number"
                                className="w-full"
                                value={prod.CessAmount || (() => {
                                  // Match desktop: Taxable Value = OfferPrice*Qty / (1 + (GST% + Cess%)/100), Cess = TaxableValue * Cess% / 100
                                  const offerPrice = parseFloat(prod.OfferPrice) || 0;
                                  const qty = parseFloat(prod.Qty || 1);
                                  const gstPercent = parseFloat(prod.GST) || 0;
                                  const cessPercent = parseFloat(prod.CessPer) || 0;
                                  const taxableValue = (offerPrice * qty) / (1 + (gstPercent + cessPercent) / 100);
                                  const cessAmount = taxableValue * cessPercent / 100;
                                  return cessAmount.toFixed(2);
                                })()}
                                readOnly
                                tabIndex={-1}
                                inputMode="none"
                                onKeyDown={e => e.preventDefault()}
                                onPaste={e => e.preventDefault()}
                                onWheel={e => (e.currentTarget as HTMLInputElement).blur()}
                                onFocus={e => e.currentTarget.blur()}
                                style={{ userSelect: 'none', pointerEvents: 'none' }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </div>

            <button className="w-full mt-4 sm:mt-5 py-3 sm:py-4 rounded-lg bg-cyan-600 text-white font-bold text-base sm:text-lg min-h-[48px] sm:min-h-[56px] hover:bg-cyan-700 transition-all duration-200 active:scale-[0.98] shadow-md" onClick={() => goToSection('payment')}>Next: Payment</button>
            <button className="w-full mt-2 sm:mt-3 py-3 sm:py-4 rounded-lg bg-gray-200 text-cyan-700 font-bold text-base sm:text-lg min-h-[48px] sm:min-h-[56px] hover:bg-gray-300 transition-all duration-200 active:scale-[0.98] shadow-sm" onClick={() => goToSection('customer')}>Back: Customer</button>
          </div>
        )}
      </div>

      {/* Payment Section */}
      <div className="mb-2 sm:mb-3">
        <button
          className={`w-full text-left py-3 sm:py-4 px-4 sm:px-5 rounded-lg font-bold text-base sm:text-lg md:text-xl ${openSection === 'payment' ? 'bg-cyan-100' : 'bg-white'} border-b-2 border-cyan-300 min-h-[48px] sm:min-h-[56px] transition-all duration-200 hover:shadow-md active:scale-[0.98]`}
          onClick={() => goToSection('payment')}
        >
          3. Payment
        </button>
        {openSection === 'payment' && (
          <div className="p-3 sm:p-4 md:p-5 bg-white rounded-b-lg shadow-md flex flex-col gap-3 sm:gap-4 animate-fade-in">
            {/* Payment Summary - At top so round-off values (Total, Paid, Balance) are always visible on mobile */}
            <div className="bg-gray-50 rounded-lg p-4 sm:p-5 flex flex-col gap-2 sm:gap-3 shadow-sm border border-gray-200">
              <div className="flex justify-between text-sm sm:text-base md:text-lg">
                <span>Total:</span>
                <span className="font-semibold text-gray-800">₹{Math.round(invoiceTotal)}</span>
              </div>
              <div className="flex justify-between text-sm sm:text-base md:text-lg">
                <span>Paid:</span>
                <span className="font-semibold text-green-600">₹{Math.round(totalPaid)}</span>
              </div>
              <div className="flex justify-between text-sm sm:text-base md:text-lg">
                <span>Balance:</span>
                <span className="font-semibold text-red-600">₹{Math.round(balanceDue)}</span>
              </div>
            </div>
            {/* Split Payment - Always visible; default from settings, user can toggle */}
            <div className="flex items-center mb-2 sm:mb-3">
              <input
                type="checkbox"
                id="split-payment-checkbox"
                className="mr-2 w-4 h-4 sm:w-5 sm:h-5"
                checked={payments.splitPaymentEnabled}
                onChange={e => setPayments(p => ({ ...p, splitPaymentEnabled: e.target.checked }))}
              />
              <label htmlFor="split-payment-checkbox" className="text-sm sm:text-base font-semibold cursor-pointer">Split Payment</label>
            </div>
            {/* Payment Method Selection */}
            <div className="mb-2 sm:mb-3">
              <label className="block text-sm sm:text-base font-semibold mb-1 sm:mb-2">Select Payment Method</label>
              <select
                className="w-full h-11 sm:h-12 md:h-14 px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition-all duration-200 text-sm sm:text-base"
                value={payments.selectedPaymentMethod}
                onChange={e => {
                  const value = e.target.value;
                  setPayments(p => ({ ...p, selectedPaymentMethod: value }));
                  
                  // Fetch customer points when Points payment method is selected
                  if (value === 'Points') {
                    fetchPointsWhenSelected();
                  }
                  
                  if (!payments.splitPaymentEnabled) {
                    // When split payment is unchecked, set the total amount for the selected payment method
                    const totalAmount = Math.round(invoiceTotal).toString();
                    setPayments(p => ({ ...p, cashPayment: value === 'Cash' ? totalAmount : '' }));
                    setPayments(p => ({ ...p, cardPayment: value === 'Card' ? totalAmount : '' }));
                    setPayments(p => ({ ...p, walletAmount: value === 'Wallets' ? totalAmount : '' }));
                    setWalletProvider(value === 'Wallets' ? walletProvider : 'PhonePe');
                    // Handle wallet transaction number based on payment method
                    if (value === 'Wallets') {
                      // When switching to Wallets, prefer the API-populated value from payments
                      const existingTx = payments.walletTxNo || walletTxNo || '';
                      setWalletTxNo(existingTx);
                      // Do not overwrite payments.walletTxNo here to avoid wiping API value
                    } else {
                      // When switching away from Wallets, preserve values in edit mode
                      if (isEditMode) {
                        // Keep both local and context values untouched
                      } else {
                        // Clear both in non-edit mode
                        setWalletTxNo('');
                        setPayments(p => ({ ...p, walletTxNo: '' }));
                      }
                    }
                    setPayments(p => ({ ...p, chequeAmount: value === 'Cheque' ? totalAmount : '' }));
                    setPayments(p => ({ ...p, chequeNo: value === 'Cheque' ? payments.chequeNo : '' }));
                    
                    // Reset the three specific payment methods (Coupon, Credit Note, Points) when switching between them
                    if (value !== 'Coupon') {
                      setPayments(p => ({ ...p, couponAmount: '0', couponNo: '', couponAmountLocked: false }));
                      setCouponError('');
                    }
                    if (value !== 'Credit Note') {
                      setPayments(p => ({ ...p, creditNoteAmount: '0', creditNoteNo: '' }));
                      setCreditNoteError('');
                    }
                    if (value !== 'Points') {
                      setPayments(p => ({ ...p, pointsRedeem: '0' }));
                      setPointsError('');
                    } else {
                      // Handle Points payment method when split payment is unchecked
                      const eligiblePoints = parseFloat(payments.eligiblePoints) || 0;
                      if (eligiblePoints >= invoiceTotal) {
                        setPayments(p => ({ ...p, pointsRedeem: invoiceTotal.toString() }));
                      } else {
                        setPayments(p => ({ ...p, pointsRedeem: '0' }));
                      }
                    }
                  }
                }}
              >
                <option value="Cash">Cash</option>
                <option value="Card">Card</option>
                <option value="Wallets">Wallet</option>
                <option value="Coupon">Coupon</option>
                <option value="Cheque">Cheque</option>
                <option value="Points">Points</option>
                <option value="Credit Note">Credit Note</option>
              </select>
            </div>
            {/* Payment Method Fields */}
              <div className="space-y-4 sm:space-y-5">
              {payments.selectedPaymentMethod === 'Cash' && (
                // Cash fields
                <div className="flex flex-col gap-2 sm:gap-3">
                  <label className="text-sm sm:text-base font-medium">Cash Amount</label>
                  <input
                    type="number"
                    className="w-full h-11 sm:h-12 md:h-14 px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition-all duration-200 text-sm sm:text-base"
                    value={payments.cashPayment}
                    onChange={e => setPayments(p => ({ ...p, cashPayment: e.target.value }))}
                    placeholder="Enter amount"
                    disabled={!payments.splitPaymentEnabled && payments.selectedPaymentMethod !== 'Cash'}
                    onFocus={handlePaymentFieldFocus}
                  />
                </div>
                )}
              {payments.selectedPaymentMethod === 'Card' && (
                // Card fields
                  <div className="flex flex-col gap-2 sm:gap-3">
                    <label className="text-sm sm:text-base font-medium">Card Amount</label>
                    <input
                      type="number"
                      className="w-full h-11 sm:h-12 md:h-14 px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition-all duration-200 text-sm sm:text-base"
                    value={payments.cardPayment}
                    onChange={e => setPayments(p => ({ ...p, cardPayment: e.target.value }))}
                    placeholder="Enter amount"
                    disabled={!payments.splitPaymentEnabled && payments.selectedPaymentMethod !== 'Card'}
                    onFocus={handlePaymentFieldFocus}
                  />
                  </div>
                )}
              {payments.selectedPaymentMethod === 'Wallets' && (
                // Wallet fields
                  <div className="flex flex-col gap-2 sm:gap-3">
                    <label className="text-sm sm:text-base font-medium">Wallet Provider</label>
                    <select
                      className="w-full h-11 sm:h-12 md:h-14 px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition-all duration-200 text-sm sm:text-base"
                      value={walletProvider}
                      onChange={e => {
                        const value = e.target.value;
                        console.log('Wallet Provider changed to:', value);
                        setWalletProvider(value);
                        setPayments(p => ({ ...p, walletProvider: value }));
                      }}
                    >
                      <option value="GooglePay">GooglePay</option>
                      <option value="PhonePe">PhonePe</option>
                      <option value="AmazonPay">AmazonPay</option>
                      <option value="Paytm">Paytm</option>
                    </select>
                    <label className="text-sm sm:text-base font-medium">Wallet Txn/Ref No.</label>
                    <input
                      type="text"
                      className="w-full h-11 sm:h-12 md:h-14 px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition-all duration-200 text-sm sm:text-base"
                      value={walletTxNo || payments.walletTxNo}
                      onChange={e => {
                        setWalletTxNo(e.target.value);
                        setPayments(p => ({ ...p, walletTxNo: e.target.value }));
                      }}
                      placeholder="Remarks/Ref."
                      onFocus={handlePaymentFieldFocus}
                    />
                    <label className="text-sm sm:text-base font-medium">Wallet Amount</label>
                    <input
                      type="number"
                      className="w-full h-11 sm:h-12 md:h-14 px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition-all duration-200 text-sm sm:text-base"
                    value={payments.walletAmount}
                    onChange={e => setPayments(p => ({ ...p, walletAmount: e.target.value }))}
                    placeholder="Enter amount"
                    disabled={!payments.splitPaymentEnabled && payments.selectedPaymentMethod !== 'Wallets'}
                    onFocus={handlePaymentFieldFocus}
                  />
                  </div>
                )}
              {payments.selectedPaymentMethod === 'Cheque' && (
                // Cheque fields
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium">Cheque Amount</label>
                    <input
                      type="number"
                      className="w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                    value={payments.chequeAmount}
                    onChange={e => setPayments(p => ({ ...p, chequeAmount: e.target.value }))}
                    placeholder="Enter amount"
                    disabled={!payments.splitPaymentEnabled && payments.selectedPaymentMethod !== 'Cheque'}
                    onFocus={handlePaymentFieldFocus}
                  />
                    <label className="text-sm font-medium">Cheque No.</label>
                    <input
                      type="text"
                      className="w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                    value={payments.chequeNo}
                    onChange={e => setPayments(p => ({ ...p, chequeNo: e.target.value }))}
                    placeholder="Cheque No."
                    disabled={!payments.splitPaymentEnabled && payments.selectedPaymentMethod !== 'Cheque'}
                    onFocus={handlePaymentFieldFocus}
                  />
                  </div>
                )}
              {payments.selectedPaymentMethod === 'Coupon' && (
                // Coupon fields
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium">Coupon Code</label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <input
                          type="text"
                          className="w-full h-10 px-3 pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                          value={payments.couponNo}
                          onChange={e => {
                            setPayments(p => ({ ...p, couponNo: e.target.value, couponAmount: '0', couponAmountLocked: false }));
                            setCouponError(''); // Clear error when user types
                          }}
                          placeholder="Coupon Code"
                          onFocus={handlePaymentFieldFocus}
                        />
                        {payments.couponNo && (
                          <button
                            type="button"
                            aria-label="Clear coupon code"
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            onClick={() => {
                              setPayments(p => ({ ...p, couponNo: '', couponAmount: '0', couponAmountLocked: false }));
                              setCouponError('');
                            }}
                          >
                            ×
                          </button>
                        )}
                      </div>
                      <button
                        type="button"
                        className="px-2 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap"
                        onClick={handleCouponValidation}
                        disabled={isValidatingCoupon || !payments.couponNo}
                      >
                        {isValidatingCoupon ? 'Validating...' : 'Validate'}
                      </button>
                    </div>
                    {couponError && (
                      <div className="text-red-600 text-sm">{couponError}</div>
                    )}
                    <label className="text-sm font-medium">Coupon Amount</label>
                    <input
                      type="number"
                      className="w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                      value={payments.couponAmount || '0'}
                      onChange={e => {
                        setPayments(p => ({ ...p, couponAmount: p.couponAmount || '0' }));
                      }}
                      placeholder="0"
                      disabled={true}
                      readOnly={true}
                      onFocus={handlePaymentFieldFocus}
                    />
                  </div>
                )}
              {payments.selectedPaymentMethod === 'Credit Note' && (
                // Credit Note fields
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium">Credit Note No.</label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <input
                          type="text"
                          className="w-full h-10 px-3 pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                          value={payments.creditNoteNo}
                          onChange={e => {
                            setPayments(p => ({ ...p, creditNoteNo: e.target.value }));
                            setCreditNoteError('');
                          }}
                          placeholder="Credit Note No."
                          disabled={!payments.splitPaymentEnabled && payments.selectedPaymentMethod !== 'Credit Note'}
                          readOnly={false}
                          onFocus={handlePaymentFieldFocus}
                        />
                        {payments.creditNoteNo && (
                          <button
                            type="button"
                            aria-label="Clear credit note number"
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            onClick={() => {
                              setPayments(p => ({ ...p, creditNoteNo: '', creditNoteAmount: '0' }));
                              setCreditNoteError('');
                            }}
                          >
                            ×
                          </button>
                        )}
                      </div>
                      <button
                        type="button"
                        className="px-2 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap"
                        onClick={handleCreditNoteValidation}
                        disabled={isValidatingCreditNote || !payments.creditNoteNo}
                      >
                        {isValidatingCreditNote ? 'Validating...' : 'Validate'}
                      </button>
                    </div>
                    {creditNoteError && (
                      <div className="text-red-600 text-sm whitespace-pre-line">{creditNoteError}</div>
                    )}
                    <label className="text-sm font-medium">Credit Note Amount</label>
                    <input
                      type="number"
                      className="w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                    value={payments.creditNoteAmount || '0'}
                    onChange={e => setPayments(p => ({ ...p, creditNoteAmount: p.creditNoteAmount || '0' }))}
                    placeholder="0"
                    disabled={true}
                    readOnly={true}
                    onFocus={handlePaymentFieldFocus}
                  />
                  </div>
                )}
              {payments.selectedPaymentMethod === 'Points' && (
                // Points fields
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium">Points to Redeem</label>
                                          <input
                        type="number"
                        className={`w-full h-10 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition ${
                          eligiblePoints <= 0 || pointsValue > eligiblePoints || (!payments.splitPaymentEnabled && eligiblePoints < invoiceTotal)
                            ? 'border-red-500 bg-red-50' 
                            : 'border-gray-300'
                        }`}
                        value={payments.pointsRedeem}
                        onChange={e => {
                          const value = e.target.value;
                          setPayments(p => ({ ...p, pointsRedeem: value }));
                          
                          // Clear error when user starts typing or when value is 0/empty
                          if (pointsError || !value || parseFloat(value) === 0) {
                            setPointsError('');
                          }
                          
                          // Validate points when split payment is unchecked
                          handlePointsValidation(value);
                        }}
                        placeholder="Enter points"
                        disabled={!payments.splitPaymentEnabled && payments.selectedPaymentMethod !== 'Points'}
                        readOnly={eligiblePoints <= 0 || (!payments.splitPaymentEnabled && eligiblePoints < invoiceTotal)}
                        onFocus={handlePaymentFieldFocus}
                      />
                      {eligiblePoints <= 0 && (
                        <div className="text-red-600 text-sm">No eligible points available</div>
                      )}
                      {pointsValue > eligiblePoints && eligiblePoints > 0 && (
                        <div className="text-red-600 text-sm">Points to redeem cannot exceed eligible points</div>
                      )}
                      {!payments.splitPaymentEnabled && eligiblePoints < invoiceTotal && eligiblePoints > 0 && (
                        <div className="text-red-600 text-sm">Eligible points are not enough for full payment</div>
                      )}
                      {pointsError && (
                        <div className="text-red-600 text-sm">{pointsError}</div>
                      )}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium">Eligible Points</label>
                        <input
                          type="text"
                          className="w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition bg-gray-50"
                          value={formattedEligiblePoints}
                          readOnly
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium">Remaining Points</label>
                        <input
                          type="text"
                          className="w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition bg-gray-50"
                          value={formattedRemainingPoints}
                          readOnly
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

            {/* Payment Methods Used - Only show in edit mode */}
            {isEditMode && editingInvoiceId && (
              <div className="bg-blue-50 rounded-lg p-4 sm:p-5 mt-3 sm:mt-4 border border-blue-200">
                <div className="text-sm sm:text-base font-semibold text-blue-900 mb-2 sm:mb-3">Payment Methods Used:</div>
                <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm">
                  {roundPayment(payments.cashPayment) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Cash:</span>
                      <span className="font-semibold text-green-600">₹{roundPayment(payments.cashPayment).toFixed(2)}</span>
                    </div>
                  )}
                  {roundPayment(payments.cardPayment) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Card:</span>
                      <span className="font-semibold text-green-600">₹{roundPayment(payments.cardPayment).toFixed(2)}</span>
                    </div>
                  )}
                  {roundPayment(payments.walletAmount) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Wallet:</span>
                      <span className="font-semibold text-green-600">₹{roundPayment(payments.walletAmount).toFixed(2)}</span>
                    </div>
                  )}
                  {roundPayment(payments.chequeAmount) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Cheque:</span>
                      <span className="font-semibold text-green-600">₹{roundPayment(payments.chequeAmount).toFixed(2)}</span>
                    </div>
                  )}
                  {roundPayment(payments.couponAmount) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Coupon:</span>
                      <span className="font-semibold text-green-600">₹{roundPayment(payments.couponAmount).toFixed(2)}</span>
                    </div>
                  )}
                  {roundPayment(payments.creditNoteAmount) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Credit Note:</span>
                      <span className="font-semibold text-green-600">₹{roundPayment(payments.creditNoteAmount).toFixed(2)}</span>
                    </div>
                  )}
                  {roundPayment(payments.pointsRedeem) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Points:</span>
                      <span className="font-semibold text-green-600">{Math.round(roundPayment(payments.pointsRedeem))} pts</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            {balanceDue !== 0 && (
              <div className="flex flex-col gap-2 sm:gap-3 mt-3 sm:mt-4">
                <label className="text-sm sm:text-base font-medium">Pay Before</label>
                <input
                  type="date"
                  className="w-full h-11 sm:h-12 md:h-14 px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition-all duration-200 text-sm sm:text-base"
                  value={payments.payBefore || getPayBeforeDateFromInvoiceDaysSetting(getSetting('Invoice No of Day for Due'))}
                  onChange={e => setPayments(p => ({ ...p, payBefore: e.target.value }))}
                  onFocus={handlePaymentFieldFocus}
                />
              </div>
            )}

            {showPaymentSuccess && (
              <div className="mt-3 sm:mt-4 text-green-600 font-bold text-center text-sm sm:text-base p-3 sm:p-4 bg-green-50 rounded-lg border border-green-200">Payment Confirmed! Total: ₹{invoiceTotal.toFixed(2)} | Paid: ₹{totalPaid.toFixed(2)} | Balance: ₹{balanceDue.toFixed(2)}</div>
            )}
            <button className="w-full mt-4 sm:mt-5 py-3 sm:py-4 rounded-lg bg-cyan-600 text-white font-bold text-base sm:text-lg min-h-[48px] sm:min-h-[56px] hover:bg-cyan-700 transition-all duration-200 active:scale-[0.98] shadow-md" onClick={() => goToSection('customer')}>Back to Customer</button>
          </div>
        )}
      </div>

      {/* Print Checkbox and Action Buttons */}
      <div className="mb-3">
        <div className="p-3 bg-white rounded-lg shadow-md">
          <div className="flex items-center gap-x-2 w-full">
            <div className="flex items-center flex-shrink-0">
              <input
                type="checkbox"
                id="print-checkbox"
                className="mr-1 h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                checked={printInvoice}
                onChange={e => setPrintInvoice(e.target.checked)}
              />
              <label htmlFor="print-checkbox" className="text-xs font-medium text-gray-700">
                Print
              </label>
            </div>
            <button
              className={`flex-1 min-w-0 py-2 rounded-md font-semibold text-sm transition-colors mx-1 ${
                isCreatingInvoice || !hasAccessedPaymentSection || balanceDue < 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-cyan-600 text-white hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-400'
              }`}
              onClick={handleCreateInvoice}
              disabled={isCreatingInvoice || !hasAccessedPaymentSection || balanceDue < 0}
              style={{ maxWidth: 140 }}
            >
              {isCreatingInvoice 
                ? (isEditMode ? 'Updating...' : 'Creating...') 
                : (isEditMode ? 'Update Invoice' : 'Create Invoice')
              }
            </button>
            <button
              className="flex-1 min-w-0 py-2 rounded-md font-semibold text-sm bg-gray-200 text-gray-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 transition-colors"
              onClick={() => {
                if (isEditMode && onBackToReports) {
                  onBackToReports();
                } else {
                  handleCancel();
                }
              }}
              style={{ maxWidth: 100 }}
            >
              {isEditMode ? 'Back' : 'Cancel'}
            </button>
          </div>
        </div>
      </div>

      {/* Barcode scanner UI removed as per user request */}
      {showScanner && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg p-4 flex flex-col items-center max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Scan Barcode</h3>
            {scannerError ? (
              <div className="text-red-600 font-semibold mb-4 text-center p-4 bg-red-50 rounded border">
                {scannerError}
                <br/>
                <button 
                  className="mt-2 px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                  onClick={() => {
                    setScannerError('');
                    setShowScanner(false);
                    setTimeout(() => setShowScanner(true), 100); // Retry
                  }}
                >
                  Try Again
                </button>
              </div>
            ) : (
              <div>
                <div id="html5qr-code-full-region" ref={scannerRef} style={{ width: 300, height: 300 }} />
                <div className="text-xs text-gray-600 mt-2 text-center">
                  Position the barcode within the square to scan
                </div>
              </div>
            )}
            <button 
              className="mt-4 px-4 py-2 bg-cyan-600 text-white rounded hover:bg-cyan-700" 
              onClick={() => { 
                setShowScanner(false); 
                setScannerError(''); 
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
      {/* Print Options Modal for Web/iOS */}
      <PrintOptionsModal
        open={showPrintOptions}
        onClose={() => {
          setShowPrintOptions(false);
          setPendingInvoiceDetails(null);
          setPendingInvoiceId(null);
          setIsBluetoothUILoading(false);
        }}
        onSelect={handlePrintOptionSelect}
        isPrinting={isPrinting}
        onDownload={handleDownloadPDF}
        devices={bluetoothDevices}
        isScanning={isScanning}
        scanError={scanError || undefined}
        onRetry={() => {
          // Handle device change - disconnect current device and start fresh scan
          if (isConnected) {
            // Disconnect current device
            if (typeof window !== 'undefined' && window.bluetoothSerial) {
              window.bluetoothSerial.disconnect(() => {
                console.log('[PrintOptionsModal] Disconnected from current device');
                setIsConnected(false);
                setConnectedDeviceName('');
                setLastUsedDevice('');
                // Clear session storage
                sessionStorage.removeItem('lastBluetoothPrinter');
                sessionStorage.removeItem('lastBluetoothPrinterName');
                // Start fresh scan
                handleRetryScan();
              }, (err) => {
                console.error('[PrintOptionsModal] Error disconnecting:', err);
                // Even if disconnect fails, start fresh scan
                setIsConnected(false);
                setConnectedDeviceName('');
                setLastUsedDevice('');
                sessionStorage.removeItem('lastBluetoothPrinter');
                sessionStorage.removeItem('lastBluetoothPrinterName');
                handleRetryScan();
              });
            } else {
              // If bluetoothSerial not available, just reset state and scan
              setIsConnected(false);
              setConnectedDeviceName('');
              setLastUsedDevice('');
              sessionStorage.removeItem('lastBluetoothPrinter');
              sessionStorage.removeItem('lastBluetoothPrinterName');
              handleRetryScan();
            }
          } else {
            // If not connected, just retry scan
            handleRetryScan();
          }
        }}
        defaultDeviceAddress={lastUsedDevice || undefined}
        isConnected={isConnected}
        connectedDeviceName={connectedDeviceName}
        showPrintFormatSelection={true} // Enable print format selection for SalesInvoice flows
        isBluetoothUILoading={isBluetoothUILoading}
        useSystemPrintOnly={!Capacitor.isNativePlatform()} // On mobile website: hide Bluetooth, use default system print only
        onConnectDevice={connectToBluetoothPrinter}
      />

      {/* Bluetooth Printer Modal for Android - DEPRECATED: Now using PrintOptionsModal */}
      {/* <Dialog open={showBluetoothModal} onOpenChange={setShowBluetoothModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="h-5 w-5" />
              Bluetooth Printer (Android Only)
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Connection Status */}
            {/* {isBluetoothConnected && connectedBluetoothDeviceName && (
              <div className="flex items-center gap-2 text-green-600 text-sm bg-green-50 p-3 rounded border">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <span>✓ Connected to: <strong>{connectedBluetoothDeviceName}</strong></span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={disconnectFromBluetoothPrinter}
                  className="ml-auto"
                >
                  Disconnect
                </Button>
              </div>
            )}

            {/* Scan Button */}
            {/* {!isBluetoothConnected && (
              <div className="space-y-2">
                <Button
                  onClick={scanBluetoothDevicesForModal}
                  disabled={isScanningBluetooth}
                  className="w-full"
                >
                  {isScanningBluetooth ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                      Scanning...
                    </>
                  ) : (
                    <>
                      <Search className="h-4 w-4 mr-2" />
                      Scan for Bluetooth Printers
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Scan Error */}
            {/* {bluetoothScanError && (
              <div className="text-red-600 text-sm bg-red-50 p-3 rounded border">
                <p className="font-medium">Scan Error:</p>
                <p>{bluetoothScanError}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={scanBluetoothDevicesForModal}
                  className="mt-2"
                >
                  Retry Scan
                </Button>
              </div>
            )}

            {/* Device List */}
            {/* {bluetoothDevices.length > 0 && !isBluetoothConnected && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Available Printers:</Label>
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {bluetoothDevices.map((device) => (
                    <div
                      key={device.address}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedBluetoothDevice === device.address
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setSelectedBluetoothDevice(device.address)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-sm">
                            {formatDeviceName(device)}
                            {isPrinterDevice(device) && ' 🖨️'}
                          </p>
                          <p className="text-xs text-gray-500">{device.address}</p>
                        </div>
                        {selectedBluetoothDevice === device.address && (
                          <span className="text-blue-600">✓</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Connect Button */}
            {/* {selectedBluetoothDevice && !isBluetoothConnected && (
              <Button
                onClick={() => connectToBluetoothPrinter(selectedBluetoothDevice)}
                className="w-full"
              >
                Connect to Selected Printer
              </Button>
            )}

            {/* Print Button */}
            {/* {isBluetoothConnected && (
              <div className="space-y-2">
                <Button
                  onClick={printToBluetoothPrinter}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Print to Bluetooth Printer
                </Button>
                <p className="text-xs text-gray-500 text-center">
                  Note: Only 2mm and 3mm templates are supported for Bluetooth printing
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setShowBluetoothModal(false)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog> */}
      {isPrinting && <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"><div className="bg-white p-6 rounded shadow text-lg">Printing...</div></div>}
      {isMobileWebPrintLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[9999]">
          <div className="bg-white p-6 rounded-lg shadow-xl flex flex-col items-center gap-3 min-w-[200px]">
            <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
            <span className="text-base font-medium text-gray-700">Preparing PDF for print...</span>
            <span className="text-xs text-gray-500">Please wait</span>
          </div>
        </div>
      )}

      {/* Product Entry Form Modal */}
      <Dialog open={showProductEntryModal} onOpenChange={setShowProductEntryModal}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Product</DialogTitle>
            <DialogDescription>
              Fill in the details to add a new product to your inventory.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <ProductEntryForm
              token={localStorage.getItem('token') || ''}
              showFooter={false}
              onSuccess={(productData) => {
                setShowProductEntryModal(false);
                // Show success message
                setProductAddMessage('Product added successfully!');
                setProductAddMessageType('success');
                
                // Add the newly created product to the invoice
                if (productData) {
                  addProductToTable(productData);
                }
                
                // Refresh products list
                if (queryClient) {
                  queryClient.invalidateQueries({ queryKey: ['products-search'] });
                }
              }}
              onCancel={() => setShowProductEntryModal(false)}
              ref={productFormRef}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowProductEntryModal(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="product-form"
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => productFormRef.current?.submit()}
            >
              Add Product
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quantity Exceeded Popup */}
      <Dialog open={showQuantityExceededPopup} onOpenChange={setShowQuantityExceededPopup}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Quantity Exceeded</DialogTitle>
            <DialogDescription>
              You can't sell more than the available quantity: {quantityExceededAvailable}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => {
                // Reset quantity to 1 for the product
                if (quantityExceededProductIndex !== null) {
                  setProducts(prev => prev.map((p, i) => {
                    if (i === quantityExceededProductIndex) {
                      const offer = parseFloat(p.OfferPrice) || 0;
                      const mrp = parseFloat(p.MRP) || 0;
                      const discountPer = parseFloat(p.DiscountPer) || 0;
                      const discPerUnit = (mrp * discountPer) / 100;
                      const discount = discPerUnit * 1; // Reset to 1
                      const gstAmount = ((parseFloat(p.UnitPrice) || 0) * (parseFloat(p.GST) || 0) / 100 * 1); // Reset to 1
                      return {
                        ...p,
                        Qty: '1',
                        DelQty: '1',
                        Total: (1 * offer).toFixed(2),
                        Discount: discount.toFixed(2),
                        GSTAmount: gstAmount.toFixed(2),
                      };
                    }
                    return p;
                  }));
                }
                setShowQuantityExceededPopup(false);
                setQuantityExceededProductIndex(null);
                setQuantityExceededAvailable(0);
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unit Price Validation Dialog */}
      <Dialog open={showUnitPriceValidation} onOpenChange={setShowUnitPriceValidation}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Unit Price Validation</DialogTitle>
            <DialogDescription>
              {unitPriceValidationData && (
                <>
                  You entered Unit Price (<strong>₹{parseFloat(unitPriceValidationData.enteredValue).toFixed(2)}</strong>) is less than the original unit price (<strong>₹{parseFloat(unitPriceValidationData.originalValue).toFixed(2)}</strong>).
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={handleUnitPriceCancel}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUnitPriceConfirm}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Serial Selection Popup */}
      {serialPopupProduct && (
        <SerialSelectionPopup
          isOpen={showSerialSelectionPopup}
          onClose={() => {
            setShowSerialSelectionPopup(false);
            setSerialPopupProduct(null);
          }}
          onAddToList={handleSerialSelection}
          serialDetails={serialPopupProduct.SerialDetails || []}
          productName={serialPopupProduct.Title || ''}
          barcode={serialPopupProduct.Barcode || ''}
          excludedSerialNumbers={addedSerialNumbers}
        />
      )}
      </div>
    </div>
  );
};

export default SalesInvoiceMobile; 